// LeadDetails.jsx (Updated with Combined Address/Yard/Zoho Tab)
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Row, Col, Typography, Button, Descriptions, Tag, Spin,
    Space, Avatar, Tabs, Badge, Empty, message, Popover, Drawer,
    Alert, Modal, Form, Input, DatePicker, Select, InputNumber, Checkbox,
    Divider, Timeline, Collapse, Table,
    Tooltip,
    Statistic,
    Radio,
    Progress
} from 'antd';
import {
    PhoneOutlined, MailOutlined, UserOutlined, EditOutlined,
    CalendarOutlined, CarOutlined, DollarOutlined,
    HistoryOutlined, EnvironmentOutlined, ArrowLeftOutlined,
    FieldTimeOutlined,
    CreditCardOutlined, HomeOutlined,
    SettingOutlined,
    SyncOutlined,
    GlobalOutlined,
    ChromeOutlined,
    LaptopOutlined,
    ClusterOutlined,
    TeamOutlined,
    CopyOutlined,
    EyeOutlined,
    SearchOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    PlusOutlined,
    InfoCircleOutlined,
    HistoryOutlined as HistoryIcon,
    SendOutlined,
    ProfileOutlined,
    FileTextOutlined,
    FilePdfOutlined,
    TransactionOutlined,
    DownloadOutlined,
    FileAddOutlined,
    CalculatorOutlined,
    CloseCircleOutlined,
    ShopOutlined,
    GoldOutlined,
    ShoppingCartOutlined,
    CloseOutlined,
    SaveOutlined,
    FlagOutlined,
    StopOutlined,
    PictureOutlined,
    CameraOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    DisconnectOutlined,
    CheckOutlined,
    BankOutlined,
    MessageOutlined,
    PercentageOutlined,
    ArrowDownOutlined,
    MinusCircleOutlined,
    RiseOutlined,
    LockOutlined,
    ScheduleOutlined,
    TruckOutlined,
    SwapOutlined,
    DeleteOutlined,
    ShoppingOutlined,
    CloudOutlined,
    BoxPlotOutlined,
    DiffOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import API from '../../API/API';
import dayjs from 'dayjs';
import './LeadDetails.css';
import '../ReLocate/YardAssignmentModal.css';
import LeadStageCard from './components/LeadStageCard';
import EmailTemplateModal from './components/EmailTemplateModal';
import TransferLeadModal from './components/TransferLeadModal';
import EditLeadDrawer from './components/EditLeadDrawer';
import DetailsTab from './components/TabComponents/DetailsTab';
import AgentInfoTab from './components/TabComponents/AgentInfoTab';
import YardContactFields from './components/YardContactFields';
import { calculateLeadAge, formatPSTDate, convertToPST } from './utils/dateUtils';
import { stageColorMap } from './utils/stageUtils';
import { roundMoney, getBalance } from '../../utils/money';
import { isOrderDecisionUnset, isPartStatusUnset } from '../../utils/orderStatus';
import ActivityHistoryTab from './components/TabComponents/ActivityHistoryTab';
import FollowUpTab from './components/TabComponents/FollowUpTab';
import QuotedTab from './components/TabComponents/QuotedTab';
import ZohoSignModal from './components/ZohoSignModal';
import InvoicePreviewModal from './components/InvoicePreviewModal';
import PaymentDetailTab from './components/TabComponents/PaymentDetailTab';
import YardReassignmentHistoryModal from '../../Components/YardReassignmentHistory/YardReassignmentHistoryModal';
import {
  TRACKING_PROMISE_DAYS_ERROR,
  TRACKING_PROMISE_DAYS_PLACEHOLDER,
  getTrackingPromiseDaysErrorMessage,
  isValidTrackingPromiseDays,
  normalizeTrackingPromiseDaysInput
} from './utils/trackingPromiseDays';
import {
    buildYardContactDraft,
    getAssignmentOrPrimaryYardValue,
    normalizeYardContactValue
} from './utils/yardContactOptions';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const TYPE_OF_LEAD_OPTIONS = ['Inbound Call', 'Live', 'Follow-Up'];
const getTypeOfLeadFormValue = (value) => TYPE_OF_LEAD_OPTIONS.includes(value) ? value : undefined;

// Tabs a SALES-AGENT does not need (admin / service agent still see every tab)
const SALES_AGENT_HIDDEN_TABS = ['activity', 'tracking-history', 'agentInfo'];

const isSalesAgentUser = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || 'null')?.role === 'SALES-AGENT';
    } catch (e) {
        return false;
    }
};

const getDefaultLeadTab = (leadData, requestedTab) => {
    const salesAgent = isSalesAgentUser();

    if (requestedTab && !(salesAgent && SALES_AGENT_HIDDEN_TABS.includes(requestedTab))) {
        return requestedTab;
    }

    // All roles land on Lead Details first
    return 'details';
};

const getAgentReferenceId = (agentRef) => {
    if (!agentRef) {
        return null;
    }

    if (typeof agentRef === 'string') {
        return agentRef;
    }

    return agentRef._id || agentRef.id || null;
};

const getAgentDisplayName = (agentRef) => {
    if (!agentRef || typeof agentRef !== 'object') {
        return null;
    }

    const fullName = [agentRef.firstName, agentRef.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    return fullName || agentRef.userName || null;
};

// Configure dayjs with timezone
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LeadDetailsDrawer from '../ZohoSign/LeadDetailsDrawer';
import PurchaseOrderTemplate from './components/PurchaseOrderTemplate';
import TrackingHistoryTab from './components/TrackingHistoryTab';
dayjs.extend(utc);
dayjs.extend(timezone);

// Stage transition rules from backend
const stageTransitionRules = {
    "New": [
        "Call Back",
        "Error",
        "Fake",
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Test Lead",
        "Voice Mail",
        "Wrong Number"
    ],
    "Call Back": [
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Voice Mail",
        "Wrong Number"
    ],
    "Voice Mail": [
        "Call Back",
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Wrong Number"
    ],
    "Follow-Up": [
        "Hang Up",
        "Negotiation",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Wrong Number"
    ],
    "Quoted": [
        "Negotiation",
        "Not Interested",
        "Part Not Available",
        "Payment Details",
        "Follow-Up",
        "Sales Made"
    ],
    "Negotiation": [
        "Not Interested",
        "Part Not Available",
        "Payment Details",
        "Sales Made"
    ],
    "Pending Payment": [
        "Negotiation",
        "Payment Details",
        "Quoted",
        "Sales Made"
    ],
    "Payment Completed": [
        "Sales Made"
    ],
    "Sales Made": [],
    "Part Not Available": [
        "Call Back",
        "Follow-Up",
        "New"
    ],
    "Wrong Number": [],
    "Not Interested": [],
    "Hang Up": [],
    "Fake": [],
    "Test Lead": [],
    "Error": [
        "Call Back",
        "Error",
        "Fake",
        "Follow-Up",
        "Hang Up",
        "New",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Test Lead",
        "Voice Mail",
        "Wrong Number"
    ]
};

// Lead stages array
const leadStages = [
    "New", "Call Back", "Voice Mail", "Follow-Up", "Quoted",
    "Negotiation", "Payment Details", "Sales Made",
    "Part Not Available", "Wrong Number", "Not Interested", "Hang Up",
    "Fake", "Test Lead", "Error"
];

// Helper functions from your second code
const calculateTotalPaid = (leadData) => {
    if (!leadData?.advancePayments) return 0;
    return leadData.advancePayments.reduce((sum, payment) => {
        if (payment.status === 'Completed' || payment.status === undefined) {
            return sum + (payment.amount || 0);
        }
        return sum;
    }, 0);
};

const calculateBalance = (leadData) => {
    const total = leadData?.totalAmount || 0;
    const paid = calculateTotalPaid(leadData);
    return Math.max(0, total - paid);
};

const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
        case 'card': return <CreditCardOutlined />;
        case 'payment link': return <SendOutlined />;
        case 'check': return <FileTextOutlined />;
        case 'zelle': return <DollarOutlined />;
        case 'cashapp': return <DollarOutlined />;
        case 'venmo': return <DollarOutlined />;
        case 'paypal': return <DollarOutlined />;
        case 'wire transfer': return <TransactionOutlined />;
        default: return <DollarOutlined />;
    }
};

const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
        case 'card': return 'blue';
        case 'payment link': return 'purple';
        case 'check': return 'orange';
        case 'zelle': return 'green';
        case 'cashapp': return 'green';
        case 'venmo': return '#3d95ce';
        case 'paypal': return '#003087';
        case 'wire transfer': return 'cyan';
        case 'cash': return 'gold';
        default: return 'default';
    }
};

const AddressModal = ({ visible, onCancel, onOk, lead }) => {
    const [form] = Form.useForm();
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const billingStreetValue = Form.useWatch('billingStreet', form);
    // Business Name is required only when a billing address exists and shipping is different
    const isBusinessNameRequired = !sameAsBilling && !!(billingStreetValue || '').toString().trim();

    useEffect(() => {
        if (visible && lead) {
            form.setFieldsValue({
                businessName: lead.bussinessName || "",
                billingStreet: lead.billingAddress?.street || "",
                billingCity: lead.billingAddress?.city || "",
                billingState: lead.billingAddress?.state || "",
                billingZip: lead.billingAddress?.zip || "",
                shippingStreet: lead.shippingAddress?.street || "",
                shippingCity: lead.shippingAddress?.city || "",
                shippingState: lead.shippingAddress?.state || "",
                shippingZip: lead.shippingAddress?.zip || "",
                attnName: lead.attnName || lead.personalInfo?.fullName || ""
            });

            if (lead.billingAddress && lead.shippingAddress) {
                const isSame =
                    lead.billingAddress.street === lead.shippingAddress.street &&
                    lead.billingAddress.city === lead.shippingAddress.city &&
                    lead.billingAddress.state === lead.shippingAddress.state &&
                    lead.billingAddress.zip === lead.shippingAddress.zip;
                setSameAsBilling(isSame);
            } else {
                setSameAsBilling(false);
            }
        }
    }, [visible, lead, form]);

    const handleSubmit = async (values) => {
        try {
            const payload = {
                bussinessName: values.businessName || "",
                billingAddress: {
                    street: values.billingStreet || "",
                    city: values.billingCity || "",
                    state: values.billingState || "",
                    zip: values.billingZip || ""
                }
            };

            if (sameAsBilling) {
                payload.shippingAddress = { ...payload.billingAddress };
            } else {
                payload.shippingAddress = {
                    street: values.shippingStreet || "",
                    city: values.shippingCity || "",
                    state: values.shippingState || "",
                    zip: values.shippingZip || ""
                };
            }

            // ATTN Name: empty when same as billing (falls back to customer name)
            payload.attnName = sameAsBilling ? "" : (values.attnName || "").trim();

            const response = await API.UpdateLead(lead._id, payload);

            if (response?.status) {
                message.success("Addresses updated successfully!");
                onOk();
            } else {
                message.error(response?.message || "Failed to update addresses");
            }
        } catch (error) {
            console.error('Error updating addresses:', error);
            message.error("Failed to update addresses");
        }
    };

    return (
        <Modal
            title="Billing & Shipping Address"
            open={visible}
            onCancel={() => {
                form.resetFields();
                setSameAsBilling(false);
                onCancel();
            }}
            onOk={() => {
                form.validateFields()
                    .then(() => form.submit())
                    .catch(() => {
                        message.error("Please fill all required fields");
                    });
            }}
            width={700}
            okText="Save Addresses"
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                    <Form.Item
                        label="Business Name"
                        name="businessName"
                        tooltip="Enter the customer's business name"
                        required={isBusinessNameRequired}
                        dependencies={['billingStreet']}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const hasBilling = !!(getFieldValue('billingStreet') || '').toString().trim();
                                    if (!sameAsBilling && hasBilling && (!value || !value.toString().trim())) {
                                        return Promise.reject(new Error('Business name is required when shipping address is different from billing'));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <Input
                            placeholder="Enter business name"
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Card>

                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CreditCardOutlined />
                            <span>Billing Address</span>
                        </div>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Form.Item
                        label="Street Address"
                        name="billingStreet"
                    >
                        <Input placeholder="123 Main St" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="City"
                                name="billingCity"
                            >
                                <Input placeholder="City" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="State"
                                name="billingState"
                            >
                                <Input placeholder="State" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="ZIP Code"
                        name="billingZip"
                        rules={[
                            { pattern: /^\d{5}(-\d{4})?$/, message: 'Valid ZIP code required (5 or 9 digits)' }
                        ]}
                    >
                        <Input placeholder="12345" maxLength={10} />
                    </Form.Item>
                </Card>

                <Card size="small" style={{ marginBottom: 16 }}>
                    <Form.Item>
                        <Checkbox
                            checked={sameAsBilling}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setSameAsBilling(checked);

                                if (checked) {
                                    const billingValues = form.getFieldsValue([
                                        'billingStreet', 'billingCity', 'billingState', 'billingZip'
                                    ]);

                                    if (billingValues.billingStreet &&
                                        billingValues.billingCity &&
                                        billingValues.billingState &&
                                        billingValues.billingZip) {
                                        form.setFieldsValue({
                                            shippingStreet: billingValues.billingStreet,
                                            shippingCity: billingValues.billingCity,
                                            shippingState: billingValues.billingState,
                                            shippingZip: billingValues.billingZip
                                        });
                                    } else {
                                        message.warning('Please fill billing address first');
                                        setSameAsBilling(false);
                                    }
                                } else {
                                    form.setFieldsValue({
                                        shippingStreet: '',
                                        shippingCity: '',
                                        shippingState: '',
                                        shippingZip: ''
                                    });
                                }
                            }}
                        >
                            Shipping address same as billing
                        </Checkbox>
                    </Form.Item>
                </Card>

                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <HomeOutlined />
                            <span>Shipping Address</span>
                        </div>
                    }
                >
                    {!sameAsBilling && (
                        <Form.Item
                            label="ATTN Name"
                            name="attnName"
                            tooltip="Person who receives the part at the shipping address. Defaults to Customer Name."
                        >
                            <Input placeholder={lead?.personalInfo?.fullName || 'Enter ATTN name'} />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Street Address"
                        name="shippingStreet"
                    >
                        <Input
                            placeholder="456 Delivery St"
                            disabled={sameAsBilling}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="City"
                                name="shippingCity"
                            >
                                <Input
                                    placeholder="City"
                                    disabled={sameAsBilling}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="State"
                                name="shippingState"
                            >
                                <Input
                                    placeholder="State"
                                    disabled={sameAsBilling}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="ZIP Code"
                        name="shippingZip"
                        rules={[
                            { pattern: /^\d{5}(-\d{4})?$/, message: 'Valid ZIP code required (5 or 9 digits)' }
                        ]}
                    >
                        <Input
                            placeholder="54321"
                            maxLength={10}
                            disabled={sameAsBilling}
                        />
                    </Form.Item>
                </Card>
            </Form>
        </Modal>
    );
};

const normalizeYardId = (yardId) => {
    if (!yardId) return null;
    if (typeof yardId === 'object') {
        return yardId._id || yardId.id || null;
    }
    return yardId;
};

const MAX_YARD_ASSIGNMENTS = 3;

const getAssignmentYardAddressText = (yardAddress) => {
    if (typeof yardAddress === 'string') {
        return yardAddress.trim();
    }

    if (!yardAddress) {
        return '';
    }

    return [
        yardAddress.address,
        yardAddress.street,
        yardAddress.city,
        yardAddress.state,
        yardAddress.zip_code
    ].filter(Boolean).join(', ');
};

const buildAssignmentYardAddress = (yardAddress) => ({
    address: getAssignmentYardAddressText(yardAddress),
    street: '',
    city: '',
    state: '',
    country: '',
    zip_code: ''
});

const getYardDraftAddressText = (yardData = {}, yardDetails = {}) => {
    return getAssignmentYardAddressText(yardData.yardAddress) ||
        getAssignmentYardAddressText(yardDetails.yardAddress);
};

const calculateYardFinalPrice = (yardDraft = {}) => {
    return (Number(yardDraft.price) || 0) +
        (Number(yardDraft.yardShipping) || 0) +
        (Number(yardDraft.ownShipping) || 0) +
        (Number(yardDraft.extraWarrantyPrice) || 0) +
        (Number(yardDraft.boxingPalleteFee) || 0) +
        (Number(yardDraft.coreCharges) || 0);
};

const buildYardDraft = (yardData = {}, yardDetails = {}) => ({
    price: Number(yardData.price) || 0,
    originalPrice: Number(yardData.originalPrice) || 0,
    description: yardData.description || '',
    yardShipping: Number(yardData.yardShipping) || 0,
    ownShipping: Number(yardData.ownShipping) || 0,
    coreCharges: Number(yardData.coreCharges) || 0,
    extraWarrantyPrice: Number(yardData.extraWarrantyPrice) || 0,
    boxingPalleteFee: Number(yardData.boxingPalleteFee) || 0,
    ccChargesPercentage: Number(yardData.ccChargesPercentage) || 0,
    yardWarranty: yardData.yardWarranty || '',
    yardMiles: yardData.yardMiles || '',
    stockNumber: yardData.stockNumber || '',
    quoteNumber: yardData.quoteNumber || '',
    partGrade: yardData.partGrade || '',
    // Add verification fields
    verifiedAmountStats: yardData.verifiedAmountStats || 'NO',
    verifiedAmountPrice: Number(yardData.verifiedAmountPrice) || 0,
    verifiedDate: yardData.verifiedDate || null,
    ...buildYardContactDraft(yardData, yardDetails),
    yardAddress: getYardDraftAddressText(yardData, yardDetails),
    yardConfirmDate: yardData.yardConfirmDate ? dayjs(yardData.yardConfirmDate) : dayjs()
});
const getLatestYardSelection = (yardEntries = []) => {
    const items = Array.isArray(yardEntries) ? yardEntries.filter(Boolean) : [];

    if (items.length === 0) {
        return {
            latestSelectionGroupId: null,
            currentGroupYards: [],
            currentYard: null,
            currentYardIndex: -1,
            comparisonYards: [],
            previousYards: []
        };
    }

    const currentIndices = items.reduce((result, yard, index) => {
        if (yard?.isCurrent === true) {
            result.push(index);
        }
        return result;
    }, []);
    const currentYardIndex = currentIndices.length > 0 ? currentIndices[currentIndices.length - 1] : items.length - 1;
    const currentYard = items[currentYardIndex] || items[items.length - 1];

    return {
        latestSelectionGroupId: currentYard?.selectionGroupId || null,
        currentGroupYards: currentYard ? [currentYard] : [],
        currentYard,
        currentYardIndex,
        comparisonYards: [],
        previousYards: items.filter((_, index) => index !== currentYardIndex)
    };
};

const findExistingYardRecord = (yardEntries = [], yardId) => {
    const normalizedYardId = normalizeYardId(yardId);
    if (!normalizedYardId) {
        return null;
    }

    for (let index = yardEntries.length - 1; index >= 0; index -= 1) {
        const yard = yardEntries[index];
        if (normalizeYardId(yard?.yardId) === normalizedYardId) {
            return yard;
        }
    }

    return null;
};

const formatYardAddress = (yardAddress = {}) => {
    return getAssignmentYardAddressText(yardAddress);
};

const getYardDisplayName = (yardDetails, yardRecord) => {
    if (yardDetails?.yardName) return yardDetails.yardName;
    if (typeof yardRecord?.yardId === 'object' && yardRecord?.yardId?.yardName) {
        return yardRecord.yardId.yardName;
    }
    const fallbackId = normalizeYardId(yardRecord?.yardId);
    return fallbackId ? `Yard ${fallbackId}` : 'Unnamed Yard';
};

const getPrimaryYardValue = (value) => {
    if (Array.isArray(value)) {
        return value[0] || null;
    }
    return value || null;
};

const formatYardAssignmentValue = (value) => {
    const normalized = value == null ? '' : String(value).trim();
    return normalized || 'N/A';
};

 export const YardModal = ({ visible, onCancel, onOk, lead }) => {
    const [loading, setLoading] = useState(false);
    const [yards, setYards] = useState([]);
    const [selectedYardIds, setSelectedYardIds] = useState([]);
    const [yardDraftsById, setYardDraftsById] = useState({});
    const [activeYardId, setActiveYardId] = useState(null);
    // Optional free-text reason captured only when this is a reassignment (a current
    // yard already exists). Sent as `yardReassignmentComment`; the backend attaches it
    // to the yardReassignmentHistory entry it writes when the current yard changes.
    const [reassignmentComment, setReassignmentComment] = useState('');
    const [errors, setErrors] = useState({
        yard: null,
        activeYardId: null,
        yards: {}
    });
    const currentYardId = normalizeYardId(getLatestYardSelection(lead?.yard || []).currentYard?.yardId);

    useEffect(() => {
        if (!visible) {
            resetForm();
            return;
        }

        resetForm();
        loadYards();
    }, [visible, lead]);

    const resetForm = () => {
        setSelectedYardIds([]);
        setYardDraftsById({});
        setActiveYardId(null);
        setReassignmentComment('');
        setErrors({
            yard: null,
            activeYardId: null,
            yards: {}
        });
    };

    const loadYards = async () => {
        try {
            const response = await API.getYardWithoutPagnation();
            if (response?.success) {
                setYards(response.data || []);
            } else if (response?.data) {
                setYards(response.data || []);
            } else if (Array.isArray(response)) {
                setYards(response);
            } else {
                setYards([]);
            }
        } catch (error) {
            console.error('Error loading yards:', error);
            message.error('Failed to load yards');
            setYards([]);
        }
    };

    const handleSelectedYardsChange = (nextSelectedYardIds) => {
        const selectedYardId = Array.isArray(nextSelectedYardIds)
            ? nextSelectedYardIds[nextSelectedYardIds.length - 1]
            : nextSelectedYardIds;
        const normalizedYardId = normalizeYardId(selectedYardId);
        const uniqueYardIds = normalizedYardId ? [normalizedYardId] : [];

        const nextDraftsById = {};
        uniqueYardIds.forEach((yardId) => {
            const existingYardRecord = findExistingYardRecord(lead?.yard || [], yardId);
            const yardDetails = yards.find((yard) => normalizeYardId(yard?._id) === yardId);
            nextDraftsById[yardId] = yardDraftsById[yardId] || buildYardDraft(existingYardRecord || {}, yardDetails || {});
        });

        setSelectedYardIds(uniqueYardIds);
        setYardDraftsById(nextDraftsById);
        setActiveYardId(uniqueYardIds[0] || null);
        setErrors((prev) => {
            const nextYardErrors = {};
            Object.entries(prev.yards || {}).forEach(([yardId, yardErrors]) => {
                if (uniqueYardIds.includes(yardId)) {
                    nextYardErrors[yardId] = yardErrors;
                }
            });

            return {
                ...prev,
                yard: null,
                activeYardId: null,
                yards: nextYardErrors
            };
        });
    };

    const handleYardDraftFieldChange = (yardId, field, value) => {
        setYardDraftsById((prev) => {
            const existingDraft = prev[yardId] || buildYardDraft();
            const nextDraft = {
                ...existingDraft,
                [field]: value
            };

            // Handle mutual exclusivity for shipping fields
            if (field === 'yardShipping' && (Number(value) || 0) > 0 && (Number(existingDraft.ownShipping) || 0) > 0) {
                nextDraft.ownShipping = 0;
            }

            if (field === 'ownShipping' && (Number(value) || 0) > 0 && (Number(existingDraft.yardShipping) || 0) > 0) {
                nextDraft.yardShipping = 0;
            }

            return {
                ...prev,
                [yardId]: nextDraft
            };
        });

        setErrors((prev) => {
            const yardErrors = { ...(prev.yards?.[yardId] || {}) };
            if (field === 'price' && (Number(value) || 0) > 0) {
                delete yardErrors.price;
            }
            if (field === 'originalPrice' && (Number(value) || 0) > 0) {
                delete yardErrors.originalPrice;
            }

            return {
                ...prev,
                yards: {
                    ...(prev.yards || {}),
                    [yardId]: yardErrors
                }
            };
        });
    };

    // Single "Shipping Type" + "Shipping Price" UI still maps to the underlying
    // yardShipping/ownShipping fields so the rest of the form (totals, submit payload,
    // history display) stays untouched — only one of the two is ever non-zero.
    const handleShippingTypeChange = (yardId, nextType) => {
        setYardDraftsById((prev) => {
            const existingDraft = prev[yardId] || buildYardDraft();
            const currentPrice = existingDraft.shippingType === 'own'
                ? (Number(existingDraft.ownShipping) || 0)
                : (Number(existingDraft.yardShipping) || 0);

            return {
                ...prev,
                [yardId]: {
                    ...existingDraft,
                    shippingType: nextType,
                    yardShipping: nextType === 'yard' ? currentPrice : 0,
                    ownShipping: nextType === 'own' ? currentPrice : 0
                }
            };
        });
    };

    const handleShippingPriceChange = (yardId, value) => {
        const shippingType = yardDraftsById[yardId]?.shippingType || 'yard';
        handleYardDraftFieldChange(yardId, shippingType === 'own' ? 'ownShipping' : 'yardShipping', value || 0);
    };

    const handleYardContactSelect = (yardId, option = {}) => {
        setYardDraftsById((prev) => {
            const existingDraft = prev[yardId] || buildYardDraft();

            return {
                ...prev,
                [yardId]: {
                    ...existingDraft,
                    contactPerson: normalizeYardContactValue(option.contactPerson),
                    contactEmail: normalizeYardContactValue(option.contactEmail),
                    contactNumber: normalizeYardContactValue(option.contactNumber),
                    contactPersonExtension: normalizeYardContactValue(option.contactPersonExtension)
                }
            };
        });
    };

    const handleVerificationChange = (yardId, field, value) => {
        setYardDraftsById((prev) => {
            const existingDraft = prev[yardId] || buildYardDraft();
            const nextDraft = {
                ...existingDraft,
                [field]: value
            };

            // If verifiedAmountStats is set to 'YES', auto-set current date if not already set
            if (field === 'verifiedAmountStats' && value === 'YES' && !existingDraft.verifiedDate) {
                nextDraft.verifiedDate = dayjs().toISOString();
            }

            // If verifiedAmountStats is set to 'NO', clear verifiedDate
            if (field === 'verifiedAmountStats' && value === 'NO') {
                nextDraft.verifiedDate = null;
            }

            return {
                ...prev,
                [yardId]: nextDraft
            };
        });
    };

    const validateForm = () => {
        const nextErrors = {
            yard: null,
            activeYardId: null,
            yards: {}
        };

        if (selectedYardIds.length === 0) {
            nextErrors.yard = 'Please select at least one yard';
        } else if (selectedYardIds.length > 1) {
            nextErrors.yard = 'Only one yard can be selected';
        }

        if (!activeYardId || !selectedYardIds.includes(activeYardId)) {
            nextErrors.activeYardId = 'Please choose one current yard';
        }

        selectedYardIds.forEach((yardId) => {
            const yardDraft = yardDraftsById[yardId] || buildYardDraft();
            const yardErrors = {};

            if (!yardDraft.price || Number(yardDraft.price) <= 0) {
                yardErrors.price = 'Yard price is required and must be greater than 0';
            }

            if (!yardDraft.originalPrice || Number(yardDraft.originalPrice) <= 0) {
                yardErrors.originalPrice = 'Original price is required and must be greater than 0';
            }

            if (Object.keys(yardErrors).length > 0) {
                nextErrors.yards[yardId] = yardErrors;
            }
        });

        setErrors(nextErrors);

        return !nextErrors.yard &&
            !nextErrors.activeYardId &&
            Object.keys(nextErrors.yards).length === 0;
    };

    const buildYardDraft = (yardData = {}, yardDetails = {}) => ({
        price: Number(yardData.price) || 0,
        originalPrice: Number(yardData.originalPrice) || 0,
        description: yardData.description || '',
        yardShipping: Number(yardData.yardShipping) || 0,
        ownShipping: Number(yardData.ownShipping) || 0,
        shippingType: (Number(yardData.ownShipping) || 0) > 0 ? 'own' : 'yard',
        coreCharges: Number(yardData.coreCharges) || 0,
        extraWarrantyPrice: Number(yardData.extraWarrantyPrice) || 0,
        boxingPalleteFee: Number(yardData.boxingPalleteFee) || 0,
        ccChargesPercentage: Number(yardData.ccChargesPercentage) || 0,
        yardWarranty: yardData.yardWarranty || '',
        yardMiles: yardData.yardMiles || '',
        stockNumber: yardData.stockNumber || '',
        quoteNumber: yardData.quoteNumber || '',
        partGrade: yardData.partGrade || '',
        verifiedAmountStats: yardData.verifiedAmountStats || 'NO',
        verifiedAmountPrice: Number(yardData.verifiedAmountPrice) || 0,
        verifiedDate: yardData.verifiedDate || null,
        ...buildYardContactDraft(yardData, yardDetails),
        yardAddress: getYardDraftAddressText(yardData, yardDetails),
        yardConfirmDate: yardData.yardConfirmDate ? dayjs(yardData.yardConfirmDate) : dayjs()
    });

    const calculateYardFinalPrice = (yardDraft = {}) => {
        let calculatedPrice = (Number(yardDraft.price) || 0) +
            (Number(yardDraft.yardShipping) || 0) +
            (Number(yardDraft.ownShipping) || 0) +
            (Number(yardDraft.coreCharges) || 0) +
            (Number(yardDraft.extraWarrantyPrice) || 0) +
            (Number(yardDraft.boxingPalleteFee) || 0);
        
        // Apply CC percentage if exists
        if (yardDraft.ccChargesPercentage && yardDraft.ccChargesPercentage > 0) {
            const ccAmount = (Number(yardDraft.price) || 0) * (yardDraft.ccChargesPercentage / 100);
            calculatedPrice += ccAmount;
        }
        
        return calculatedPrice;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            message.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);

            const existingLeadYards = Array.isArray(lead?.yard) ? lead.yard.filter(Boolean) : [];
            const selectedYardId = selectedYardIds[0];
            const yardDraft = yardDraftsById[selectedYardId] || buildYardDraft();
            const existingYardRecord = findExistingYardRecord(existingLeadYards, selectedYardId);

            if (!existingYardRecord && existingLeadYards.length >= MAX_YARD_ASSIGNMENTS) {
                message.error(`A lead can have a maximum of ${MAX_YARD_ASSIGNMENTS} yard assignments`);
                return;
            }

            const historyYards = existingYardRecord
                ? existingLeadYards.filter((yard) => yard !== existingYardRecord)
                : [...existingLeadYards];
            const existingRecordBase = existingYardRecord ? { ...existingYardRecord } : {};
            delete existingRecordBase.selectionGroupId;
            delete existingRecordBase.isCurrent;
            
            const nextCurrentYard = {
                ...existingRecordBase,
                yardId: selectedYardId,
                yardConfirmDate: (yardDraft.yardConfirmDate || dayjs()).toISOString(),
                price: Number(yardDraft.price) || 0,
                originalPrice: Number(yardDraft.originalPrice) || 0,
                description: yardDraft.description || '',
                yardShipping: Number(yardDraft.yardShipping) || 0,
                ownShipping: Number(yardDraft.ownShipping) || 0,
                coreCharges: Number(yardDraft.coreCharges) || 0,
                extraWarrantyPrice: Number(yardDraft.extraWarrantyPrice) || 0,
                boxingPalleteFee: Number(yardDraft.boxingPalleteFee) || 0,
                ccChargesPercentage: Number(yardDraft.ccChargesPercentage) || 0,
                yardWarranty: yardDraft.yardWarranty?.trim() || '',
                yardMiles: yardDraft.yardMiles?.trim() || '',
                stockNumber: yardDraft.stockNumber?.trim() || '',
                quoteNumber: yardDraft.quoteNumber?.trim() || '',
                partGrade: yardDraft.partGrade?.trim() || '',
                verifiedAmountStats: yardDraft.verifiedAmountStats || 'NO',
                verifiedAmountPrice: Number(yardDraft.verifiedAmountPrice) || 0,
                verifiedDate: yardDraft.verifiedDate || null,
                contactPerson: normalizeYardContactValue(yardDraft.contactPerson),
                contactEmail: normalizeYardContactValue(yardDraft.contactEmail),
                contactNumber: normalizeYardContactValue(yardDraft.contactNumber),
                contactPersonExtension: normalizeYardContactValue(yardDraft.contactPersonExtension),
                yardAddress: buildAssignmentYardAddress(yardDraft.yardAddress),
                finalPrice: calculateYardFinalPrice(yardDraft),
                isCurrent: true
            };

            const payload = {
                yard: [
                    ...historyYards.map((yard) => ({ ...yard, isCurrent: false })),
                    nextCurrentYard
                ]
            };

            // Only meaningful when the current yard actually changes; the backend
            // ignores it otherwise. Guarded so a plain details-edit doesn't send noise.
            const trimmedReassignmentComment = reassignmentComment.trim();
            if (trimmedReassignmentComment && normalizeYardId(selectedYardId) !== currentYardId) {
                payload.yardReassignmentComment = trimmedReassignmentComment;
            }

            const response = await API.UpdateLead(lead._id, payload);

            if (response?.status || response?.success) {
                message.success('Yard selection saved successfully!');
                onOk();
            } else {
                const errorMsg = response?.message || response?.data?.message || 'Failed to save yard selection';
                message.error(errorMsg);
            }
        } catch (error) {
            console.error('Error assigning yard:', error);
            message.error('Failed to save yard selection');
        } finally {
            setLoading(false);
        }
    };

    // Update the renderYardOption and return JSX to include new fields in each yard card
    const yardOptions = yards
        .filter((yard) => normalizeYardId(yard?._id) !== currentYardId)
        .map((yard) => ({
            value: normalizeYardId(yard._id),
            label: yard.yardName || yard.name || yard._id
        }));

    selectedYardIds.forEach((yardId) => {
        if (!yardOptions.some((option) => option.value === yardId)) {
            yardOptions.push({
                value: yardId,
                label: `Yard ${yardId}`
            });
        }
    });

    const renderYardOption = (option) => {
        const optionValue = option?.data?.value || option?.value;
        const optionLabel = option?.data?.label || option?.label || optionValue;
        const isSelected = selectedYardIds.includes(optionValue);

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '8px 6px',
                    borderRadius: 10,
                    background: isSelected ? '#e6f4ff' : '#ffffff',
                    border: isSelected ? '1px solid #91caff' : '1px solid transparent'
                }}
            >
                <Space size={10}>
                    <Radio checked={isSelected} style={{ pointerEvents: 'none' }} />
                    <span style={{ color: isSelected ? '#0958d9' : '#262626', fontWeight: isSelected ? 600 : 500 }}>
                        {optionLabel}
                    </span>
                </Space>
                <Text style={{ fontSize: 12, color: isSelected ? '#1677ff' : '#8c8c8c' }}>
                    {isSelected ? 'Selected' : 'Select'}
                </Text>
            </div>
        );
    };

    const isYardFieldInvalid = selectedYardIds.length === 0 && !!errors.yard;
    const yardHelperText = 'Select one yard. Leads keep up to 3 yard history records.';

    return (
        <Modal
            title={
                <div className="yard-assignment-modal-title">
                    <span className="yard-assignment-modal-title__icon"><ShopOutlined /></span>
                    <span className="yard-assignment-modal-title__copy">
                        <strong>Assign Yard to Lead</strong>
                        <span>Select a yard and capture assignment-specific details.</span>
                    </span>
                </div>
            }
            className="yard-assignment-modal"
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            centered
            width={1040}
            okText="Assign Yard"
            cancelText="Cancel"
        >
            <div>
                <Card
                    className="yard-assignment-selector"
                    size="small"
                    title={
                        <Space>
                            <EnvironmentOutlined style={{ color: '#1677ff' }} />
                            <span>Select Yard <span style={{ color: '#ff4d4f' }}>*</span></span>
                        </Space>
                    }
                    style={{
                        marginBottom: 18,
                        borderColor: isYardFieldInvalid ? '#ff4d4f' : undefined
                    }}
                >
                    <Select
                        allowClear
                        placeholder="Search and select one yard"
                        showSearch
                        optionFilterProp="label"
                        optionRender={renderYardOption}
                        menuItemSelectedIcon={null}
                        options={yardOptions}
                        onChange={handleSelectedYardsChange}
                        value={selectedYardIds[0]}
                        style={{ width: '100%' }}
                        dropdownStyle={{
                            padding: '8px',
                            borderRadius: '12px',
                            background: '#ffffff'
                        }}
                        loading={yards.length === 0}
                        size="large"
                        status={isYardFieldInvalid ? 'error' : undefined}
                    />
                    {isYardFieldInvalid && (
                        <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {errors.yard}
                        </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                        <InfoCircleOutlined /> {yardHelperText}
                    </Text>
                </Card>

                {currentYardId ? (
                    <Card
                        className="yard-assignment-selector"
                        size="small"
                        title={
                            <Space>
                                <InfoCircleOutlined style={{ color: '#722ed1' }} />
                                <span>Reason for reassignment <Text type="secondary">(optional)</Text></span>
                            </Space>
                        }
                        style={{ marginBottom: 18 }}
                    >
                        <Input.TextArea
                            placeholder="Why is this lead being moved to a different yard? (saved to the yard reassignment history)"
                            value={reassignmentComment}
                            onChange={(e) => setReassignmentComment(e.target.value)}
                            rows={2}
                            maxLength={1000}
                            showCount
                        />
                    </Card>
                ) : null}

                {errors.activeYardId && (
                    <Alert
                        message={errors.activeYardId}
                        type="error"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {selectedYardIds.map((yardId) => {
                    const yardDetails = yards.find((yard) => normalizeYardId(yard?._id) === yardId);
                    const yardDraft = yardDraftsById[yardId] || buildYardDraft();
                    const yardErrors = errors.yards?.[yardId] || {};
                    const finalPrice = calculateYardFinalPrice(yardDraft);

                    return (
                        <div
                            key={yardId}
                            style={{ marginBottom: 4 }}
                        >
                            <Card
                                className="yard-assignment-details-card yard-assignment-fields"
                                size="small"
                                title={
                                    <div className="yard-assignment-selected-yard">
                                        <span className="yard-assignment-selected-yard__icon"><ShopOutlined /></span>
                                        <span className="yard-assignment-selected-yard__copy">
                                            <strong>{yardDetails?.yardName || `Yard ${yardId}`}</strong>
                                        </span>
                                    </div>
                                }
                                extra={(
                                    <Tag className="yard-assignment-current-tag">Will become current</Tag>
                                )}
                            >
                                <YardContactFields
                                    yardId={yardId}
                                    yardDetails={yardDetails}
                                    yardDraft={yardDraft}
                                    onFieldChange={handleYardDraftFieldChange}
                                    onContactSelect={handleYardContactSelect}
                                />

                                <Form.Item
                                    label="Yard Address"
                                    style={{ marginBottom: 18 }}
                                >
                                    <Input.TextArea
                                        placeholder="Enter the yard location for this lead assignment"
                                        value={yardDraft.yardAddress}
                                        onChange={(e) => handleYardDraftFieldChange(yardId, 'yardAddress', e.target.value)}
                                        rows={2}
                                    />
                                </Form.Item>

                                <Divider style={{ margin: '4px 0 22px', borderColor: '#e9edf2' }} />
                                <div className="yard-assignment-section-heading">
                                    <span className="yard-assignment-section-heading__icon"><DollarOutlined /></span>
                                    <span className="yard-assignment-section-heading__copy">
                                        <strong>Pricing & fees</strong>
                                    </span>
                                </div>
                                <Row gutter={[18, 2]}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Car Parts Price (Original Price)"
                                            validateStatus={yardErrors.originalPrice ? 'error' : undefined}
                                            help={yardErrors.originalPrice}
                                            required
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter original price"
                                                value={yardDraft.originalPrice}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'originalPrice', value || 0)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                status={yardErrors.originalPrice ? 'error' : undefined}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Yard Price"
                                            validateStatus={yardErrors.price ? 'error' : undefined}
                                            help={yardErrors.price}
                                            required
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter yard price"
                                                value={yardDraft.price}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'price', value || 0)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                status={yardErrors.price ? 'error' : undefined}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[18, 2]}>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <CarOutlined />
                                                    <span>Shipping Type</span>
                                                </Space>
                                            )}
                                            tooltip="Who is responsible for shipping this part"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                value={yardDraft.shippingType || 'yard'}
                                                onChange={(value) => handleShippingTypeChange(yardId, value)}
                                                options={[
                                                    { value: 'yard', label: 'Yard Shipping' },
                                                    { value: 'own', label: 'Own Shipping' }
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <DollarOutlined />
                                                    <span>Shipping Price ($)</span>
                                                </Space>
                                            )}
                                            tooltip={
                                                yardDraft.shippingType === 'own'
                                                    ? 'Your own shipping cost'
                                                    : 'Shipping cost charged by the yard'
                                            }
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter shipping cost"
                                                value={
                                                    yardDraft.shippingType === 'own'
                                                        ? yardDraft.ownShipping
                                                        : yardDraft.yardShipping
                                                }
                                                onChange={(value) => handleShippingPriceChange(yardId, value)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[18, 2]}>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <GoldOutlined />
                                                    <span>Core Charges</span>
                                                </Space>
                                            )}
                                            tooltip="Core deposit charges"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter core charges"
                                                value={yardDraft.coreCharges}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'coreCharges', value || 0)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <RiseOutlined />
                                                    <span>Extra Warranty Price</span>
                                                </Space>
                                            )}
                                            tooltip="Additional warranty price for the part"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter extra warranty price"
                                                value={yardDraft.extraWarrantyPrice}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'extraWarrantyPrice', value || 0)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[18, 2]}>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <BoxPlotOutlined />
                                                    <span>Boxing & Pallete Fee</span>
                                                </Space>
                                            )}
                                            tooltip="Fee for boxing and pallete handling"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter boxing & pallete fee"
                                                value={yardDraft.boxingPalleteFee}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'boxingPalleteFee', value || 0)}
                                                min={0}
                                                precision={2}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <PercentageOutlined />
                                                    <span>CC Charges (%)</span>
                                                </Space>
                                            )}
                                            tooltip="Credit card charges as percentage"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter CC charges percentage"
                                                value={yardDraft.ccChargesPercentage}
                                                onChange={(value) => handleYardDraftFieldChange(yardId, 'ccChargesPercentage', value || 0)}
                                                min={0}
                                                max={100}
                                                precision={2}
                                                formatter={(value) => `${value}%`}
                                                parser={(value) => value.replace('%', '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider style={{ margin: '4px 0 22px', borderColor: '#e9edf2' }} />
                                <div className="yard-assignment-section-heading">
                                    <span className="yard-assignment-section-heading__icon"><EnvironmentOutlined /></span>
                                    <span className="yard-assignment-section-heading__copy">
                                        <strong>Assignment details</strong>
                                        <span>These values belong to this lead and are kept with yard history.</span>
                                    </span>
                                </div>

                                <Row gutter={[18, 2]}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Yard Warranty"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Input
                                                placeholder="Enter yard warranty"
                                                value={yardDraft.yardWarranty}
                                                onChange={(e) => handleYardDraftFieldChange(yardId, 'yardWarranty', e.target.value)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Yard Miles"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Input
                                                placeholder="Enter yard miles"
                                                value={yardDraft.yardMiles}
                                                onChange={(e) => handleYardDraftFieldChange(yardId, 'yardMiles', e.target.value)}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[18, 2]}>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Stock Number"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Input
                                                placeholder="Enter stock number"
                                                value={yardDraft.stockNumber}
                                                onChange={(e) => handleYardDraftFieldChange(yardId, 'stockNumber', e.target.value)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Quote Number"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Input
                                                placeholder="Enter quote number"
                                                value={yardDraft.quoteNumber}
                                                onChange={(e) => handleYardDraftFieldChange(yardId, 'quoteNumber', e.target.value)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Part Grade"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Input
                                                placeholder="Enter part grade"
                                                value={yardDraft.partGrade}
                                                onChange={(e) => handleYardDraftFieldChange(yardId, 'partGrade', e.target.value)}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item label="Description / Internal Notes" style={{ marginBottom: 18 }}>
                                    <Input.TextArea
                                        placeholder="Add optional assignment notes"
                                        value={yardDraft.description}
                                        onChange={(e) => handleYardDraftFieldChange(yardId, 'description', e.target.value)}
                                        rows={3}
                                    />
                                </Form.Item>

                                {/* Verification Section */}
                                <div className="yard-assignment-section-heading">
                                    <span className="yard-assignment-section-heading__icon"><CheckCircleOutlined /></span>
                                    <span className="yard-assignment-section-heading__copy">
                                        <strong>Verification Details</strong>
                                        <span>Track amount verification status and details</span>
                                    </span>
                                </div>

                                <Row gutter={[18, 2]}>
                                    <Col span={8}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <CheckCircleOutlined />
                                                    <span>Verified Amount</span>
                                                </Space>
                                            )}
                                            tooltip="Indicates whether the amount has been verified"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <Select
                                                placeholder="Select verification status"
                                                value={yardDraft.verifiedAmountStats || 'NO'}
                                                onChange={(value) => handleVerificationChange(yardId, 'verifiedAmountStats', value)}
                                                style={{ width: '100%' }}
                                            >
                                                <Select.Option value="NO">
                                                    <Space>
                                                        <ClockCircleOutlined style={{ color: '#faad14' }} />
                                                        <span>NO</span>
                                                    </Space>
                                                </Select.Option>
                                                <Select.Option value="YES">
                                                    <Space>
                                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                        <span>YES</span>
                                                    </Space>
                                                </Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <DollarOutlined />
                                                    <span>Verified Amount Price</span>
                                                </Space>
                                            )}
                                            tooltip="The verified price amount"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Enter verified amount"
                                                value={yardDraft.verifiedAmountPrice}
                                                onChange={(value) => handleVerificationChange(yardId, 'verifiedAmountPrice', value || 0)}
                                                min={0}
                                                precision={2}
                                                disabled={yardDraft.verifiedAmountStats !== 'YES'}
                                                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label={(
                                                <Space>
                                                    <ClockCircleOutlined />
                                                    <span>Verified Date</span>
                                                </Space>
                                            )}
                                            tooltip="Date when the verification was performed"
                                            style={{ marginBottom: 18 }}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                placeholder="Select verification date"
                                                value={yardDraft.verifiedDate ? dayjs(yardDraft.verifiedDate) : null}
                                                onChange={(date) => handleVerificationChange(yardId, 'verifiedDate', date ? date.toISOString() : null)}
                                                disabled={yardDraft.verifiedAmountStats !== 'YES'}
                                                format="YYYY-MM-DD HH:mm:ss"
                                                showTime
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <div className="yard-assignment-total">
                                    <div className="yard-assignment-total__copy">
                                        <strong>Final assignment total</strong>
                                        <span>Yard price, shipping, fees, core charges, warranty, boxing, and CC fee.</span>
                                    </div>
                                    <Statistic
                                        title="Calculated total"
                                        value={finalPrice}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{ color: '#15803d', fontSize: '26px', fontWeight: 700 }}
                                    />
                                </div>
                            </Card>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

const AddressTab = ({ lead, onEditAddress, isAdmin }) => {  // Add isAdmin prop
    const hasAddressData = () => {
        if (!lead) return false;
        if (lead.bussinessName && lead.bussinessName.trim().length > 0) return true;

        const billingHasData = lead.billingAddress &&
            lead.billingAddress.street &&
            lead.billingAddress.street.trim().length > 0;

        const shippingHasData = lead.shippingAddress &&
            lead.shippingAddress.street &&
            lead.shippingAddress.street.trim().length > 0;

        return billingHasData || shippingHasData;
    };

    // Check if lead stage is Sales Made
    const isSalesMade = lead?.leadStage === "Sales Made";
    const canEdit = !isSalesMade || isAdmin; // Admin can edit even after Sales Made

    return (
        <Card>
            {/* Header with Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Title level={4} style={{ margin: 0 }}>Address Information</Title>
                
                {/* Show edit button if can edit */}
                {canEdit && (
                    <Button
                        type="primary"
                        onClick={onEditAddress}
                        icon={<EditOutlined />}
                    >
                        {hasAddressData() ? 'Edit Address' : 'Add Address'}
                    </Button>
                )}
                
                {/* Show disabled button with tooltip if cannot edit */}
                {!canEdit && (
                    <Tooltip title="Cannot edit address after Sales Made stage">
                        <Button
                            type="primary"
                            disabled
                            icon={<LockOutlined />}
                        >
                            {hasAddressData() ? 'Edit Address' : 'Add Address'}
                        </Button>
                    </Tooltip>
                )}
            </div>

            {/* Sales Made Banner for non-admin */}
            {isSalesMade && !isAdmin && (
                <Alert
                    message="Lead Completed"
                    description="This lead has reached Sales Made stage. Address information cannot be modified."
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginBottom: '16px' }}
                />
            )}

            {/* Admin Override Banner for admin on Sales Made */}
          

            {/* Address Content */}
            {hasAddressData() ? (
                <Row gutter={16}>
                    {lead.bussinessName && lead.bussinessName.trim().length > 0 && (
                        <Col span={24} style={{ marginBottom: '16px' }}>
                            <Card
                                title="Business Information"
                                size="small"
                                style={{ borderTop: '3px solid #722ed1' }}
                            >
                                <div style={{ marginBottom: 8 }}>
                                    <Text strong style={{ fontSize: '12px', color: '#666' }}>Business Name:</Text>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                                        {lead.bussinessName}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    )}

                    {lead.billingAddress &&
                        lead.billingAddress.street &&
                        lead.billingAddress.street.trim().length > 0 && (
                            <Col span={24} style={{ marginBottom: '16px' }}>
                                <Card
                                    title="Billing Address"
                                    size="small"
                                    style={{ borderTop: '3px solid #1890ff' }}
                                >
                                    <div>
                                        <div style={{ marginBottom: 6 }}>
                                            <Text strong style={{ fontSize: '12px', color: '#666' }}>Customer Name: </Text>
                                            <Text>{lead.personalInfo?.fullName || 'N/A'}</Text>
                                        </div>
                                        <div>{lead.billingAddress.street}</div>
                                        <div>
                                            {[lead.billingAddress.city, lead.billingAddress.state]
                                                .filter(Boolean)
                                                .join(', ')}
                                            {lead.billingAddress.zip ? ` ${lead.billingAddress.zip}` : ''}
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '16px', fontSize: '12px', color: '#1890ff' }}>
                                        <CreditCardOutlined /> Billing Address
                                    </div>
                                </Card>
                            </Col>
                        )}

                    {lead.shippingAddress &&
                        lead.shippingAddress.street &&
                        lead.shippingAddress.street.trim().length > 0 && (
                            <Col span={24}>
                                <Card
                                    title="Shipping Address"
                                    size="small"
                                    style={{ borderTop: '3px solid #52c41a' }}
                                >
                                    {lead.billingAddress &&
                                        lead.billingAddress.street === lead.shippingAddress.street &&
                                        lead.billingAddress.city === lead.shippingAddress.city &&
                                        lead.billingAddress.state === lead.shippingAddress.state &&
                                        lead.billingAddress.zip === lead.shippingAddress.zip ? (
                                        <div style={{ textAlign: 'center', padding: '16px' }}>
                                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                                            <div style={{ color: '#52c41a', fontWeight: 'bold' }}>Same as Billing Address</div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{ marginBottom: 6 }}>
                                                <Text strong style={{ fontSize: '12px', color: '#666' }}>ATTN Name: </Text>
                                                <Text>{lead.attnName || lead.personalInfo?.fullName || 'N/A'}</Text>
                                            </div>
                                            <div>{lead.shippingAddress.street}</div>
                                            <div>
                                                {[lead.shippingAddress.city, lead.shippingAddress.state]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                                {lead.shippingAddress.zip ? ` ${lead.shippingAddress.zip}` : ''}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '16px', fontSize: '12px', color: '#52c41a' }}>
                                        <HomeOutlined /> Shipping Address
                                    </div>
                                </Card>
                            </Col>
                        )}
                </Row>
            ) : (
                <div style={{ textAlign: 'center', padding: '64px 16px' }}>
                    <EnvironmentOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '24px' }} />
                    <Title level={4} style={{ color: '#666', marginBottom: '16px' }}>
                        No Address Information
                    </Title>
                    <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
                        Add billing and shipping addresses to complete customer information
                    </Text>
                    
                    {/* Show add button if can edit */}
                    {canEdit && (
                        <Button
                            type="primary"
                            size="large"
                            onClick={onEditAddress}
                            icon={<PlusOutlined />}
                            style={{ minWidth: '200px' }}
                        >
                            Add Address Information
                        </Button>
                    )}
                    
                    {/* Show disabled button with message if cannot edit */}
                    {!canEdit && (
                        <Tooltip title="Cannot add address after Sales Made stage">
                            <Button
                                type="primary"
                                size="large"
                                disabled
                                icon={<LockOutlined />}
                                style={{ minWidth: '200px' }}
                            >
                                Add Address Information
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </Card>
    );
};

export const YardTab = ({ lead, onAssignYard, onUpdateSuccess, isAdmin }) => {
    const [yardDetailsMap, setYardDetailsMap] = useState({});
    const [yardLoading, setYardLoading] = useState(false);
    const [yardError, setYardError] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    
    // Edit form state with all fields including new ones
    const [editPrice, setEditPrice] = useState(0);
    const [editOriginalPrice, setEditOriginalPrice] = useState(0);
    const [editDescription, setEditDescription] = useState('');
    const [editYardShipping, setEditYardShipping] = useState(0);
    const [editOwnShipping, setEditOwnShipping] = useState(0);
    const [editCoreCharges, setEditCoreCharges] = useState(0);
    const [editExtraWarrantyPrice, setEditExtraWarrantyPrice] = useState(0);
    const [editBoxingPalleteFee, setEditBoxingPalleteFee] = useState(0);
    const [editCcChargesPercentage, setEditCcChargesPercentage] = useState(0);
    const [editYardWarranty, setEditYardWarranty] = useState('');
    const [editYardMiles, setEditYardMiles] = useState('');
    const [editStockNumber, setEditStockNumber] = useState('');
    const [editQuoteNumber, setEditQuoteNumber] = useState('');
    const [editPartGrade, setEditPartGrade] = useState('');
    const [editContactPerson, setEditContactPerson] = useState('');
    const [editContactEmail, setEditContactEmail] = useState('');
    const [editContactNumber, setEditContactNumber] = useState('');
    const [editContactPersonExtension, setEditContactPersonExtension] = useState('');
    const [editYardAddress, setEditYardAddress] = useState('');
    const [editFinalPrice, setEditFinalPrice] = useState(0);
    const [editLoading, setEditLoading] = useState(false);
    const [switchingYardKey, setSwitchingYardKey] = useState(null);
    const [reassignHistoryModalOpen, setReassignHistoryModalOpen] = useState(false);

    // Verification fields state
    const [editVerifiedAmountStats, setEditVerifiedAmountStats] = useState('NO');
    const [editVerifiedAmountPrice, setEditVerifiedAmountPrice] = useState(0);
    const [editVerifiedDate, setEditVerifiedDate] = useState(null);
    
    const isSalesMade = lead?.leadStage === "Sales Made";
    const canEdit = !isSalesMade || isAdmin;
    
    const yards = lead?.yard || [];
    const {
        currentYard,
        currentYardIndex,
        previousYards
    } = getLatestYardSelection(yards);
    const yardIdsToLoad = yards
        .map((yard) => normalizeYardId(yard.yardId))
        .filter(Boolean);
    const yardIdsToLoadKey = yardIdsToLoad.join('|');
    const currentYardId = normalizeYardId(currentYard?.yardId);
    const currentYardDetails = currentYardId ? yardDetailsMap[currentYardId] : null;
    const currentYardAddress = currentYard?.yardAddress;
    const editContactDraft = {
        contactPerson: editContactPerson,
        contactEmail: editContactEmail,
        contactNumber: editContactNumber,
        contactPersonExtension: editContactPersonExtension
    };

    const handleEditContactFieldChange = (_, field, value) => {
        const nextValue = value || '';
        if (field === 'contactPerson') setEditContactPerson(nextValue);
        if (field === 'contactEmail') setEditContactEmail(nextValue);
        if (field === 'contactNumber') setEditContactNumber(nextValue);
        if (field === 'contactPersonExtension') setEditContactPersonExtension(nextValue);
    };

    const handleEditContactSelect = (_, option = {}) => {
        setEditContactPerson(normalizeYardContactValue(option.contactPerson));
        setEditContactEmail(normalizeYardContactValue(option.contactEmail));
        setEditContactNumber(normalizeYardContactValue(option.contactNumber));
        setEditContactPersonExtension(normalizeYardContactValue(option.contactPersonExtension));
    };

    useEffect(() => {
        const fetchYardDetails = async () => {
            if (yardIdsToLoad.length === 0) {
                setYardDetailsMap({});
                setYardError(null);
                setYardLoading(false);
                return;
            }

            setYardLoading(true);
            setYardError(null);

            try {
                const responses = await Promise.allSettled(
                    yardIdsToLoad.map((yardId) => API.GetYardById(yardId))
                );

                const nextYardDetailsMap = {};
                responses.forEach((result, index) => {
                    if (result.status !== 'fulfilled') {
                        return;
                    }

                    const yardId = yardIdsToLoad[index];
                    const response = result.value;
                    const yardDetails = response?.status || response?.success
                        ? response.data || response
                        : response?.data || null;

                    if (yardDetails) {
                        nextYardDetailsMap[yardId] = yardDetails;
                    }
                });

                setYardDetailsMap(nextYardDetailsMap);

                if (currentYardId && !nextYardDetailsMap[currentYardId]) {
                    setYardError('Yard is assigned but details could not be loaded.');
                }
            } catch (error) {
                console.error('Error fetching yard details:', error);
                setYardDetailsMap({});
                setYardError('Error loading yard details');
            } finally {
                setYardLoading(false);
            }
        };

        fetchYardDetails();
    }, [yardIdsToLoadKey, currentYardId]);
    
    // Calculate final price including new fields
    useEffect(() => {
        let calculatedPrice = (Number(editPrice) || 0) + 
                             (Number(editYardShipping) || 0) + 
                             (Number(editOwnShipping) || 0) + 
                             (Number(editCoreCharges) || 0) +
                             (Number(editExtraWarrantyPrice) || 0) +
                             (Number(editBoxingPalleteFee) || 0);
        
        // Apply CC percentage if exists
        if (editCcChargesPercentage && editCcChargesPercentage > 0) {
            const ccAmount = (Number(editPrice) || 0) * (editCcChargesPercentage / 100);
            calculatedPrice += ccAmount;
        }
        
        setEditFinalPrice(calculatedPrice);
    }, [editPrice, editYardShipping, editOwnShipping, editCoreCharges, 
        editExtraWarrantyPrice, editBoxingPalleteFee, editCcChargesPercentage]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('MM-DD-YYYY hh:mm A');
    };

    // Handle Edit button click
    const handleEditClick = () => {
        if (currentYard) {
            const yardData = currentYard;
            setEditPrice(yardData.price || 0);
            setEditOriginalPrice(yardData.originalPrice || 0);
            setEditDescription(yardData.description || '');
            setEditYardShipping(yardData.yardShipping || 0);
            setEditOwnShipping(yardData.ownShipping || 0);
            setEditCoreCharges(yardData.coreCharges || 0);
            setEditExtraWarrantyPrice(yardData.extraWarrantyPrice || 0);
            setEditBoxingPalleteFee(yardData.boxingPalleteFee || 0);
            setEditCcChargesPercentage(yardData.ccChargesPercentage || 0);
            setEditYardWarranty(yardData.yardWarranty || '');
            setEditYardMiles(yardData.yardMiles || '');
            setEditStockNumber(yardData.stockNumber || '');
            setEditQuoteNumber(yardData.quoteNumber || '');
            setEditPartGrade(yardData.partGrade || '');
            // Set verification fields
            setEditVerifiedAmountStats(yardData.verifiedAmountStats || 'NO');
            setEditVerifiedAmountPrice(yardData.verifiedAmountPrice || 0);
            setEditVerifiedDate(yardData.verifiedDate || null);
            const contactDraft = buildYardContactDraft(yardData, currentYardDetails || {});
            setEditContactPerson(contactDraft.contactPerson);
            setEditContactEmail(contactDraft.contactEmail);
            setEditContactNumber(contactDraft.contactNumber);
            setEditContactPersonExtension(contactDraft.contactPersonExtension);
            setEditYardAddress(getYardDraftAddressText(yardData, currentYardDetails || {}));
            setEditModalVisible(true);
        }
    };

    // Handle Edit submission
    const handleEditSubmit = async () => {
        try {
            setEditLoading(true);

            const existingYards = lead?.yard || [];
            
            // Calculate final price with new fields
            const calculatedFinalPrice = (Number(editPrice) || 0) + 
                                        (Number(editYardShipping) || 0) + 
                                        (Number(editOwnShipping) || 0) + 
                                        (Number(editCoreCharges) || 0) +
                                        (Number(editExtraWarrantyPrice) || 0) +
                                        (Number(editBoxingPalleteFee) || 0);
            
            const ccAmount = (Number(editPrice) || 0) * ((Number(editCcChargesPercentage) || 0) / 100);
            const finalPriceWithCC = calculatedFinalPrice + ccAmount;
            
            // Update only the current yard
            const updatedYards = existingYards.map((yard, index) => {
                if (index === currentYardIndex) {
                    return {
                        ...yard,
                        price: Number(editPrice) || yard.price || 0,
                        originalPrice: Number(editOriginalPrice) || yard.originalPrice || 0,
                        description: editDescription || yard.description || '',
                        yardShipping: Number(editYardShipping) || 0,
                        ownShipping: Number(editOwnShipping) || 0,
                        coreCharges: Number(editCoreCharges) || 0,
                        extraWarrantyPrice: Number(editExtraWarrantyPrice) || 0,
                        boxingPalleteFee: Number(editBoxingPalleteFee) || 0,
                        ccChargesPercentage: Number(editCcChargesPercentage) || 0,
                        yardWarranty: editYardWarranty || '',
                        yardMiles: editYardMiles || '',
                        stockNumber: editStockNumber?.trim() || '',
                        quoteNumber: editQuoteNumber?.trim() || '',
                        partGrade: editPartGrade?.trim() || '',
                        // Add verification fields
                        verifiedAmountStats: editVerifiedAmountStats || 'NO',
                        verifiedAmountPrice: Number(editVerifiedAmountPrice) || 0,
                        verifiedDate: editVerifiedDate || null,
                        contactPerson: normalizeYardContactValue(editContactPerson),
                        contactEmail: normalizeYardContactValue(editContactEmail),
                        contactNumber: normalizeYardContactValue(editContactNumber),
                        contactPersonExtension: normalizeYardContactValue(editContactPersonExtension),
                        yardAddress: buildAssignmentYardAddress(editYardAddress),
                        finalPrice: finalPriceWithCC,
                        editedAt: new Date().toISOString()
                    };
                }
                return yard;
            });

            const payload = { yard: updatedYards };
            const response = await API.UpdateLead(lead._id, payload);

            if (response?.status || response?.success) {
                message.success('Yard details updated successfully!');
                setEditModalVisible(false);
                onUpdateSuccess(); // Refresh lead data
            } else {
                message.error(response?.message || 'Failed to update yard');
            }
        } catch (error) {
            console.error('Error updating yard:', error);
            message.error('Failed to update yard');
        } finally {
            setEditLoading(false);
        }
    };

    // Handle mutual exclusivity for shipping fields
    const handleYardShippingChange = (value) => {
        setEditYardShipping(value || 0);
        if (value > 0 && editOwnShipping > 0) {
            setEditOwnShipping(0);
        }
    };

    const handleOwnShippingChange = (value) => {
        setEditOwnShipping(value || 0);
        if (value > 0 && editYardShipping > 0) {
            setEditYardShipping(0);
        }
    };

    const handleSetCurrentFromHistory = async (yardToActivate, yardKey) => {
        try {
            setSwitchingYardKey(yardKey);
            const existingYards = Array.isArray(lead?.yard) ? lead.yard.filter(Boolean) : [];
            const historyYards = existingYards.filter((yard) => yard !== yardToActivate);
            const payload = {
                yard: [
                    ...historyYards.map((yard) => ({ ...yard, isCurrent: false })),
                    { ...yardToActivate, isCurrent: true }
                ]
            };
            const response = await API.UpdateLead(lead._id, payload);

            if (response?.status || response?.success) {
                message.success('Current yard updated from history');
                onUpdateSuccess();
            } else {
                message.error(response?.message || 'Failed to update current yard');
            }
        } catch (error) {
            console.error('Error switching current yard:', error);
            message.error('Failed to update current yard');
        } finally {
            setSwitchingYardKey(null);
        }
    };

    // Display helper for verification fields in view mode
    const renderVerificationDisplay = (yard) => {
        const hasVerification = yard.verifiedAmountStats === 'YES';
        
        if (!hasVerification) {
            return (
                <div style={{ marginTop: '8px', padding: '6px 12px', backgroundColor: '#fff7e6', borderRadius: '6px' }}>
                    <Tag color="warning" icon={<ClockCircleOutlined />}>
                        Not Verified
                    </Tag>
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                        Amount not yet verified
                    </Text>
                </div>
            );
        }
        
        return (
            <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#f6ffed', borderRadius: '6px', borderLeft: '3px solid #52c41a' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                        Verified
                    </Tag>
                    <div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>Amount: </Text>
                        <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
                            ${(yard.verifiedAmountPrice || 0).toFixed(2)}
                        </Text>
                    </div>
                    {yard.verifiedDate && (
                        <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Date: </Text>
                            <Text style={{ fontSize: '12px' }}>
                                {formatDate(yard.verifiedDate)}
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Display helper for new fields in view mode
    const renderNewFieldsDisplay = (yard) => {
        const hasExtraWarranty = yard.extraWarrantyPrice && yard.extraWarrantyPrice > 0;
        const hasBoxingFee = yard.boxingPalleteFee && yard.boxingPalleteFee > 0;
        const hasCcPercentage = yard.ccChargesPercentage && yard.ccChargesPercentage > 0;
        
        if (!hasExtraWarranty && !hasBoxingFee && !hasCcPercentage) return null;
        
        return (
            <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f5ff', borderRadius: '8px' }}>
                <Text strong style={{ fontSize: '12px', color: '#1890ff', display: 'block', marginBottom: '8px' }}>
                    Additional Charges:
                </Text>
                <Row gutter={[16, 8]}>
                    {hasExtraWarranty && (
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Extra Warranty:</Text>
                            <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
                                ${yard.extraWarrantyPrice.toFixed(2)}
                            </div>
                        </Col>
                    )}
                    {hasBoxingFee && (
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Boxing & Pallete:</Text>
                            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
                                ${yard.boxingPalleteFee.toFixed(2)}
                            </div>
                        </Col>
                    )}
                    {hasCcPercentage && (
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>CC Charges:</Text>
                            <div style={{ fontWeight: 'bold', color: '#eb2f96' }}>
                                {yard.ccChargesPercentage}%
                            </div>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    const currentContactPerson = getAssignmentOrPrimaryYardValue(currentYard?.contactPerson, currentYardDetails?.contactPerson);
    const currentContactEmail = getAssignmentOrPrimaryYardValue(currentYard?.contactEmail, currentYardDetails?.email);
    const currentContactNumber = getAssignmentOrPrimaryYardValue(currentYard?.contactNumber, currentYardDetails?.contactNumbers);
    const currentContactExtension = getAssignmentOrPrimaryYardValue(
        currentYard?.contactPersonExtension,
        currentYardDetails?.contactPersonExtension
    );

    return (
        <>
            <Card>
                {/* Header with Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Title level={4} style={{ margin: 0 }}>Yard Information</Title>
                    <Space>
                        {isSalesMade && !isAdmin && (
                            <Tag color="green" style={{ padding: '4px 12px' }}>
                                <CheckCircleOutlined /> Sales Made - Read Only
                            </Tag>
                        )}
                   
                        
                        { currentYard && (
                            <>
                                <Button
                                    type="default"
                                    onClick={handleEditClick}
                                    icon={<EditOutlined />}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onAssignYard}
                                    icon={<CarOutlined />}
                                >
                                    Change Yard
                                </Button>
                            </>
                        )}
                        
                        {!currentYard && (
                            <Button
                                type="primary"
                                onClick={onAssignYard}
                                icon={<PlusOutlined />}
                            >
                                Assign Yard
                            </Button>
                        )}
                     
                        
                        {!currentYard && (
                            <Tooltip title="Cannot assign yard after Sales Made stage">
                                <Button type="primary" disabled icon={<LockOutlined />}>Assign Yard</Button>
                            </Tooltip>
                        )}
                    </Space>
                </div>

                {isSalesMade && !isAdmin && (
                    <Alert
                        message="Lead Completed"
                        description="This lead has reached Sales Made stage. Yard information cannot be modified."
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {yardLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '16px', color: '#666' }}>Loading yard details...</div>
                    </div>
                ) : currentYard ? (
                    <Row gutter={[16, 16]}>
                        {yardError && (
                            <Col span={24}>
                                <Alert message="Yard Details Unavailable" description={yardError} type="warning" showIcon />
                            </Col>
                        )}

                        <Col span={24}>
                            <Card style={{ marginBottom: '16px', borderLeft: '4px solid #52c41a' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <CarOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                                            <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                                                {getYardDisplayName(currentYardDetails, currentYard)}
                                            </Title>
                                            <Tag color="green">Current</Tag>
                                        </div>
                                        <Text type="secondary">
                                            <EnvironmentOutlined style={{ marginRight: '4px' }} />
                                            {formatYardAddress(currentYardAddress) || 'N/A'}
                                        </Text>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Final Price</div>
                                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                            ${(currentYard.finalPrice || currentYard.price || 0).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <Card size="small" style={{ height: '100%' }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Contact Person</div>
                                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                    {currentContactPerson || 'N/A'}
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card size="small" style={{ height: '100%' }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email</div>
                                                <div>
                                                    {currentContactEmail ? (
                                                        <a href={`mailto:${currentContactEmail}`} style={{ fontSize: '14px' }}>
                                                            {currentContactEmail}
                                                        </a>
                                                    ) : 'N/A'}
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card size="small" style={{ height: '100%' }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Phone</div>
                                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                    {currentContactNumber || 'N/A'}
                                                </div>
                                                {currentContactExtension && (
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Ext: {currentContactExtension}
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Verification Display Section */}
                                {currentYard && renderVerificationDisplay(currentYard)}

                                <Card size="small" title="Price Breakdown" style={{ marginTop: '16px', backgroundColor: '#fafafa' }}>
                                    <Row gutter={[16, 16]}>
                                        <Col span={8}>
                                            <Statistic
                                                title="Car Parts Price (Original Price)"
                                                value={currentYard.originalPrice || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Yard Price"
                                                value={currentYard.price || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Yard Shipping"
                                                value={currentYard.yardShipping || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.yardShipping > 0 ? '#52c41a' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Own Shipping"
                                                value={currentYard.ownShipping || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.ownShipping > 0 ? '#52c41a' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Core Charges"
                                                value={currentYard.coreCharges || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.coreCharges > 0 ? '#722ed1' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        {/* NEW FIELDS DISPLAY */}
                                        <Col span={8}>
                                            <Statistic
                                                title="Extra Warranty Price"
                                                value={currentYard.extraWarrantyPrice || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.extraWarrantyPrice > 0 ? '#722ed1' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Boxing & Pallete Fee"
                                                value={currentYard.boxingPalleteFee || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.boxingPalleteFee > 0 ? '#fa8c16' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="CC Charges (%)"
                                                value={currentYard.ccChargesPercentage || 0}
                                                suffix="%"
                                                precision={2}
                                                valueStyle={{ fontSize: '16px', color: currentYard.ccChargesPercentage > 0 ? '#eb2f96' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Yard Warranty"
                                                value={currentYard.yardWarranty || 'N/A'}
                                                valueStyle={{ fontSize: '16px', color: currentYard.yardWarranty ? '#531dab' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Yard Miles"
                                                value={currentYard.yardMiles || 'N/A'}
                                                valueStyle={{ fontSize: '16px', color: currentYard.yardMiles ? '#096dd9' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Stock Number"
                                                value={formatYardAssignmentValue(currentYard.stockNumber)}
                                                valueStyle={{ fontSize: '16px', color: currentYard.stockNumber ? '#0958d9' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Quote Number"
                                                value={formatYardAssignmentValue(currentYard.quoteNumber)}
                                                valueStyle={{ fontSize: '16px', color: currentYard.quoteNumber ? '#0958d9' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Part Grade"
                                                value={formatYardAssignmentValue(currentYard.partGrade)}
                                                valueStyle={{ fontSize: '16px', color: currentYard.partGrade ? '#389e0d' : '#8c8c8c' }}
                                            />
                                        </Col>
                                        <Col span={24}>
                                            <Statistic
                                                title={(
                                                    <Space>
                                                        <span>Final Total</span>
                                                        <Popover
                                                            content={(
                                                                <div style={{ maxWidth: '300px' }}>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#52c41a' }}>
                                                                        Price Breakdown
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                                                                        <div>
                                                                            <div>Yard Price:</div>
                                                                            <div>Yard Shipping:</div>
                                                                            <div>Own Shipping:</div>
                                                                            <div>Core Charges:</div>
                                                                            {currentYard.extraWarrantyPrice > 0 && <div>Extra Warranty:</div>}
                                                                            {currentYard.boxingPalleteFee > 0 && <div>Boxing & Pallete:</div>}
                                                                            {currentYard.ccChargesPercentage > 0 && <div>CC Charges ({currentYard.ccChargesPercentage}%):</div>}
                                                                            <Divider style={{ margin: '8px 0' }} />
                                                                            <div style={{ fontWeight: 'bold' }}>Final Total:</div>
                                                                        </div>
                                                                        <div style={{ textAlign: 'right' }}>
                                                                            <div>${(currentYard.price || 0).toFixed(2)}</div>
                                                                            <div>+ ${(currentYard.yardShipping || 0).toFixed(2)}</div>
                                                                            <div>+ ${(currentYard.ownShipping || 0).toFixed(2)}</div>
                                                                            <div>+ ${(currentYard.coreCharges || 0).toFixed(2)}</div>
                                                                            {currentYard.extraWarrantyPrice > 0 && <div>+ ${currentYard.extraWarrantyPrice.toFixed(2)}</div>}
                                                                            {currentYard.boxingPalleteFee > 0 && <div>+ ${currentYard.boxingPalleteFee.toFixed(2)}</div>}
                                                                            {currentYard.ccChargesPercentage > 0 && (
                                                                                <div>+ ${((currentYard.price || 0) * (currentYard.ccChargesPercentage / 100)).toFixed(2)}</div>
                                                                            )}
                                                                            <Divider style={{ margin: '8px 0' }} />
                                                                            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                                                                ${(currentYard.finalPrice || currentYard.price || 0).toFixed(2)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            title="How Final Total is Calculated"
                                                            trigger="click"
                                                            placement="top"
                                                        >
                                                            <InfoCircleOutlined style={{ fontSize: '16px', color: '#1890ff', cursor: 'pointer', marginLeft: '8px' }} />
                                                        </Popover>
                                                    </Space>
                                                )}
                                                value={currentYard.finalPrice || currentYard.price || 0}
                                                prefix="$"
                                                precision={2}
                                                valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}
                                            />
                                        </Col>
                                        {/* Add this after the Final Total Statistic in the Price Breakdown card */}
{currentYard.verifiedAmountStats === 'YES' && currentYard.verifiedAmountPrice > 0 && (
    <Col span={24}>
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ 
            padding: '12px 16px', 
            backgroundColor: currentYard.verifiedAmountPrice !== (currentYard.finalPrice || currentYard.price || 0) 
                ? '#fff7e6' 
                : '#f6ffed',
            borderRadius: '8px',
            borderLeft: `4px solid ${
                currentYard.verifiedAmountPrice !== (currentYard.finalPrice || currentYard.price || 0) 
                    ? '#faad14' 
                    : '#52c41a'
            }`
        }}>
            <Row gutter={[16, 8]} align="middle">
                <Col span={8}>
                    <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <CheckCircleOutlined /> Verified Amount
                        </Text>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                            ${(currentYard.verifiedAmountPrice || 0).toFixed(2)}
                        </div>
                    </div>
                </Col>
                <Col span={8}>
                    <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <DollarOutlined /> Final Amount
                        </Text>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                            ${(currentYard.finalPrice || currentYard.price || 0).toFixed(2)}
                        </div>
                    </div>
                </Col>
                <Col span={8}>
                    <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <DiffOutlined /> Difference
                        </Text>
                        <div style={{ 
                            fontSize: '20px', 
                            fontWeight: 'bold',
                            color: (currentYard.verifiedAmountPrice || 0) !== (currentYard.finalPrice || currentYard.price || 0) 
                                ? '#ff4d4f' 
                                : '#52c41a'
                        }}>
                            {(() => {
                                const diff = (currentYard.verifiedAmountPrice || 0) - (currentYard.finalPrice || currentYard.price || 0);
                                return (
                                    <>
                                        {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                                        {diff !== 0 && (
                                            <Tag 
                                                color={diff > 0 ? 'orange' : 'red'} 
                                                style={{ marginLeft: '8px', fontSize: '11px' }}
                                            >
                                                {diff > 0 ? 'Over' : 'Under'}
                                            </Tag>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </Col>
            </Row>
           
            {currentYard.verifiedAmountPrice === (currentYard.finalPrice || currentYard.price || 0) && (
                <div style={{ marginTop: '8px' }}>
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                        Amounts Match ✓
                    </Tag>
                </div>
            )}
        </div>
    </Col>
)}
                                    </Row>
                                </Card>

                                {currentYard.description && (
                                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                        <Text strong style={{ fontSize: '12px', color: '#666' }}>Description: </Text>
                                        <Text>{currentYard.description}</Text>
                                    </div>
                                )}

                                {currentYardDetails?.link && (
                                    <div style={{ marginTop: '16px' }}>
                                        <Text strong style={{ fontSize: '12px', color: '#666' }}>Website: </Text>
                                        <a href={currentYardDetails.link} target="_blank" rel="noopener noreferrer">
                                            {currentYardDetails.link}
                                        </a>
                                    </div>
                                )}

                                {currentYard.yardConfirmDate && (
                                    <div style={{ marginTop: '16px', textAlign: 'right' }}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            <CalendarOutlined /> Confirmed: {formatDate(currentYard.yardConfirmDate)}
                                        </Text>
                                    </div>
                                )}
                            </Card>
                        </Col>

                        {previousYards.length > 0 && (
                            <Col span={24}>
                                <Card title={`Yard History (${previousYards.length})`} size="small">
                                    <Row gutter={[16, 16]}>
                                        {previousYards.map((yard, index) => {
                                            const historyYardId = normalizeYardId(yard.yardId);
                                            const historyDetails = historyYardId ? yardDetailsMap[historyYardId] : null;
                                            const historyAddress = yard?.yardAddress;
                                            
                                            return (
                                                <Col xs={24} md={12} xl={8} key={`${historyYardId || index}-${index}`}>
                                                    <Card size="small" style={{ height: '100%', borderTop: '3px solid #91caff' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                            <Text strong>{getYardDisplayName(historyDetails, yard)}</Text>
                                                            <Tag color="default">History</Tag>
                                                        </div>

                                                        <div style={{ marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                                                            Yard ID: {normalizeYardId(yard.yardId) || 'N/A'}
                                                        </div>

                                                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1677ff', marginBottom: '12px' }}>
                                                            ${(yard.finalPrice || yard.price || 0).toFixed(2)}
                                                        </div>

                                                        <div style={{ marginBottom: '8px' }}>
                                                            <Text type="secondary">Contact:</Text>{' '}
                                                            <Text>{getAssignmentOrPrimaryYardValue(yard.contactPerson, historyDetails?.contactPerson) || 'N/A'}</Text>
                                                        </div>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <Text type="secondary">Phone:</Text>{' '}
                                                            <Text>
                                                                {getAssignmentOrPrimaryYardValue(yard.contactNumber, historyDetails?.contactNumbers) || 'N/A'}
                                                                {getAssignmentOrPrimaryYardValue(yard.contactPersonExtension, historyDetails?.contactPersonExtension)
                                                                    ? ` Ext: ${getAssignmentOrPrimaryYardValue(yard.contactPersonExtension, historyDetails?.contactPersonExtension)}`
                                                                    : ''}
                                                            </Text>
                                                        </div>

                                                        <div style={{ marginBottom: '8px' }}>
                                                            <Text type="secondary">Address:</Text>{' '}
                                                            <Text>{formatYardAddress(historyAddress) || 'N/A'}</Text>
                                                        </div>

                                                        {(yard.yardWarranty || yard.yardMiles) && (
                                                            <div style={{ marginBottom: '8px' }}>
                                                                {yard.yardWarranty && <Tag color="purple">Warranty: {yard.yardWarranty}</Tag>}
                                                                {yard.yardMiles && <Tag color="geekblue">Miles: {yard.yardMiles}</Tag>}
                                                            </div>
                                                        )}

                                                        {(yard.stockNumber || yard.quoteNumber || yard.partGrade) && (
                                                            <div style={{ marginBottom: '8px' }}>
                                                                {yard.stockNumber && <Tag color="blue">Stock #: {yard.stockNumber}</Tag>}
                                                                {yard.quoteNumber && <Tag color="cyan">Quote #: {yard.quoteNumber}</Tag>}
                                                                {yard.partGrade && <Tag color="green">Grade: {yard.partGrade}</Tag>}
                                                            </div>
                                                        )}

                                                        {(yard.extraWarrantyPrice > 0 || yard.boxingPalleteFee > 0 || yard.ccChargesPercentage > 0) && (
                                                            <div style={{ marginTop: '8px', padding: '6px', backgroundColor: '#f0f5ff', borderRadius: '6px' }}>
                                                                <Text type="secondary" style={{ fontSize: '11px' }}>Extra: </Text>
                                                                {yard.extraWarrantyPrice > 0 && <Tag color="purple" style={{ fontSize: '10px' }}>Warranty: ${yard.extraWarrantyPrice}</Tag>}
                                                                {yard.boxingPalleteFee > 0 && <Tag color="orange" style={{ fontSize: '10px' }}>Boxing: ${yard.boxingPalleteFee}</Tag>}
                                                                {yard.ccChargesPercentage > 0 && <Tag color="pink" style={{ fontSize: '10px' }}>CC: {yard.ccChargesPercentage}%</Tag>}
                                                            </div>
                                                        )}

                                                        {/* Verification display in history */}
                                                        {yard.verifiedAmountStats === 'YES' && (
                                                            <div style={{ marginTop: '8px', padding: '4px 8px', backgroundColor: '#f6ffed', borderRadius: '4px' }}>
                                                                <Tag color="success" size="small" style={{ fontSize: '10px' }}>
                                                                    <CheckCircleOutlined /> Verified
                                                                </Tag>
                                                                <Text style={{ fontSize: '11px', marginLeft: '4px' }}>
                                                                    ${(yard.verifiedAmountPrice || 0).toFixed(2)}
                                                                </Text>
                                                            </div>
                                                        )}

                                                        {yard.description && (
                                                            <div style={{ marginTop: '12px' }}>
                                                                <Text type="secondary">Description:</Text>{' '}
                                                                <Text>{yard.description}</Text>
                                                            </div>
                                                        )}

                                                        {canEdit && (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                style={{ padding: 0, marginTop: '12px' }}
                                                                loading={switchingYardKey === `${historyYardId || index}-${index}`}
                                                                onClick={() => handleSetCurrentFromHistory(yard, `${historyYardId || index}-${index}`)}
                                                            >
                                                                Make Current
                                                            </Button>
                                                        )}
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card>
                            </Col>
                        )}

                        {Array.isArray(lead?.yardReassignmentHistory) && lead.yardReassignmentHistory.length > 0 && (
                            <Col span={24}>
                                <Card
                                    title={`Yard Reassignment History (${lead.yardReassignmentHistory.length})`}
                                    size="small"
                                    extra={(
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<FileTextOutlined />}
                                            onClick={() => setReassignHistoryModalOpen(true)}
                                        >
                                            History Notes
                                        </Button>
                                    )}
                                >
                                    <Timeline
                                        mode="left"
                                        items={[...lead.yardReassignmentHistory]
                                            .sort((a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0))
                                            .map((entry, index) => {
                                                const isInitial = !entry.previousYardId;
                                                const actor = entry.changedByName
                                                    || (entry.changedBy && typeof entry.changedBy === 'object'
                                                        ? ([entry.changedBy.firstName, entry.changedBy.lastName].filter(Boolean).join(' ').trim() || entry.changedBy.userName)
                                                        : '')
                                                    || entry.changedByEmail
                                                    || 'Unknown user';
                                                const prevName = entry.previousYardName
                                                    || (entry.previousYardId ? (yardDetailsMap[normalizeYardId(entry.previousYardId)]?.yardName || normalizeYardId(entry.previousYardId)) : '');
                                                const newName = entry.newYardName
                                                    || (yardDetailsMap[normalizeYardId(entry.newYardId)]?.yardName || normalizeYardId(entry.newYardId));

                                                return {
                                                    key: entry._id || index,
                                                    color: index === 0 ? 'green' : 'blue',
                                                    children: (
                                                        <div style={{ paddingBottom: '8px' }}>
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <Tag color={isInitial ? 'blue' : 'purple'}>
                                                                    {isInitial ? 'Yard assigned' : 'Yard reassigned'}
                                                                </Tag>
                                                                {index === 0 && <Tag color="green">Current</Tag>}
                                                            </div>
                                                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                                                                {isInitial ? (
                                                                    <Text strong>{newName || 'N/A'}</Text>
                                                                ) : (
                                                                    <>
                                                                        <Text delete type="secondary">{prevName || 'N/A'}</Text>
                                                                        {' → '}
                                                                        <Text strong>{newName || 'N/A'}</Text>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                                {entry.changedAt ? formatDate(entry.changedAt) : 'Date not available'} · {actor}
                                                            </div>
                                                            {entry.comment && (
                                                                <div style={{ marginTop: '6px', padding: '6px 10px', background: '#fafafa', borderRadius: '6px', fontSize: '12px' }}>
                                                                    {entry.comment}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                };
                                            })}
                                    />
                                </Card>
                            </Col>
                        )}
                    </Row>
                ) : (
                    <div style={{ textAlign: 'center', padding: '64px 16px' }}>
                        <CarOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '24px' }} />
                        <Title level={4} style={{ color: '#666', marginBottom: '16px' }}>
                            No Yard Assigned
                        </Title>
                        <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
                            Assign a yard to this lead to track inventory and pricing
                        </Text>
                        
                            <Button type="primary" size="large" onClick={onAssignYard} icon={<PlusOutlined />} style={{ minWidth: '200px' }}>
                                Assign Yard
                            </Button>
                      
                    </div>
                )}
            </Card>

            <Modal
                title={
                    <div className="yard-assignment-modal-title">
                        <span className="yard-assignment-modal-title__icon"><EditOutlined /></span>
                        <span className="yard-assignment-modal-title__copy">
                            <strong>Edit Yard Assignment</strong>
                            <span>Update the current yard's lead-specific pricing and details.</span>
                        </span>
                    </div>
                }
                className="yard-assignment-modal"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleEditSubmit}
                confirmLoading={editLoading}
                centered
                width={1040}
                okText="Save Changes"
                cancelText="Cancel"
            >
                <Card
                    className="yard-assignment-details-card yard-assignment-fields"
                    size="small"
                    title={
                        <div className="yard-assignment-selected-yard">
                            <span className="yard-assignment-selected-yard__icon"><ShopOutlined /></span>
                            <span className="yard-assignment-selected-yard__copy">
                                <strong>{getYardDisplayName(currentYardDetails, currentYard)}</strong>
                                <span>{formatYardAddress(currentYardAddress) || 'No assignment address saved'}</span>
                            </span>
                        </div>
                    }
                    extra={<Tag className="yard-assignment-current-tag">Current yard</Tag>}
                >
                    <YardContactFields
                        yardId={currentYardId}
                        yardDetails={currentYardDetails}
                        yardDraft={editContactDraft}
                        onFieldChange={handleEditContactFieldChange}
                        onContactSelect={handleEditContactSelect}
                    />

                    <Form.Item label="Yard Address">
                        <Input.TextArea
                            placeholder="Enter the yard location for this lead assignment"
                            value={editYardAddress}
                            onChange={(e) => setEditYardAddress(e.target.value)}
                            rows={2}
                        />
                    </Form.Item>

                    <Divider style={{ margin: '4px 0 22px', borderColor: '#e9edf2' }} />
                    <div className="yard-assignment-section-heading">
                        <span className="yard-assignment-section-heading__icon"><DollarOutlined /></span>
                        <span className="yard-assignment-section-heading__copy">
                            <strong>Pricing & fees</strong>
                        </span>
                    </div>

                    <Row gutter={[18, 2]}>
                        <Col span={12}>
                            <Form.Item label="Car Parts Price (Original Price)">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter original price"
                                    value={editOriginalPrice}
                                    onChange={setEditOriginalPrice}
                                    min={0}
                                    precision={2}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Yard Price">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter yard price"
                                    value={editPrice}
                                    onChange={setEditPrice}
                                    min={0}
                                    precision={2}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[18, 2]}>
                        <Col span={12}>
                            <Form.Item label="Yard Shipping" tooltip="Shipping cost charged by the yard">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter yard shipping"
                                    value={editYardShipping}
                                    onChange={handleYardShippingChange}
                                    min={0}
                                    precision={2}
                                    disabled={editOwnShipping > 0}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                                {editOwnShipping > 0 && <Text type="secondary" style={{ fontSize: '11px' }}>Disabled because Own Shipping is set</Text>}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Own Shipping" tooltip="Your own shipping cost">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter own shipping"
                                    value={editOwnShipping}
                                    onChange={handleOwnShippingChange}
                                    min={0}
                                    precision={2}
                                    disabled={editYardShipping > 0}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                                {editYardShipping > 0 && <Text type="secondary" style={{ fontSize: '11px' }}>Disabled because Yard Shipping is set</Text>}
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[18, 2]}>
                        <Col span={12}>
                            <Form.Item label="Core Charges" tooltip="Core deposit charges">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter core charges"
                                    value={editCoreCharges}
                                    onChange={setEditCoreCharges}
                                    min={0}
                                    precision={2}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Extra Warranty Price" tooltip="Additional warranty price for the part">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter extra warranty price"
                                    value={editExtraWarrantyPrice}
                                    onChange={setEditExtraWarrantyPrice}
                                    min={0}
                                    precision={2}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[18, 2]}>
                        <Col span={12}>
                            <Form.Item label="Boxing & Pallete Fee" tooltip="Fee for boxing and pallete handling">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter boxing & pallete fee"
                                    value={editBoxingPalleteFee}
                                    onChange={setEditBoxingPalleteFee}
                                    min={0}
                                    precision={2}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="CC Charges (%)" tooltip="Credit card charges as percentage">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter CC charges percentage"
                                    value={editCcChargesPercentage}
                                    onChange={setEditCcChargesPercentage}
                                    min={0}
                                    max={100}
                                    precision={2}
                                    formatter={value => `${value}%`}
                                    parser={value => value.replace('%', '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '4px 0 22px', borderColor: '#e9edf2' }} />
                    <div className="yard-assignment-section-heading">
                        <span className="yard-assignment-section-heading__icon"><EnvironmentOutlined /></span>
                        <span className="yard-assignment-section-heading__copy">
                            <strong>Assignment details</strong>
                            <span>Update the values stored specifically on this yard assignment.</span>
                        </span>
                    </div>

                    <Row gutter={[18, 2]}>
                        <Col span={12}>
                            <Form.Item label="Yard Warranty">
                                <Input
                                    placeholder="Enter yard warranty"
                                    value={editYardWarranty}
                                    onChange={(e) => setEditYardWarranty(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Yard Miles">
                                <Input
                                    placeholder="Enter yard miles"
                                    value={editYardMiles}
                                    onChange={(e) => setEditYardMiles(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[18, 2]}>
                        <Col span={8}>
                            <Form.Item label="Stock Number">
                                <Input
                                    placeholder="Enter stock number"
                                    value={editStockNumber}
                                    onChange={(e) => setEditStockNumber(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Quote Number">
                                <Input
                                    placeholder="Enter quote number"
                                    value={editQuoteNumber}
                                    onChange={(e) => setEditQuoteNumber(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Part Grade">
                                <Input
                                    placeholder="Enter part grade"
                                    value={editPartGrade}
                                    onChange={(e) => setEditPartGrade(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Description / Internal Notes" style={{ marginBottom: 18 }}>
                        <Input.TextArea
                            rows={3}
                            placeholder="Add optional assignment notes"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                    </Form.Item>

                    {/* Verification Section */}
                    <div className="yard-assignment-section-heading">
                        <span className="yard-assignment-section-heading__icon"><CheckCircleOutlined /></span>
                        <span className="yard-assignment-section-heading__copy">
                            <strong>Verification Details</strong>
                            <span>Track amount verification status and details</span>
                        </span>
                    </div>

                    <Row gutter={[18, 2]}>
                        <Col span={8}>
                            <Form.Item
                                label={(
                                    <Space>
                                        <CheckCircleOutlined />
                                        <span>Verified Amount</span>
                                    </Space>
                                )}
                                tooltip="Indicates whether the amount has been verified"
                                style={{ marginBottom: 18 }}
                            >
                                <Select
                                    placeholder="Select verification status"
                                    value={editVerifiedAmountStats || 'NO'}
                                    onChange={setEditVerifiedAmountStats}
                                    style={{ width: '100%' }}
                                >
                                    <Select.Option value="NO">
                                        <Space>
                                            <ClockCircleOutlined style={{ color: '#faad14' }} />
                                            <span>NO</span>
                                        </Space>
                                    </Select.Option>
                                    <Select.Option value="YES">
                                        <Space>
                                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                            <span>YES</span>
                                        </Space>
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={(
                                    <Space>
                                        <DollarOutlined />
                                        <span>Verified Amount Price</span>
                                    </Space>
                                )}
                                tooltip="The verified price amount"
                                style={{ marginBottom: 18 }}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Enter verified amount"
                                    value={editVerifiedAmountPrice}
                                    onChange={setEditVerifiedAmountPrice}
                                    min={0}
                                    precision={2}
                                    disabled={editVerifiedAmountStats !== 'YES'}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={(
                                    <Space>
                                        <ClockCircleOutlined />
                                        <span>Verified Date</span>
                                    </Space>
                                )}
                                tooltip="Date when the verification was performed"
                                style={{ marginBottom: 18 }}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="Select verification date"
                                    value={editVerifiedDate ? dayjs(editVerifiedDate) : null}
                                    onChange={(date) => setEditVerifiedDate(date ? date.toISOString() : null)}
                                    disabled={editVerifiedAmountStats !== 'YES'}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    showTime
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="yard-assignment-total">
                        <div className="yard-assignment-total__copy">
                            <strong>Final assignment total</strong>
                            <span>Yard price, shipping, fees, core charges, warranty, boxing, and CC fee.</span>
                        </div>
                        <Statistic
                            title="Calculated total"
                            value={editFinalPrice}
                            prefix="$"
                            precision={2}
                            valueStyle={{ color: '#15803d', fontSize: '26px', fontWeight: 700 }}
                        />
                    </div>
                </Card>
            </Modal>

            <YardReassignmentHistoryModal
                open={reassignHistoryModalOpen}
                onClose={() => setReassignHistoryModalOpen(false)}
                history={lead?.yardReassignmentHistory}
                leadId={lead?._id}
                yards={lead?.yard}
                yardDetailsMap={yardDetailsMap}
                leadName={lead?.personalInfo?.fullName}
                orderNumber={lead?.orderNumber}
                onNotesChanged={() => { if (typeof onUpdateSuccess === 'function') onUpdateSuccess(); }}
            />
        </>
    );
};

const ZohoTab = ({ lead, onSendZoho, onPreviewInvoice, onFTWEPreviewInvoice, onUpdateSuccess, isAdmin }) => {
    // State variables
    const [zohoDocAllow, setZohoDocAllow] = useState(lead?.zohoDocAllow);
    const [updating, setUpdating] = useState(false);

    // Sync with lead data
    useEffect(() => {
        if (lead) {
            setZohoDocAllow(lead.zohoDocAllow);
        }
    }, [lead]);

    // Handle Zoho Doc Allow Change - Immediate save
    const handleZohoDocAllowChange = async (value) => {
        // Don't update if it's the same value
        if (value === zohoDocAllow) return;
        
        try {
            setUpdating(true);
            
            // Update local state immediately for UI feedback
            setZohoDocAllow(value);
            
            // Direct API call
            const payload = {
                zohoDocAllow: value
            };
            
            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status || response?.success) {
                message.success('Zoho document setting updated');
                if (onUpdateSuccess) {
                    onUpdateSuccess(); // Refresh lead data
                }
            } else {
                // Revert on error
                setZohoDocAllow(lead?.zohoDocAllow);
                message.error(response?.message || 'Failed to update');
            }
        } catch (error) {
            console.error('Error updating:', error);
            setZohoDocAllow(lead?.zohoDocAllow);
            message.error('Failed to update');
        } finally {
            setUpdating(false);
        }
    };

    const getPaymentMethodColor = (method) => {
        switch (method?.toLowerCase()) {
            case 'card': return 'blue';
            case 'payment link': return 'purple';
            case 'check': return 'orange';
            case 'zelle': return 'green';
            case 'cashapp': return 'green';
            case 'venmo': return '#3d95ce';
            case 'paypal': return '#003087';
            case 'wire transfer': return 'cyan';
            case 'cash': return 'gold';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('MM-DD-YYYY hh:mm A');
    };

    // Check if Zoho Sign can be generated
    const canGenerateZohoSign = () => {
        return lead?.zohoDocAllow === 'Yes';  // Only need Zoho Doc Allow
    };

    return (
        <Card>
            {/* Header */}
            <Title level={4} style={{ marginBottom: '24px' }}>Zoho Sign Documents</Title>

            {/* Zoho Document Allow Card */}
            <Card 
                size="small" 
                style={{ 
                    marginBottom: '24px',
                    backgroundColor: '#fafafa',
                    border: '1px solid #e8e8e8'
                }}
            >
                <Row gutter={[16, 16]}>
                  
                    {/* Zoho Doc Allow - Single Field */}
                    <Col span={24}>
                        <div style={{ marginBottom: '8px' }}>
                            <Text strong>Zoho Document Allow</Text>
                            <Text type="secondary" style={{ marginLeft: '8px' }}>
                                (Required for sending)
                            </Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Select
                                value={zohoDocAllow}
                                onChange={handleZohoDocAllowChange}
                                style={{ flex: 1 }}
                                disabled={updating}
                                placeholder="Select Yes or No"
                                size="large"
                            >
                                <Option value="No">
                                    <Space>
                                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                        <span>No - Disable</span>
                                    </Space>
                                </Option>
                                <Option value="Yes">
                                    <Space>
                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        <span>Yes - Enable</span>
                                    </Space>
                                </Option>
                            </Select>
                            {zohoDocAllow === 'Yes' ? (
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px' }} />
                            ) : zohoDocAllow === 'No' ? (
                                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px' }} />
                            ) : null}
                        </div>
                        {updating && (
                            <div style={{ marginTop: '12px', color: '#1890ff' }}>
                                <Spin size="small" /> Updating...
                            </div>
                        )}
                    </Col>
                </Row>
            </Card>

            {/* Action Buttons */}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        <Tooltip title={
                            !canGenerateZohoSign() 
                                ? "Zoho Document Allow must be 'Yes' to send" 
                                : ""
                        }>
                            <Button
                                type="primary"
                                size="large"
                                onClick={onSendZoho} 
                                icon={<SendOutlined />}
                                style={{ 
                                    minWidth: '200px', 
                                    height: '48px',
                                    opacity: !canGenerateZohoSign() ? 0.5 : 1
                                }}
                                disabled={!canGenerateZohoSign() || updating}
                            >
                                Send Zoho Sign
                            </Button>
                        </Tooltip>

                        <Button
                            type="default"
                            size="large"
                            onClick={onPreviewInvoice}
                            icon={<FileTextOutlined />}
                            style={{ minWidth: '200px', height: '48px' }}
                            disabled={updating}
                        >
                            Preview Invoice
                        </Button>

                        {isAdmin ? (
                            <Button
                                type="default"
                                size="large"
                                onClick={onFTWEPreviewInvoice}
                                icon={<FileTextOutlined />}
                                style={{
                                    minWidth: '200px',
                                    height: '48px',
                                    background: '#722ed1',
                                    color: 'white',
                                    borderColor: '#722ed1'
                                }}
                                disabled={updating}
                            >
                                Purchase Order
                            </Button>
                        ) : (
                            <Tooltip title="Please go to Order Process page to send Purchase Order">
                                <Button
                                    type="default"
                                    size="large"
                                    icon={<FileTextOutlined />}
                                    disabled
                                    style={{
                                        minWidth: '200px',
                                        height: '48px',
                                        opacity: 0.5,
                                        cursor: 'not-allowed'
                                    }}
                                >
                                    Purchase Order
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </Col>

                {/* Status Information */}
                {lead.zohoStatus && (
                    <Col span={24}>
                        <Card 
                            title="Document Status" 
                            size="small"
                            style={{ marginTop: '16px' }}
                        >
                            <Descriptions column={2} bordered size="small">
                                <Descriptions.Item label="Status">
                                    <Tag color={getPaymentMethodColor(lead.zohoStatus)}>
                                        {lead.zohoStatus}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Last Updated">
                                    {formatDate(lead.updatedAt)}
                                </Descriptions.Item>
                                {lead.zohoSign?.documentId && (
                                    <Descriptions.Item label="Document ID" span={2}>
                                        <Text copyable>{lead.zohoSign.documentId}</Text>
                                    </Descriptions.Item>
                                )}
                                {lead.zohoSign?.sentAt && (
                                    <Descriptions.Item label="Sent At">
                                        {formatDate(lead.zohoSign.sentAt)}
                                    </Descriptions.Item>
                                )}
                                {lead.zohoSign?.signedAt && (
                                    <Descriptions.Item label="Signed At">
                                        {formatDate(lead.zohoSign.signedAt)}
                                    </Descriptions.Item>
                                )}
                                {lead.zohoSign?.signerEmail && (
                                    <Descriptions.Item label="Signer Email" span={2}>
                                        {lead.zohoSign.signerEmail}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    </Col>
                )}

                {/* Document History */}
                {lead.zohoSign?.history && lead.zohoSign.history.length > 0 && (
                    <Col span={24}>
                        <Card title="Document History" size="small">
                            <Timeline>
                                {lead.zohoSign.history.map((item, index) => (
                                    <Timeline.Item key={index} color={item.status === 'completed' ? 'green' : 'blue'}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{item.action}</span>
                                            <Text type="secondary">{formatDate(item.timestamp)}</Text>
                                        </div>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        </Card>
                    </Col>
                )}

                {/* No Documents Message */}
                {!lead.zohoStatus && (
                    <Col span={24}>
                        <Card style={{ textAlign: 'center', padding: '40px', backgroundColor: '#fafafa' }}>
                            <InfoCircleOutlined style={{ fontSize: '48px', color: '#fa8c16', marginBottom: '16px' }} />
                            <Title level={4} style={{ color: '#666', marginBottom: '8px' }}>
                                No Zoho Sign Documents
                            </Title>
                            <Text type="secondary">
                                Set Zoho Document Allow to "Yes" to enable sending
                            </Text>
                            <div style={{ marginTop: '16px' }}>
                                <Tag color={lead?.zohoDocAllow === 'Yes' ? 'green' : 'orange'} style={{ padding: '8px 16px' }}>
                                    Current: {lead?.zohoDocAllow || 'Not Set'}
                                </Tag>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>
        </Card>
    );
};



const ShipmentTrackingTab = ({ shipmentData, lead, leadId, onUpdateSuccess }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);

  const getStatusConfig = (value) => {
    if (!value) return { color: 'default', icon: null, text: 'N/A' };
    const val = value.toLowerCase();
    
    // Delivery Status mapping
    if (val.includes('delivered')) 
      return { color: 'success', icon: <CheckCircleOutlined />, text: value };
    if (val.includes('not delivered')) 
      return { color: 'error', icon: <CloseCircleOutlined />, text: value };
    
    // Part Status mapping
    if (val.includes('available')) 
      return { color: 'success', icon: <CheckCircleOutlined />, text: value };
    if (val.includes('not available')) 
      return { color: 'error', icon: <CloseCircleOutlined />, text: value };
    if (val.includes('not good')) 
      return { color: 'warning', icon: <WarningOutlined />, text: value };
    if (val.includes('wrong part')) 
      return { color: 'error', icon: <DisconnectOutlined />, text: value };
    if (val.includes('customer canceled')) 
      return { color: 'default', icon: <StopOutlined />, text: value };
    
    return { color: 'default', icon: null, text: value };
  };

  const getPriorityConfig = (priority) => {
    switch(priority) {
      case 'High': 
        return { color: 'error', icon: <ExclamationCircleOutlined />, label: 'High Priority' };
      case 'Medium': 
        return { color: 'warning', icon: <FlagOutlined />, label: 'Medium Priority' };
      case 'Low': 
        return { color: 'success', icon: <CheckCircleOutlined />, label: 'Low Priority' };
      default: 
        return { color: 'default', icon: null, label: 'Medium Priority' };
    }
  };

  // The stored default is "Not Available"; treat it as Not Set until someone chooses
  const partStatusUnset = isPartStatusUnset(lead);
  const shownPartStatus = partStatusUnset ? undefined : shipmentData?.partStatus;
  const partCard = partStatusUnset
    ? { bg: '#fafafa', border: '#d9d9d9', iconBg: '#f0f0f0', icon: '#8c8c8c' }
    : shipmentData?.partStatus === 'Available'
      ? { bg: '#f6ffed', border: '#b7eb8f', iconBg: '#b7eb8f', icon: '#52c41a' }
      : shipmentData?.partStatus === 'Not Good'
        ? { bg: '#fff7e6', border: '#ffe58f', iconBg: '#ffe58f', icon: '#faad14' }
        : { bg: '#fff2f0', border: '#ffccc7', iconBg: '#ffccc7', icon: '#f5222d' };

  const deliveryStatus = getStatusConfig(shipmentData?.deliveryStatus);
  const partStatus = getStatusConfig(shownPartStatus);
  const priorityConfig = getPriorityConfig(shipmentData?.priority || 'Medium');

  const showEditModal = () => {
    form.setFieldsValue({
      trackingStatus: shipmentData?.trackingStatus,
      shippingCompanyName: shipmentData?.shippingCompanyName,
      deliveryStatus: shipmentData?.deliveryStatus,
      // Part Status and Priority stay empty (both required) until someone has chosen them
      partStatus: shownPartStatus,
      picturesApproval: shipmentData?.picturesApproval,
      picturesStatus: shipmentData?.picturesStatus,
      priority: shipmentData?.priority,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const getPriorityFromPartStatus = (status) => {
    if (status === "Available" || status === "Customer Canceled Order") {
      return "Medium";
    }
    if (
      status === "Not Available" ||
      status === "Not Good" ||
      status === "Wrong Part"
    ) {
      return "High";
    }
    return "Low";
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      const values = await form.validateFields();
      
      let purchaseOrderStatus;
      
      // Auto change PO Status
      if (
        values.partStatus === "Not Available" ||
        values.partStatus === "Not Good" ||
        values.partStatus === "Wrong Part"
      ) {
        purchaseOrderStatus = "PO-NOT-SENT";
      }
      
      const payload = {
        ShipmentTracking: {
          trackingStatus: values.trackingStatus,
          shippingCompanyName: values.shippingCompanyName,
          deliveryStatus: values.deliveryStatus,
          partStatus: values.partStatus,
          picturesApproval: values.picturesApproval,
          picturesStatus: values.picturesStatus,
          priority: values.priority,
          lastUpdated: new Date().toISOString()
        },
        purchaseOrderStatus: purchaseOrderStatus,
      };
      
      // Sync OrderProcessComplete based on conditions
      const orderProcessCompleteUpdate = {};
      
      // Condition 1: Sync deliveryStatus
      if (values.deliveryStatus === "Delivered") {
        orderProcessCompleteUpdate.deliveryStatus = "Delivered";
      } else if (values.deliveryStatus === "Not Delivered") {
        orderProcessCompleteUpdate.deliveryStatus = "Not Delivered";
      }
      
      // Condition 2: Sync partStatus
      if (values.partStatus === "Customer Canceled Order") {
        orderProcessCompleteUpdate.partStatus = "Customer Canceled";
      } else {
        orderProcessCompleteUpdate.partStatus = "Customer Accepted";
      }
      
      // Only add orderProcessComplete to payload if we have updates
      if (Object.keys(orderProcessCompleteUpdate).length > 0) {
        payload.orderProcessComplete = {
          ...lead?.orderProcessComplete,
          ...orderProcessCompleteUpdate
        };
      }
      
      const response = await API.UpdateLead(leadId, payload);
      
      if (response?.status || response?.success) {
        message.success("Shipment tracking updated successfully!");
        setIsModalVisible(false);
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } else {
        message.error(response?.message || "Failed to update shipment tracking");
      }
    } catch (error) {
      console.error("Error updating shipment tracking:", error);
      message.error("Failed to update shipment tracking");
    } finally {
      setUpdating(false);
    }
  };

  const getPartStatusOptions = () => {
    const baseOptions = [
      { value: "Available", icon: <CheckCircleOutlined />, color: "#52c41a" },
      { value: "Not Available", icon: <CloseCircleOutlined />, color: "#f5222d" },
      { value: "Not Good", icon: <WarningOutlined />, color: "#faad14" },
      { value: "Wrong Part", icon: <DisconnectOutlined />, color: "#f5222d" },
      { value: "Customer Canceled Order", icon: <StopOutlined />, color: "#8c8c8c" }
    ];
    return baseOptions;
  };

  return (
    <>
      <Card
        bordered={false}
        style={{ 
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)", 
          borderRadius: 16,
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: 0 }}
        extra={
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={showEditModal}
            style={{ 
              borderRadius: 30,
              padding: '4px 20px',
              height: 40,
              fontWeight: 500
            }}
          >
            Edit Details
          </Button>
        }
      >
        {/* Header Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
          padding: '24px 32px',
          color: 'white'
        }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space size={16} align="center">
                <div style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  width: 48, 
                  height: 48, 
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CarOutlined style={{ color: 'white', fontSize: 24 }} />
                </div>
                <div>
                  <Title level={4} style={{ color: 'white', margin: 0 }}>Shipment Tracking</Title>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                    Tracking #: {shipmentData?.trackingStatus || 'N/A'} 
                    {shipmentData?.shippingCompanyName && (
                      <> • {shipmentData.shippingCompanyName}</>
                    )}
                    {shipmentData?.lastUpdated && (
                      <> • Updated: {dayjs(shipmentData.lastUpdated).format('MM-DD-YYYY')}</>
                    )}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Tag 
                color={priorityConfig.color}
                icon={priorityConfig.icon}
                style={{ 
                  padding: '6px 16px', 
                  borderRadius: 30,
                  fontSize: 14,
                  fontWeight: 600,
                  border: 'none'
                }}
              >
                {priorityConfig.label}
              </Tag>
            </Col>
          </Row>
        </div>

        {/* Main Content */}
        <div style={{ padding: '32px' }}>
      
          {/* Status Cards Grid */}
          <Row gutter={[24, 24]}>
            {/* Tracking Status Card */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ 
                background: '#f8f9fa', 
                borderRadius: 12, 
                padding: 20,
                height: '100%',
                border: '1px solid #f0f0f0',
                transition: 'all 0.3s',
              }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space align="center">
                    <div style={{ 
                      background: '#e6f7ff', 
                      width: 40, 
                      height: 40, 
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                    </div>
                    <Text strong style={{ fontSize: 16 }}>Tracking Number</Text>
                  </Space>
                  <Tag 
                    color="processing"
                    style={{ 
                      padding: '8px 16px', 
                      borderRadius: 30,
                      fontSize: 14,
                      fontWeight: 500,
                      margin: '8px 0',
                      width: 'fit-content'
                    }}
                  >
                    {shipmentData?.trackingStatus || "Not Assigned"}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Shipment tracking identifier
                  </Text>
                </Space>
              </div>
            </Col>

            {/* Shipping Company Card */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ 
                background: shipmentData?.shippingCompanyName ? '#f6ffed' : '#fff7e6',
                borderRadius: 12, 
                padding: 20,
                height: '100%',
                border: `1px solid ${shipmentData?.shippingCompanyName ? '#b7eb8f' : '#ffd591'}`
              }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space align="center">
                    <div style={{ 
                      background: shipmentData?.shippingCompanyName ? '#b7eb8f' : '#ffe58f',
                      width: 40, 
                      height: 40, 
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TruckOutlined style={{ 
                        color: shipmentData?.shippingCompanyName ? '#52c41a' : '#faad14', 
                        fontSize: 20 
                      }} />
                    </div>
                    <Text strong style={{ fontSize: 16 }}>Shipping Company</Text>
                  </Space>
                  <Tag 
                    color={shipmentData?.shippingCompanyName ? 'success' : 'warning'}
                    style={{ 
                      padding: '8px 16px', 
                      borderRadius: 30,
                      fontSize: 14,
                      fontWeight: 500,
                      margin: '8px 0'
                    }}
                  >
                    {shipmentData?.shippingCompanyName || "Not Assigned"}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {shipmentData?.shippingCompanyName ? 'Carrier assigned' : 'Awaiting carrier assignment'}
                  </Text>
                </Space>
              </div>
            </Col>

            {/* Delivery Status Card */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ 
                background: shipmentData?.deliveryStatus === 'Delivered' ? '#f6ffed' : '#fff2f0',
                borderRadius: 12, 
                padding: 20,
                height: '100%',
                border: `1px solid ${shipmentData?.deliveryStatus === 'Delivered' ? '#b7eb8f' : '#ffccc7'}`
              }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space align="center">
                    <div style={{ 
                      background: shipmentData?.deliveryStatus === 'Delivered' ? '#b7eb8f' : '#ffccc7',
                      width: 40, 
                      height: 40, 
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CarOutlined style={{ color: shipmentData?.deliveryStatus === 'Delivered' ? '#52c41a' : '#f5222d', fontSize: 20 }} />
                    </div>
                    <Text strong style={{ fontSize: 16 }}>Delivery Status</Text>
                  </Space>
                  <Tag 
                    color={deliveryStatus.color}
                    icon={deliveryStatus.icon}
                    style={{ 
                      padding: '8px 16px', 
                      borderRadius: 30,
                      fontSize: 14,
                      fontWeight: 500,
                      margin: '8px 0'
                    }}
                  >
                    {shipmentData?.deliveryStatus || "Not Delivered"}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {shipmentData?.deliveryStatus === 'Delivered' ? 'Successfully delivered' : 'Pending delivery'}
                  </Text>
                </Space>
              </div>
            </Col>

            {/* Part Status Card */}
            <Col xs={24} sm={12} md={8}>
              <div style={{
                background: partCard.bg,
                borderRadius: 12,
                padding: 20,
                height: '100%',
                border: `1px solid ${partCard.border}`
              }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space align="center">
                    <div style={{
                      background: partCard.iconBg,
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircleOutlined style={{
                        color: partCard.icon,
                        fontSize: 20
                      }} />
                    </div>
                    <Text strong style={{ fontSize: 16 }}>Part Status</Text>
                  </Space>
                  <Tag 
                    color={partStatus.color}
                    icon={partStatus.icon}
                    style={{ 
                      padding: '8px 16px', 
                      borderRadius: 30,
                      fontSize: 14,
                      fontWeight: 500,
                      margin: '8px 0'
                    }}
                  >
                    {shownPartStatus || "Not Set"}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {partStatusUnset ? 'Nobody has checked the part yet' :
                     shipmentData?.partStatus === 'Available' ? 'Part is ready' :
                     shipmentData?.partStatus === 'Not Good' ? 'Part condition issues' :
                     shipmentData?.partStatus === 'Wrong Part' ? 'Incorrect part received' :
                     shipmentData?.partStatus === 'Customer Canceled Order' ? 'Order canceled' :
                     'Part not available'}
                  </Text>
                </Space>
              </div>
            </Col>

            {/* Pictures Section - Full Width */}
            <Col span={24}>
              <Divider orientation="left" style={{ margin: '16px 0' }}>
                <Space>
                  <CameraOutlined />
                  <Text strong>Pictures Documentation</Text>
                </Space>
              </Divider>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    background: shipmentData?.picturesApproval === 'Yes' ? '#f6ffed' : '#fff7e6',
                    borderRadius: 10,
                    padding: '16px 20px',
                    border: `1px solid ${shipmentData?.picturesApproval === 'Yes' ? '#b7eb8f' : '#ffd591'}`
                  }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text strong>Approval Status</Text>
                        <Tag 
                          color={shipmentData?.picturesApproval === 'Yes' ? 'success' : 'warning'}
                          icon={shipmentData?.picturesApproval === 'Yes' ? 
                            <CheckCircleOutlined /> : 
                            <ClockCircleOutlined />
                          }
                          style={{ padding: '4px 12px', borderRadius: 20 }}
                        >
                          {shipmentData?.picturesApproval || 'No'}
                        </Tag>
                      </Space>
                      <Text type="secondary">
                        {shipmentData?.picturesApproval === 'Yes' ? 'Pictures approved' : 'Awaiting approval'}
                      </Text>
                    </Space>
                  </div>
                </Col>
                
                <Col xs={24} sm={12}>
                  <div style={{ 
                    background: shipmentData?.picturesStatus === 'Received' ? '#f6ffed' : '#f6f6f6',
                    borderRadius: 10,
                    padding: '16px 20px',
                    border: `1px solid ${shipmentData?.picturesStatus === 'Received' ? '#b7eb8f' : '#d9d9d9'}`
                  }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text strong>Pictures Status</Text>
                        <Tag 
                          color={shipmentData?.picturesStatus === 'Received' ? 'success' : 'default'}
                          icon={shipmentData?.picturesStatus === 'Received' ? 
                            <CheckCircleOutlined /> : 
                            <SyncOutlined spin />
                          }
                          style={{ padding: '4px 12px', borderRadius: 20 }}
                        >
                          {shipmentData?.picturesStatus || 'Not Received'}
                        </Tag>
                      </Space>
                      <Text type="secondary">
                        {shipmentData?.picturesStatus === 'Received' ? 'Pictures uploaded' : 'No pictures yet'}
                      </Text>
                    </Space>
                  </div>
                </Col>
              </Row>
            </Col>

          </Row>
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: 18 }}>Edit Shipment Details</Text>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} icon={<CloseOutlined />} disabled={updating}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSave} 
            icon={<SaveOutlined />}
            loading={updating}
          >
            Save Changes
          </Button>
        ]}
        width={800}
        style={{ top: 30 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            trackingStatus: shipmentData?.trackingStatus,
            shippingCompanyName: shipmentData?.shippingCompanyName,
            deliveryStatus: shipmentData?.deliveryStatus,
            partStatus: shownPartStatus,
            picturesApproval: shipmentData?.picturesApproval,
            picturesStatus: shipmentData?.picturesStatus,
            priority: shipmentData?.priority,
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="trackingStatus"
                label="Tracking Number"
                rules={[{ required: true, message: 'Please enter tracking number' }]}
              >
                <Input 
                  prefix={<ClockCircleOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="Enter tracking number"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="shippingCompanyName"
                label="Shipping Company Name"
              >
                <Input 
                  prefix={<TruckOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="Enter shipping company name (e.g., UPS, FedEx, DHL, USPS)"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="deliveryStatus"
                label="Delivery Status"
                rules={[{ required: true, message: 'Please select delivery status' }]}
              >
                <Select 
                  placeholder="Select delivery status"
                  style={{ borderRadius: 8 }}
                >
                  <Option value="Delivered">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Delivered</span>
                    </Space>
                  </Option>
                  <Option value="Not Delivered">
                    <Space>
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />
                      <span>Not Delivered</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="partStatus"
                label="Part Status"
                rules={[{ required: true, message: 'Please select part status' }]}
              >
                <Select
                  placeholder="Select part status"
                  style={{ borderRadius: 8 }}
                  onChange={(value) => {
                    const priority = getPriorityFromPartStatus(value);
                    form.setFieldsValue({ priority });
                    
                    // Show warning when selecting Customer Canceled Order
                    if (value === "Customer Canceled Order") {
                      message.warning("This will update Order Process Complete to 'Customer Canceled'");
                    }
                  }}
                >
                  {getPartStatusOptions().map(option => (
                    <Option key={option.value} value={option.value}>
                      <Space>
                        {option.icon}
                        <span style={{ color: option.color }}>{option.value}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select 
                  placeholder="Select priority"
                  style={{ borderRadius: 8 }}
                >
                  <Option value="High">
                    <Space>
                      <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
                      <span>High</span>
                    </Space>
                  </Option>
                  <Option value="Medium">
                    <Space>
                      <FlagOutlined style={{ color: '#fa8c16' }} />
                      <span>Medium</span>
                    </Space>
                  </Option>
                  <Option value="Low">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Low</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="picturesApproval"
                label="Pictures Approval"
                rules={[{ required: true, message: 'Please select pictures approval' }]}
              >
                <Select 
                  placeholder="Select approval status"
                  style={{ borderRadius: 8 }}
                >
                  <Option value="Yes">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Yes</span>
                    </Space>
                  </Option>
                  <Option value="No">
                    <Space>
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />
                      <span>No</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="picturesStatus"
                label="Pictures Status"
                rules={[{ required: true, message: 'Please select pictures status' }]}
              >
                <Select 
                  placeholder="Select pictures status"
                  style={{ borderRadius: 8 }}
                >
                  <Option value="Received">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Received</span>
                    </Space>
                  </Option>
                  <Option value="Not Received">
                    <Space>
                      <SyncOutlined spin style={{ color: '#fa8c16' }} />
                      <span>Not Received</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// The stored default for a new lead is "Customer Canceled". Until someone has
// chosen, the decision stays empty (shown as Pending), so saving this tab can
// only cancel the order when the agent picks "Customer Canceled".
const getOrderProcessFormData = (lead) => ({
  deliveryStatus: lead?.orderProcessComplete?.deliveryStatus || "Not Delivered",
  partStatus: isOrderDecisionUnset(lead)
    ? undefined
    : lead?.orderProcessComplete?.partStatus || "Customer Accepted"
});

const OrderProcessCompleteTab = ({ lead, onUpdateSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => getOrderProcessFormData(lead));
  // View mode always reflects the latest saved lead; edits start from it
  const savedData = getOrderProcessFormData(lead);

  const startEdit = () => {
    setFormData(getOrderProcessFormData(lead));
    setIsEditing(true);
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

const saveEdit = async () => {
    // Saving without a decision would store the default "Customer Canceled"
    if (!formData.partStatus) {
      message.warning("Choose Customer Accepted or Customer Canceled before saving.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        orderProcessComplete: {
          deliveryStatus: formData.deliveryStatus,
          partStatus: formData.partStatus
        }
      };
      
      // Sync ShipmentTracking based on conditions
      const shipmentTrackingUpdate = {};
      
      // Condition 1: Sync deliveryStatus
      // If deliveryStatus is Delivered, update ShipmentTracking deliveryStatus to Delivered
      // If deliveryStatus is Not Delivered, update ShipmentTracking deliveryStatus to Not Delivered
      shipmentTrackingUpdate.deliveryStatus = formData.deliveryStatus;
      
      // Condition 2: Sync partStatus
      // If partStatus is Customer Canceled, update ShipmentTracking partStatus to Customer Canceled Order
      if (formData.partStatus === "Customer Canceled") {
        shipmentTrackingUpdate.partStatus = "Customer Canceled Order";
        shipmentTrackingUpdate.priority = "Low";
      } else if (formData.partStatus === "Customer Accepted") {
        // If partStatus is Customer Accepted, don't override existing partStatus in ShipmentTracking
        // Only update if we want to reset it (optional)
        // Keep existing partStatus or set to default
        if (!lead?.ShipmentTracking?.partStatus || 
            lead?.ShipmentTracking?.partStatus === "Customer Canceled Order") {
          shipmentTrackingUpdate.partStatus = "Available"; // or keep as is
        }
      }
      
      // Add ShipmentTracking update if we have updates
      if (Object.keys(shipmentTrackingUpdate).length > 0) {
        payload.ShipmentTracking = {
          ...lead?.ShipmentTracking,
          ...shipmentTrackingUpdate,
          lastUpdated: new Date().toISOString()
        };
      }
      
      const res = await API.UpdateLead(lead._id, payload);

      if (res.status) {
        message.success("Updated Successfully");
        setIsEditing(false);
        
        // Call refresh callback to reload lead data
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } else {
        message.error(res?.message || "Update Failed");
      }
    } catch (error) {
      console.error('Error updating order process:', error);
      message.error("Update Failed");
    } finally {
      setLoading(false);
    }
  }
  // Helper function to get priority based on part status
  const getPriorityFromPartStatus = (status) => {
    if (status === "Customer Canceled Order") return "Low";
    if (status === "Available") return "Medium";
    return "Medium";
  };

  return (
    <Card
      title="Order Process Complete"
      extra={
        !isEditing && (
          <Button
            icon={<EditOutlined />}
            type="primary"
            onClick={startEdit}
            disabled={loading}
          >
            Edit
          </Button>
        )
      }
      style={{ borderRadius: 10 }}
    >
      <Row gutter={20}>
        {/* Delivery Status */}
        <Col span={12}>
          <h4>Delivery Status</h4>
          {isEditing ? (
            <Select
              value={formData.deliveryStatus}
              style={{ width: "100%" }}
              onChange={(v) => handleChange("deliveryStatus", v)}
              disabled={loading}
            >
              <Option value="Delivered">Delivered</Option>
              <Option value="Not Delivered">Not Delivered</Option>
            </Select>
          ) : (
            <Tag color={savedData.deliveryStatus === "Delivered" ? "green" : "orange"}>
              {savedData.deliveryStatus}
            </Tag>
          )}
        </Col>

        {/* Part Status */}
        <Col span={12}>
          <h4>Part Status</h4>
          {isEditing ? (
            <Select
              value={formData.partStatus}
              placeholder="Choose Accepted or Canceled"
              style={{ width: "100%" }}
              onChange={(v) => handleChange("partStatus", v)}
              disabled={loading}
            >
              <Option value="Customer Accepted">Customer Accepted</Option>
              <Option value="Customer Canceled">Customer Canceled</Option>
            </Select>
          ) : savedData.partStatus ? (
            <Tag color={savedData.partStatus === "Customer Accepted" ? "green" : "red"}>
              {savedData.partStatus}
            </Tag>
          ) : (
            <Tooltip title="Not chosen yet">
              <Tag icon={<ClockCircleOutlined />}>Pending</Tag>
            </Tooltip>
          )}
        </Col>
      </Row>

      {isEditing && (
        <Space style={{ marginTop: 25 }}>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={saveEdit}
            loading={loading}
          >
            Save
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => setIsEditing(false)}
            disabled={loading}
          >
            Cancel
          </Button>
        </Space>
      )}
    </Card>
  );
};
// Full redesigned YardRefundTab - drop-in replacement
const YardRefundTab = ({ lead ,isAdmin }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    yardCharged: lead?.yardRefund?.yardCharged || "No",
    yardRefundReceived: lead?.yardRefund?.yardRefundReceived || "No",
    yardRestockingFee: lead?.yardRefund?.yardRestockingFee || 0,
    comments: lead?.yardRefund?.comments || ""
  });

  const isSalesMade = lead?.leadStage === "Sales Made";
  const canEdit = !isSalesMade || isAdmin; // Admin can edit even after Sales Made


  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const saveData = async () => {
    try {
      setSaving(true);
      const res = await API.UpdateLead(lead._id, { yardRefund: formData });
      if (res.status) {
        message.success("Yard Refund Updated");
        setEditing(false);
        window.location.reload();
      }
    } catch {
      message.error("Update Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      yardCharged: lead?.yardRefund?.yardCharged || "No",
      yardRefundReceived: lead?.yardRefund?.yardRefundReceived || "No",
      yardRestockingFee: lead?.yardRefund?.yardRestockingFee || 0,
      comments: lead?.yardRefund?.comments || ""
    });
    setEditing(false);
  };

  const StatusBadge = ({ value }) => {
    const isYes = value === "Yes";
    return (
      <Tag
        icon={isYes ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        color={isYes ? "success" : "default"}
        style={{ padding: "4px 14px", borderRadius: 20, fontSize: 14, fontWeight: 500 }}
      >
        {value}
      </Tag>
    );
  };

  const FieldCard = ({ icon, label, children, viewContent }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #f0f0f0",
      borderRadius: 12,
      padding: "20px 24px",
      height: "100%",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
    }}>
      <Space align="center" style={{ marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: "#f0f5ff",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          {React.cloneElement(icon, { style: { color: "#2563eb", fontSize: 17 } })}
        </div>
        <Text style={{ fontWeight: 600, fontSize: 13, color: "#6b7280", letterSpacing: "0.03em", textTransform: "uppercase" }}>
          {label}
        </Text>
      </Space>
      {editing ? children : viewContent}
    </div>
  );

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", overflow: "hidden" }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
        padding: "24px 32px",
        display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <Space size={14} align="center">
          <div style={{
            width: 46, height: 46, borderRadius: 12,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <DollarOutlined style={{ color: "white", fontSize: 22 }} />
          </div>
          <div>
            <Title level={4} style={{ color: "white", margin: 0 }}>Yard Refund</Title>
            <Text style={{ color: "rgba(255,255,255,0.72)", fontSize: 13 }}>
              Manage yard charges and refund details
            </Text>
          </div>
        </Space>

        {!editing  && (
          <Button
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
            style={{
              background: "rgba(255,255,255,0.15)",
              borderColor: "rgba(255,255,255,0.35)",
              color: "white",
              borderRadius: 30,
              height: 38,
              padding: "0 20px",
              fontWeight: 500
            }}
          >
            Edit Details
          </Button>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px", background: "#f9fafb" }}>
        <Row gutter={[16, 16]}>

          {/* Yard Charged */}
          <Col xs={24} sm={12} md={8}>
            <FieldCard
              icon={<BankOutlined />}
              label="Yard Charged"
              viewContent={<StatusBadge value={formData.yardCharged} />}
            >
              <Select
                value={formData.yardCharged}
                style={{ width: "100%", borderRadius: 8 }}
                onChange={(v) => handleChange("yardCharged", v)}
              >
                <Option value="Yes"><Space><CheckCircleOutlined style={{ color: "#52c41a" }} />Yes</Space></Option>
                <Option value="No"><Space><CloseCircleOutlined style={{ color: "#f5222d" }} />No</Space></Option>
              </Select>
            </FieldCard>
          </Col>

          {/* Refund Received */}
          <Col xs={24} sm={12} md={8}>
            <FieldCard
              icon={<CheckCircleOutlined />}
              label="Refund Received"
              viewContent={<StatusBadge value={formData.yardRefundReceived} />}
            >
              <Select
                value={formData.yardRefundReceived}
                style={{ width: "100%", borderRadius: 8 }}
                onChange={(v) => handleChange("yardRefundReceived", v)}
              >
                <Option value="Yes"><Space><CheckCircleOutlined style={{ color: "#52c41a" }} />Yes</Space></Option>
                <Option value="No"><Space><CloseCircleOutlined style={{ color: "#f5222d" }} />No</Space></Option>
              </Select>
            </FieldCard>
          </Col>

          {/* Restocking Fee */}
    <Col xs={24} sm={12} md={8}>
  <FieldCard
    icon={<PercentageOutlined />}
    label="Restocking Fee"
    viewContent={
      <Text
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: formData.yardRestockingFee > 0 ? "#1d4ed8" : "#9ca3af"
        }}
      >
        ${(formData.yardRestockingFee || 0)}
      </Text>
    }
  >
    <InputNumber
      style={{ width: "100%", borderRadius: 8 }}
      prefix="$"
      min={0}
      precision={2}
      value={formData.yardRestockingFee}
      onChange={(v) => handleChange("yardRestockingFee", v)}
    />
  </FieldCard>
</Col>     

          {/* Comments */}
          <Col span={24}>
            <div style={{
              background: "#fff",
              border: "1px solid #f0f0f0",
              borderRadius: 12,
              padding: "20px 24px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
            }}>
              <Space align="center" style={{ marginBottom: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: "#f0f5ff",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <MessageOutlined style={{ color: "#2563eb", fontSize: 17 }} />
                </div>
                <Text style={{ fontWeight: 600, fontSize: 13, color: "#6b7280", letterSpacing: "0.03em", textTransform: "uppercase" }}>
                  Comments
                </Text>
              </Space>
              {editing ? (
                <Input.TextArea
                  rows={4}
                  value={formData.comments}
                  placeholder="Add notes or comments..."
                  style={{ borderRadius: 8 }}
                  onChange={(e) => handleChange("comments", e.target.value)}
                />
              ) : (
                <Text style={{ color: formData.comments ? "#111827" : "#9ca3af", fontSize: 15, display: "block", lineHeight: 1.7 }}>
                  {formData.comments || "No comments added."}
                </Text>
              )}
            </div>
          </Col>
        </Row>

        {/* Action Buttons */}
        {editing && (
          <div style={{
            marginTop: 20, paddingTop: 20,
            borderTop: "1px solid #e5e7eb",
            display: "flex", justifyContent: "flex-end", gap: 12
          }}>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
              disabled={saving}
              style={{ borderRadius: 8, height: 40, padding: "0 20px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={saveData}
              loading={saving}
              style={{
                borderRadius: 8, height: 40, padding: "0 24px",
                background: "#1d4ed8", borderColor: "#1d4ed8", fontWeight: 600
              }}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
const CustomerRefundTab = ({ lead }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    customerChargedAmount: lead?.customerRefund?.customerChargedAmount || lead?.totalAmount || 0,
    refundAmount: lead?.customerRefund?.refundAmount || 0,
    restockingFee: lead?.customerRefund?.restockingFee || 0,
    customerRefundStatus: lead?.customerRefund?.customerRefundStatus || "No",
    updatedDate: lead?.customerRefund?.updatedDate ? dayjs(lead.customerRefund.updatedDate) : dayjs()
  });

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const saveData = async () => {
    try {
      setSaving(true);
      const res = await API.UpdateLead(lead._id, {
        customerRefund: { ...formData, customerChargedAmount: lead.totalAmount, updatedDate: formData.updatedDate }
      });
      if (res.status) {
        message.success("Customer Refund Updated");
        setIsEditing(false);
      }
    } catch {
      message.error("Update Failed");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      customerChargedAmount: lead?.customerRefund?.customerChargedAmount || lead?.totalAmount || 0,
      refundAmount: lead?.customerRefund?.refundAmount || 0,
      restockingFee: lead?.customerRefund?.restockingFee || 0,
      customerRefundStatus: lead?.customerRefund?.customerRefundStatus || "No",
      updatedDate: lead?.customerRefund?.updatedDate ? dayjs(lead.customerRefund.updatedDate) : dayjs()
    });
    setIsEditing(false);
  };

  const netRefund = (Number(formData.refundAmount) - Number(formData.restockingFee)).toFixed(2);
  const isRefunded = formData.customerRefundStatus === "Yes";

  const FieldCard = ({ icon, label, color = "#2563eb", bgColor = "#f0f5ff", children, viewContent }) => (
    <div style={{
      background: "#fff", border: "1px solid #f0f0f0",
      borderRadius: 12, padding: "20px 24px", height: "100%",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
    }}>
      <Space align="center" style={{ marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, background: bgColor,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          {React.cloneElement(icon, { style: { color, fontSize: 17 } })}
        </div>
        <Text style={{ fontWeight: 600, fontSize: 13, color: "#6b7280", letterSpacing: "0.03em", textTransform: "uppercase" }}>
          {label}
        </Text>
      </Space>
      {isEditing ? children : viewContent}
    </div>
  );

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", overflow: "hidden" }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
        padding: "24px 32px",
        display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <Space size={14} align="center">
          <div style={{
            width: 46, height: 46, borderRadius: 12,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <DollarOutlined style={{ color: "white", fontSize: 22 }} />
          </div>
          <div>
            <Title level={4} style={{ color: "white", margin: 0 }}>Customer Refund</Title>
            <Text style={{ color: "rgba(255,255,255,0.72)", fontSize: 13 }}>
              Refund tracking & financial breakdown
            </Text>
          </div>
        </Space>

        <Space size={12} align="center">
         
          {!isEditing && (
            <Button
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
              style={{
                background: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.35)",
                color: "white", borderRadius: 30, height: 38, padding: "0 20px", fontWeight: 500
              }}
            >
              Edit Details
            </Button>
          )}
        </Space>
      </div>


      {/* Body */}
      <div style={{ padding: "28px 32px", background: "#f9fafb" }}>
        <Row gutter={[16, 16]}>

          {/* Customer Charged Amount — read only */}
          <Col xs={24} sm={12} md={6}>
            <FieldCard
              icon={<BankOutlined />}
              label="Charged Amount"
              color="#0f766e"
              bgColor="#f0fdfa"
              viewContent={
                <Text style={{ fontSize: 22, fontWeight: 700, color: "#0f766e" }}>
                  ${Number(lead?.totalAmount || 0).toFixed(2)}
                </Text>
              }
            >
              <Text style={{ fontSize: 22, fontWeight: 700, color: "#0f766e" }}>
                ${Number(lead?.totalAmount || 0).toFixed(2)}
              </Text>
              <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                Auto-calculated from order
              </Text>
            </FieldCard>
          </Col>

          {/* Refund Amount */}
          <Col xs={24} sm={12} md={6}>
            <FieldCard
              icon={<ArrowDownOutlined/>}
              label="Refund Amount"
              color="#2563eb"
              bgColor="#eff6ff"
              viewContent={
                <Text style={{ fontSize: 22, fontWeight: 700, color: "#2563eb" }}>
                  ${Number(formData.refundAmount || 0).toFixed(2)}
                </Text>
              }
            >
              <InputNumber
                style={{ width: "100%", borderRadius: 8 }}
                prefix="$"
                min={0}
                precision={2}
                value={formData.refundAmount}
                onChange={(v) => handleChange("refundAmount", v)}
              />
            </FieldCard>
          </Col>

          {/* Restocking Fee */}
          <Col xs={24} sm={12} md={6}>
            <FieldCard
              icon={<MinusCircleOutlined />}
              label="Restocking Fee"
              color="#dc2626"
              bgColor="#fff1f2"
              viewContent={
                <Text style={{ fontSize: 22, fontWeight: 700, color: "#dc2626" }}>
                  ${Number(formData.restockingFee || 0).toFixed(2)}
                </Text>
              }
            >
              <InputNumber
                style={{ width: "100%", borderRadius: 8 }}
                prefix="$"
                min={0}
                precision={2}
                value={formData.restockingFee}
                onChange={(v) => handleChange("restockingFee", v)}
              />
            </FieldCard>
          </Col>

          {/* Refund Status */}
          <Col xs={24} sm={12} md={6}>
            <FieldCard
              icon={<CheckCircleOutlined />}
              label="Refund Status"
              color={isRefunded ? "#059669" : "#d97706"}
              bgColor={isRefunded ? "#f0fdf4" : "#fffbeb"}
              viewContent={
                <Tag
                  icon={isRefunded ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                  color={isRefunded ? "success" : "warning"}
                  style={{ padding: "5px 14px", borderRadius: 20, fontSize: 14, fontWeight: 500 }}
                >
                  {formData.customerRefundStatus}
                </Tag>
              }
            >
              <Select
                style={{ width: "100%", borderRadius: 8 }}
                value={formData.customerRefundStatus}
                onChange={(v) => handleChange("customerRefundStatus", v)}
              >
                <Option value="Yes">
                  <Space><CheckCircleOutlined style={{ color: "#52c41a" }} />Yes — Refunded</Space>
                </Option>
                <Option value="No">
                  <Space><ClockCircleOutlined style={{ color: "#faad14" }} />No — Pending</Space>
                </Option>
              </Select>
            </FieldCard>
          </Col>

        </Row>

        {/* Action Buttons */}
        {isEditing && (
          <div style={{
            marginTop: 20, paddingTop: 20,
            borderTop: "1px solid #e5e7eb",
            display: "flex", justifyContent: "flex-end", gap: 12
          }}>
            <Button
              icon={<CloseOutlined />}
              onClick={cancelEdit}
              disabled={saving}
              style={{ borderRadius: 8, height: 40, padding: "0 20px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveData}
              loading={saving}
              style={{
                borderRadius: 8, height: 40, padding: "0 24px",
                background: "#0f766e", borderColor: "#0f766e", fontWeight: 600
              }}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
const SaleMadeTab = ({ lead, onUpdateSuccess }) => {
  const [isEditingTrackingPromiseDays, setIsEditingTrackingPromiseDays] = useState(false);
  const [savingTrackingPromiseDays, setSavingTrackingPromiseDays] = useState(false);
  const [trackingPromiseDaysInput, setTrackingPromiseDaysInput] = useState(
    normalizeTrackingPromiseDaysInput(lead?.trackingPromiseDays)
  );
  
  // State for Lead Type editing
  const [isEditingLeadType, setIsEditingLeadType] = useState(false);
  const [savingLeadType, setSavingLeadType] = useState(false);
  const [leadTypeInput, setLeadTypeInput] = useState(getTypeOfLeadFormValue(lead?.typeOfLead));

  // Helper function to get lead type color
  const getLeadTypeColor = (type) => {
    switch(type) {
      case 'Inbound Call': return 'blue';
      case 'Live': return 'green';
      case 'Follow-Up': return 'gold';
      default: return 'default';
    }
  };

  // Helper function to get lead type icon
  const getLeadTypeIcon = (type) => {
    switch(type) {
      case 'Inbound Call': return <PhoneOutlined />;
      case 'Live': return <CheckCircleOutlined />;
      case 'Follow-Up': return <CalendarOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  const totalAmount = Number(lead?.totalAmount || 0);
  const isSalesMadeStatus = lead?.leadStage === "Sales Made";

  // Get the latest payment method from advancePayments
  const getPaymentMethod = () => {
    if (!lead?.advancePayments || lead.advancePayments.length === 0) {
      return null;
    }
    const lastPayment = lead.advancePayments[lead.advancePayments.length - 1];
    return lastPayment?.paymentMethod;
  };

  const paymentMethod = getPaymentMethod();
  const currentLeadYard = getLatestYardSelection(lead?.yard || []).currentYard;

  // Calculate CC fee based on payment method
  const calculateCCFee = (amount) => {
    const method = paymentMethod;
    
    const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
    if (threePointFivePercent.includes(method)) {
      return parseFloat((amount * 0.035).toFixed(2));
    }
    
    if (method === 'Pay Tomorrow') {
      return parseFloat((amount * 0.06).toFixed(2));
    }
    
    if (method === 'Wire Transfer') {
      return 35.00;
    }
    
    return 0;
  };

  // Get latest yard price from yard array
  const getLatestYardPrice = () => {
    return currentLeadYard?.finalPrice || 0;
  };

  const latestYardPrice = getLatestYardPrice();

  const compute = (finalPrice) => {
    const customerAmountReceived = totalAmount;
    const customerCCFee = calculateCCFee(customerAmountReceived);
    const totalGP = parseFloat((customerAmountReceived - customerCCFee - (finalPrice || 0)).toFixed(2));
    return { customerAmountReceived, customerCCFee, totalGP };
  };

  const [formData, setFormData] = useState(() => ({
    finalPrice: latestYardPrice,
    ...compute(latestYardPrice)
  }));

  useEffect(() => {
    if (!isSalesMadeStatus) {
      return;
    }

    setFormData({
      finalPrice: latestYardPrice,
      ...compute(latestYardPrice)
    });
  }, [isSalesMadeStatus, latestYardPrice, totalAmount, paymentMethod]);

  useEffect(() => {
    setTrackingPromiseDaysInput(
      normalizeTrackingPromiseDaysInput(lead?.trackingPromiseDays)
    );
    setIsEditingTrackingPromiseDays(false);
  }, [lead?._id, lead?.trackingPromiseDays]);

  useEffect(() => {
    setLeadTypeInput(getTypeOfLeadFormValue(lead?.typeOfLead));
    setIsEditingLeadType(false);
  }, [lead?._id, lead?.typeOfLead]);

  // Auto-save to database only for actual Sales Made status
  useEffect(() => {
    const autoSaveYardPrice = async () => {
      const currentSavedPrice = lead?.saleMadeCharge?.finalPrice || 0;
      
      if (latestYardPrice !== currentSavedPrice) {
        try {
          const newFormData = {
            finalPrice: latestYardPrice,
            ...compute(latestYardPrice)
          };
          
          setFormData(newFormData);
          
          const res = await API.UpdateLead(lead._id, { saleMadeCharge: newFormData });
          
          if (res.status) {
            console.log("Yard price auto-saved to database:", latestYardPrice);
            if (onUpdateSuccess) onUpdateSuccess();
          }
        } catch (error) {
          console.error("Failed to auto-save yard price:", error);
        }
      }
    };

    if (isSalesMadeStatus && lead?._id && latestYardPrice > 0) {
      autoSaveYardPrice();
    }
  }, [isSalesMadeStatus, latestYardPrice, lead?._id, onUpdateSuccess, paymentMethod, totalAmount]);

  // Get fee description
  const getFeeDescription = () => {
    const method = paymentMethod;
    const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
    
    if (threePointFivePercent.includes(method)) {
      return `3.5% of $${formData.customerAmountReceived.toFixed(2)} (${method})`;
    }
    if (method === 'Pay Tomorrow') {
      return `6% of $${formData.customerAmountReceived.toFixed(2)} (${method})`;
    }
    if (method === 'Wire Transfer') {
      return `$$35 flat fee (${method})`;
    }
    if (method) {
      return `0% fee (${method})`;
    }
    return "No payment method selected";
  };

  const gpPositive = formData.totalGP >= 0;
  const gpColor = gpPositive ? "#059669" : "#dc2626";
  const gpBg = gpPositive ? "#f0fdf4" : "#fff1f2";
  const gpBorder = gpPositive ? "#bbf7d0" : "#fecdd3";
  const margin = formData.customerAmountReceived > 0
    ? ((formData.totalGP / formData.customerAmountReceived) * 100).toFixed(1)
    : "0.0";

  const handleStartTrackingPromiseDaysEdit = () => {
    setTrackingPromiseDaysInput(
      normalizeTrackingPromiseDaysInput(lead?.trackingPromiseDays)
    );
    setIsEditingTrackingPromiseDays(true);
  };

  const handleCancelTrackingPromiseDaysEdit = () => {
    setTrackingPromiseDaysInput(
      normalizeTrackingPromiseDaysInput(lead?.trackingPromiseDays)
    );
    setIsEditingTrackingPromiseDays(false);
  };

  const handleSaveTrackingPromiseDays = async () => {
    const nextValue = normalizeTrackingPromiseDaysInput(trackingPromiseDaysInput);

    if (!isValidTrackingPromiseDays(nextValue)) {
      message.error(TRACKING_PROMISE_DAYS_ERROR);
      return;
    }

    setSavingTrackingPromiseDays(true);

    try {
      const response = await API.UpdateLead(lead._id, { trackingPromiseDays: nextValue });

      if (response?.status) {
        message.success("Tracking promise days saved successfully");
        setIsEditingTrackingPromiseDays(false);
        if (onUpdateSuccess) {
          await onUpdateSuccess({ showSpinner: false });
        }
      } else {
        message.error(getTrackingPromiseDaysErrorMessage(response?.message));
      }
    } catch (error) {
      console.error("Failed to save tracking promise days:", error);
      message.error(getTrackingPromiseDaysErrorMessage(error?.response?.data?.message));
    } finally {
      setSavingTrackingPromiseDays(false);
    }
  };

  // Lead Type handlers
  const handleStartLeadTypeEdit = () => {
    setLeadTypeInput(getTypeOfLeadFormValue(lead?.typeOfLead));
    setIsEditingLeadType(true);
  };

  const handleCancelLeadTypeEdit = () => {
    setLeadTypeInput(getTypeOfLeadFormValue(lead?.typeOfLead));
    setIsEditingLeadType(false);
  };

  const handleSaveLeadType = async () => {
    if (!TYPE_OF_LEAD_OPTIONS.includes(leadTypeInput)) {
      message.error('Please select Type Of Lead');
      return;
    }

    setSavingLeadType(true);

    try {
      const response = await API.UpdateLead(lead._id, { typeOfLead: leadTypeInput });

      if (response?.status) {
        message.success("Lead type saved successfully");
        setIsEditingLeadType(false);
        if (onUpdateSuccess) {
          await onUpdateSuccess({ showSpinner: false });
        }
      } else {
        message.error(response?.message || 'Failed to save lead type');
      }
    } catch (error) {
      console.error("Failed to save lead type:", error);
      message.error(error?.response?.data?.message || 'Failed to save lead type');
    } finally {
      setSavingLeadType(false);
    }
  };

  // Custom card component with stacked vertical text design
  const StatCard = ({ 
    letters,           
    value, 
    sub, 
    color, 
    bgColor, 
    badge, 
    badgeColor,
    valueColor,
    children 
  }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #f0f0f0",
      borderRadius: 16,
      padding: "20px 16px",
      height: "100%",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      display: "flex",
      flexDirection: "column",
      position: "relative"
    }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        lineHeight: 1.2,
        marginBottom: 16
      }}>
        {letters.map((letter, index) => (
          <Text key={index} style={{
            fontSize: 20,
            fontWeight: 700,
            color: color || "#1f2937",
            letterSpacing: "0.02em"
          }}>
            {letter}
          </Text>
        ))}
      </div>

      <Text style={{
        fontSize: 28,
        fontWeight: 800,
        color: valueColor || color || "#1f2937",
        lineHeight: 1.2,
        marginBottom: 4
      }}>
        {value}
      </Text>

      <Text style={{
        fontSize: 12,
        color: "#6b7280",
        marginBottom: children ? 12 : 0
      }}>
        {sub}
      </Text>

      {badge && (
        <Tag 
          color={badgeColor || "default"} 
          style={{ 
            position: "absolute", 
            top: 20, 
            right: 16,
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            padding: "2px 8px"
          }}
        >
          {badge}
        </Tag>
      )}

      {children}
    </div>
  );

  // Editable Field Card Component
  const EditableFieldCard = ({ title, value, onEdit, isEditing, editComponent, icon, color }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #f0f0f0",
      borderRadius: 16,
      padding: "16px",
      height: "100%",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      transition: "all 0.3s ease"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: "1px solid #f0f0f0"
      }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          {icon}
        </div>
        <Text strong style={{ fontSize: 13, color: "#6b7280" }}>{title}</Text>
      </div>
      
      {isEditing ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {editComponent}
          <Space size={4}>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={onEdit.save}
              loading={onEdit.saving}
            >
              Save
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={onEdit.cancel}
            >
              Cancel
            </Button>
          </Space>
        </div>
      ) : (
        <div 
          onClick={onEdit.start}
          style={{ cursor: "pointer" }}
        >
          {value}
        </div>
      )}
    </div>
  );

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", overflow: "hidden" }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{
        background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
        padding: "20px 24px",
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center"
      }}>
        <Space size={12} align="center">
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <RiseOutlined style={{ color: "white", fontSize: 20 }} />
          </div>
          <div>
            <Title level={4} style={{ color: "white", margin: 0, fontSize: 18 }}>Sale Made</Title>
            <Text style={{ color: "rgba(255,255,255,0.72)", fontSize: 12 }}>
              Revenue breakdown & gross profit
            </Text>
          </div>
        </Space>
      </div>

      <div style={{ padding: "20px", background: "#f9fafb" }}>
        {!isSalesMadeStatus ? (
          <Alert
            type="info"
            showIcon
            message="Sale Made metrics will appear after lead reaches Sales Made status."
            style={{
              borderRadius: 12,
              background: "#eff6ff",
              border: "1px solid #bfdbfe"
            }}
          />
        ) : (
          <>
            {/* Editable Fields Row - Tracking Promise Days & Lead Type */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12}>
                <EditableFieldCard
                  title="TRACKING PROMISE DAYS"
                  value={
                    isEditingTrackingPromiseDays ? null : (
                      lead?.trackingPromiseDays ? (
                        <Tag
                          color="blue"
                          icon={<ClockCircleOutlined />}
                          style={{
                            borderRadius: 12,
                            padding: "6px 12px",
                            fontSize: 13,
                            fontWeight: 500,
                            width: "100%",
                            textAlign: "center",
                            cursor: "pointer"
                          }}
                        >
                          Days Promised: {lead.trackingPromiseDays}
                          <EditOutlined style={{ marginLeft: 8, fontSize: 12 }} />
                        </Tag>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            borderRadius: 12,
                            background: "#fff7e6",
                            border: "1px solid #ffd591"
                          }}
                        >
                          <Space>
                            <ClockCircleOutlined style={{ color: "#d46b08" }} />
                            <Text style={{ color: "#8c4a00", fontSize: 12 }}>Not set</Text>
                          </Space>
                          <Button type="link" size="small" icon={<EditOutlined />}>
                            Add
                          </Button>
                        </div>
                      )
                    )
                  }
                  onEdit={{
                    start: handleStartTrackingPromiseDaysEdit,
                    save: handleSaveTrackingPromiseDays,
                    cancel: handleCancelTrackingPromiseDaysEdit,
                    saving: savingTrackingPromiseDays
                  }}
                  isEditing={isEditingTrackingPromiseDays}
                  editComponent={
                    <Input
                      size="middle"
                      placeholder={TRACKING_PROMISE_DAYS_PLACEHOLDER}
                      value={trackingPromiseDaysInput}
                      onChange={(event) => setTrackingPromiseDaysInput(event.target.value)}
                      style={{ width: "100%" }}
                    />
                  }
                  icon={<ClockCircleOutlined style={{ color: "white", fontSize: 16 }} />}
                  color="#1890ff"
                />
              </Col>

              <Col xs={24} sm={12}>
                <EditableFieldCard
                  title="TYPE OF LEAD"
                  value={
                    isEditingLeadType ? null : (
                      TYPE_OF_LEAD_OPTIONS.includes(lead?.typeOfLead) ? (
                        <Tag
                          color={getLeadTypeColor(lead.typeOfLead)}
                          icon={getLeadTypeIcon(lead.typeOfLead)}
                          style={{
                            borderRadius: 12,
                            padding: "6px 12px",
                            fontSize: 13,
                            fontWeight: 500,
                            width: "100%",
                            textAlign: "center",
                            cursor: "pointer"
                          }}
                        >
                          {lead.typeOfLead}
                          <EditOutlined style={{ marginLeft: 8, fontSize: 12 }} />
                        </Tag>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            borderRadius: 12,
                            background: "#fef3c7",
                            border: "1px solid #fde68a"
                          }}
                        >
                          <Space>
                            <ExclamationCircleOutlined style={{ color: "#d97706" }} />
                            <Text style={{ color: "#92400e", fontSize: 12 }}>Not classified</Text>
                          </Space>
                          <Button type="link" size="small" icon={<EditOutlined />}>
                            Set
                          </Button>
                        </div>
                      )
                    )
                  }
                  onEdit={{
                    start: handleStartLeadTypeEdit,
                    save: handleSaveLeadType,
                    cancel: handleCancelLeadTypeEdit,
                    saving: savingLeadType
                  }}
                  isEditing={isEditingLeadType}
                  editComponent={
                    <Select
                      value={leadTypeInput}
                      onChange={setLeadTypeInput}
                      style={{ width: "100%" }}
                      size="middle"
                      placeholder="Select Type Of Lead"
                      allowClear
                    >
                      {TYPE_OF_LEAD_OPTIONS.map((option) => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  }
                  icon={<Tag style={{ color: "white", fontSize: 16, background: "transparent", margin: 0 }}>🏷️</Tag>}
                  color="#7c3aed"
                />
              </Col>
            </Row>

            {/* Payment Method Info */}
            {paymentMethod && (
              <div style={{ marginBottom: 16 }}>
                <Alert
                  type="info"
                  showIcon
                  message={`Payment Method: ${paymentMethod}`}
                  description={`Fee calculation: ${getFeeDescription()}`}
                  style={{ borderRadius: 12 }}
                />
              </div>
            )}

            {/* Stats Cards Row */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12}>
                <StatCard
                  letters={["AMOUNT RECEIVED"]}
                  value={`$${formData.customerAmountReceived.toFixed(2)}`}
                  sub="From order total"
                  color="#7c3aed"
                  valueColor="#7c3aed"
                  badge="Auto"
                  badgeColor="purple"
                />
              </Col>

              <Col xs={24} sm={12} md={12}>
                <StatCard
                  letters={["YARD PRICE"]}
                  value={`$${formData.finalPrice?.toFixed(2)}`}
                  sub="Auto-synced from yard"
                  color="#dc2626"
                  valueColor="#dc2626"
                  badge="Auto-Saved"
                  badgeColor="green"
                />
              </Col>

              <Col xs={24} sm={12} md={12}>
                <StatCard
                  letters={["CC FEE"]}
                  value={paymentMethod === 'Wire Transfer' 
                    ? `$${formData.customerCCFee.toFixed(2)}` 
                    : `$${formData.customerCCFee.toFixed(2)}`}
                  sub={getFeeDescription()}
                  color="#d97706"
                  valueColor="#d97706"
                  badge={paymentMethod === 'Wire Transfer' ? "$35" : paymentMethod === 'Pay Tomorrow' ? "6%" : paymentMethod ? "3.5%" : "0%"}
                  badgeColor="orange"
                />
              </Col>

              <Col xs={24} sm={12} md={12}>
                <StatCard
                  letters={["TOTAL GP"]}
                  value={`$${formData.totalGP.toFixed(2)}`}
                  sub={`${margin}% margin`}
                  color={gpColor}
                  valueColor={gpColor}
                  badge="Auto"
                  badgeColor="default"
                >
                  <div style={{
                    marginTop: 8,
                    padding: "4px 8px",
                    background: gpBg,
                    borderRadius: 8,
                    border: `1px solid ${gpBorder}`,
                    fontSize: 11,
                    color: gpColor,
                    fontWeight: 600,
                    textAlign: "center"
                  }}>
                    {gpPositive ? "▲" : "▼"} {margin}% margin
                  </div>
                </StatCard>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Card>
  );
};
const ServiceAgentCard = ({ lead, serviceAgentMap, serviceAgentDetailsMap, onUpdateSuccess }) => {
    const [modalState, setModalState] = useState({
        visible: false,
        type: null, // 'service', 'order', 'zoho', 'yard'
        selectedAgent: null
    });
    const [assigning, setAssigning] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [removingType, setRemovingType] = useState(null);
    const [activeTab, setActiveTab] = useState('zoho'); // Default active tab

    // Filter agents that can be service agents - use the passed serviceAgentMap
    const serviceAgents = Object.keys(serviceAgentDetailsMap)
        .map(agentId => ({
            id: agentId,
            name: serviceAgentMap[agentId],
            ...serviceAgentDetailsMap[agentId]
        }));

    // Get current handlers
    const currentServiceHandler = {
       
        zoho: {
            id: lead?.zohoServiceHandledBy?._id || lead?.zohoServiceHandledBy,
            name: null,
            details: null
        },
        yard: {
            id: lead?.yardServiceHandledBy?._id || lead?.yardServiceHandledBy,
            name: null,
            details: null
        },
        
        order: {
            id: lead?.orderServiceHandledBy?._id || lead?.orderServiceHandledBy,
            name: null,
            details: null
        },
         service: {
            id: lead?.serviceHandledBy?._id || lead?.serviceHandledBy,
            name: null,
            details: null
        },
    };

    // Populate names and details
    Object.keys(currentServiceHandler).forEach(key => {
        const id = currentServiceHandler[key].id;
        if (id) {
            currentServiceHandler[key].name = serviceAgentMap[id];
            currentServiceHandler[key].details = serviceAgentDetailsMap[id];
        }
    });

    const getModalTitle = (type) => {
        const titles = {
             zoho: 'Assign Zoho Service Agent',
              yard: 'Assign Yard Service Agent',
          
            order: 'Assign Order Service Agent',
              service: 'Assign Shipping Service Agent',
           
           
        };
        return titles[type] || 'Assign Agent';
    };

    const getModalIcon = (type) => {
        const icons = {
      
           
            zoho: <CloudOutlined style={{ color: '#eb2f96' }} />,
            yard: <HomeOutlined style={{ color: '#fa8c16' }} />,
             order: <ShoppingOutlined style={{ color: '#1890ff' }} />,
                   service: <TeamOutlined style={{ color: '#52c41a' }} />,
        };
        return icons[type] || <TeamOutlined style={{ color: '#52c41a' }} />;
    };

    const getAgentDescription = (type) => {
        const descriptions = {
         
            zoho: 'Handles Zoho CRM integration and data synchronization',
            yard: 'Handles yard operations and inventory management',

            order: 'Handles order processing and management for this lead',
                           service: 'Handles service and support needs for this lead',
        };
        return descriptions[type] || 'Handles service and support needs';
    };

    const getTabIcon = (type) => {
        const icons = {
            service: <TeamOutlined />,
            order: <ShoppingOutlined />,
            zoho: <CloudOutlined />,
            yard: <HomeOutlined />
        };
        return icons[type];
    };

    const getTabLabel = (type) => {
        const labels = {
            service: 'Shipping Service',
            order: 'Order Service',
            zoho: 'Zoho Service',
            yard: 'Yard Service'
        };
        return labels[type];
    };

    const getAssignedBadge = (type) => {
        const isAssigned = !!currentServiceHandler[type]?.name;
        if (!isAssigned) return null;
        
        const colors = {
            service: 'green',
            order: 'blue',
            zoho: 'pink',
            yard: 'orange'
        };
        
        return (
            <Tag color={colors[type]} style={{ marginLeft: '8px', fontSize: '10px' }}>
                Assigned
            </Tag>
        );
    };

    const handleAssignAgent = async () => {
        if (!modalState.selectedAgent) {
            message.warning('Please select an agent');
            return;
        }

        try {
            setAssigning(true);
            const payload = {};
            
            switch (modalState.type) {
                case 'service':
                    payload.serviceHandledBy = modalState.selectedAgent;
                    break;
                case 'order':
                    payload.orderServiceHandledBy = modalState.selectedAgent;
                    break;
                case 'zoho':
                    payload.zohoServiceHandledBy = modalState.selectedAgent;
                    break;
                case 'yard':
                    payload.yardServiceHandledBy = modalState.selectedAgent;
                    break;
                default:
                    break;
            }

            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status || response?.success) {
                message.success(`${getModalTitle(modalState.type)} assigned successfully!`);
                setModalState({ visible: false, type: null, selectedAgent: null });
                if (onUpdateSuccess) onUpdateSuccess();
            } else {
                message.error(response?.message || 'Failed to assign agent');
            }
        } catch (error) {
            console.error('Error assigning agent:', error);
            message.error('Failed to assign agent');
        } finally {
            setAssigning(false);
        }
    };

    const handleRemoveAgent = async (type) => {
        Modal.confirm({
            title: `Remove ${getModalTitle(type)}`,
            content: `Are you sure you want to remove the ${getModalTitle(type).toLowerCase()} from this lead?`,
            okText: 'Yes, Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setRemoving(true);
                    setRemovingType(type);
                    const payload = {};
                    
                    switch (type) {
                        case 'service':
                            payload.serviceHandledBy = null;
                            break;
                        case 'order':
                            payload.orderServiceHandledBy = null;
                            break;
                        case 'zoho':
                            payload.zohoServiceHandledBy = null;
                            break;
                        case 'yard':
                            payload.yardServiceHandledBy = null;
                            break;
                        default:
                            break;
                    }

                    const response = await API.UpdateLead(lead._id, payload);
                    
                    if (response?.status || response?.success) {
                        message.success(`${getModalTitle(type)} removed successfully!`);
                        if (onUpdateSuccess) onUpdateSuccess();
                    } else {
                        message.error(response?.message || 'Failed to remove agent');
                    }
                } catch (error) {
                    console.error('Error removing agent:', error);
                    message.error('Failed to remove agent');
                } finally {
                    setRemoving(false);
                    setRemovingType(null);
                }
            }
        });
    };

    const openModal = (type) => {
        setModalState({
            visible: true,
            type: type,
            selectedAgent: null
        });
    };

    // Render content for each tab
    const renderTabContent = (type) => {
        const currentAgent = currentServiceHandler[type];
        const isAssigned = !!currentAgent?.name;
        
        const colorSchemes = {
            service: { border: '#b7eb8f', bg: '#f6ffed', icon: '#52c41a', buttonBg: '#52c41a' },
            order: { border: '#91d5ff', bg: '#e6f7ff', icon: '#1890ff', buttonBg: '#1890ff' },
            zoho: { border: '#ffadd6', bg: '#fff0f6', icon: '#eb2f96', buttonBg: '#eb2f96' },
            yard: { border: '#ffd591', bg: '#fff7e6', icon: '#fa8c16', buttonBg: '#fa8c16' }
        };
        
        const colorScheme = colorSchemes[type];
        const icon = getTabIcon(type);
        
        const titles = {
            service: 'Shipping Service Agent',
            order: 'Order Service Agent',
            zoho: 'Zoho Service Agent',
            yard: 'Yard Service Agent'
        };
        
        const title = titles[type];

        return (
            <div style={{ padding: '20px 0' }}>
                {isAssigned ? (
                    <div>
                        {/* Assigned Agent Info */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '16px',
                            marginBottom: '20px',
                            padding: '20px',
                            backgroundColor: colorScheme.bg,
                            borderRadius: '12px'
                        }}>
                            <Avatar 
                                icon={icon} 
                                style={{ backgroundColor: colorScheme.icon, width: '56px', height: '56px' }} 
                                size={56}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>
                                    {currentAgent.name}
                                </div>
                               
                              
                            
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button 
                                type="default" 
                                icon={<SwapOutlined />}
                                onClick={() => openModal(type)}
                                style={{ flex: 1, height: '40px' }}
                                loading={assigning && modalState.type === type}
                            >
                                Change Agent
                            </Button>
                            <Button 
                                type="default" 
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveAgent(type)}
                                style={{ flex: 1, height: '40px' }}
                                loading={removing && removingType === type}
                            >
                                Remove Agent
                            </Button>
                        </div>

                        {/* Note if different from assigned sales agent (only for service type) */}
                        {type === 'service' && lead.handledBy && lead.handledBy !== currentAgent.id && (
                            <div style={{ 
                                marginTop: '16px', 
                                padding: '10px',
                                backgroundColor: '#fff7e6',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#fa8c16'
                            }}>
                                <InfoCircleOutlined style={{ marginRight: '4px' }} />
                                Different from assigned sales agent
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        {React.cloneElement(icon, { style: { fontSize: '64px', color: '#d9d9d9', marginBottom: '20px' } })}
                        <div style={{ marginBottom: '12px', color: '#666', fontSize: '14px' }}>
                            {getAgentDescription(type)}
                        </div>
                        <div style={{ marginBottom: '24px', color: '#999', fontSize: '13px' }}>
                            No agent assigned
                        </div>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => openModal(type)}
                            size="large"
                            style={{ 
                                backgroundColor: colorScheme.buttonBg, 
                                borderColor: colorScheme.buttonBg,
                                padding: '0 32px',
                                height: '44px'
                            }}
                        >
                            Assign {title}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    // Tab items configuration
    const tabItems = [
      
        {
            key: 'zoho',
            label: (
                <span>
                    <CloudOutlined />
                    Zoho Service
                    {getAssignedBadge('zoho')}
                </span>
            ),
            children: renderTabContent('zoho')
        },
        {
            key: 'yard',
            label: (
                <span>
                    <HomeOutlined />
                    Yard Service
                    {getAssignedBadge('yard')}
                </span>
            ),
            children: renderTabContent('yard')
        },
         {
            key: 'order',
            label: (
                <span>
                    <ShoppingOutlined />
                    Order Service
                    {getAssignedBadge('order')}
                </span>
            ),
            children: renderTabContent('order')
        },
          {
            key: 'service',
            label: (
                <span>
                    <TeamOutlined />
                 Shipping Service Agent
                    {getAssignedBadge('service')}
                </span>
            ),
            children: renderTabContent('service')
        },
       
    ];

    return (
        <>
            <Card 
                title="Service Agents Management"
                style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
            >
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    items={tabItems}
                    type="card"
                    style={{ marginTop: '8px' }}
                />
            </Card>

            {/* Assign Agent Modal */}
            <Modal
                title={
                    <Space>
                        {getModalIcon(modalState.type)}
                        <span>{getModalTitle(modalState.type)}</span>
                    </Space>
                }
                open={modalState.visible}
                onCancel={() => {
                    setModalState({ visible: false, type: null, selectedAgent: null });
                }}
                onOk={handleAssignAgent}
                confirmLoading={assigning}
                okText="Assign Agent"
                cancelText="Cancel"
                width={500}
            >
                <div style={{ padding: '16px 0' }}>
                    <Alert
                        message={getModalTitle(modalState.type)}
                        description={modalState.type && getAgentDescription(modalState.type)}
                        type="info"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                    
                    <Select
                        placeholder="Search and select agent"
                        style={{ width: '100%' }}
                        size="large"
                        value={modalState.selectedAgent}
                        onChange={(value) => setModalState(prev => ({ ...prev, selectedAgent: value }))}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.props?.children?.props?.children?.[0] || '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {serviceAgents.map(agent => (
                            <Option key={agent.id} value={agent.id}>
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>{agent.name}</div>
                                   
                                </div>
                            </Option>
                        ))}
                    </Select>
                    
                    {serviceAgents.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No agents available
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};
// ========== NEW: LEAD COMMENTS HISTORY TAB COMPONENT ==========
const LeadCommentsTab = ({ lead, onUpdateSuccess, userData }) => {
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    const [comments, setComments] = useState([]);
    const [editingCommentIndex, setEditingCommentIndex] = useState(null);
    const [editedComment, setEditedComment] = useState('');
    const messageApi = message;

    // Load comments when lead changes
    useEffect(() => {
        if (lead?.leadCommentsHistory && Array.isArray(lead.leadCommentsHistory)) {
            // Sort by date (oldest first or newest first - your choice)
            const sortedComments = [...lead.leadCommentsHistory].sort((a, b) => 
                new Date(a.changedAt) - new Date(b.changedAt)
            );
            setComments(sortedComments);
        } else {
            setComments([]);
        }
    }, [lead]);

    // Handle adding new comment
    const handleAddComment = async () => {
        if (!newComment.trim()) {
            messageApi.warning('Please enter a comment');
            return;
        }

        try {
            setAddingComment(true);
            
            const payload = {
                comment: newComment.trim()
                // Note: Your backend should handle adding to leadCommentsHistory array
                // Based on your updateLead function, this should work automatically
            };
            
            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status || response?.success) {
                messageApi.success('Comment added successfully');
                setNewComment('');
                if (onUpdateSuccess) {
                    await onUpdateSuccess(); // Refresh lead data
                }
            } else {
                messageApi.error(response?.message || 'Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            messageApi.error('Failed to add comment');
        } finally {
            setAddingComment(false);
        }
    };

    // Handle editing comment
    const handleEditComment = (index) => {
        setEditingCommentIndex(index);
        setEditedComment(comments[index].comment);
    };

    const handleSaveEdit = async (index) => {
        if (!editedComment.trim()) {
            messageApi.warning('Comment cannot be empty');
            return;
        }

        try {
            setAddingComment(true);
            
            // Update the comment in the array
            const updatedComments = [...comments];
            updatedComments[index] = {
                ...updatedComments[index],
                comment: editedComment.trim(),
                editedAt: new Date().toISOString() // Optional: track edit time
            };
            
            // You need to send the entire updated array to backend
            // This assumes your backend supports updating leadCommentsHistory directly
            const payload = {
                leadCommentsHistory: updatedComments
            };
            
            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status || response?.success) {
                messageApi.success('Comment updated successfully');
                setEditingCommentIndex(null);
                setEditedComment('');
                if (onUpdateSuccess) {
                    await onUpdateSuccess();
                }
            } else {
                messageApi.error(response?.message || 'Failed to update comment');
            }
        } catch (error) {
            console.error('Error updating comment:', error);
            messageApi.error('Failed to update comment');
        } finally {
            setAddingComment(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentIndex(null);
        setEditedComment('');
    };

    // Handle deleting comment
    const handleDeleteComment = (index) => {
        Modal.confirm({
            title: 'Delete Comment',
            content: 'Are you sure you want to delete this comment? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setAddingComment(true);
                    
                    const updatedComments = comments.filter((_, i) => i !== index);
                    
                    const payload = {
                        leadCommentsHistory: updatedComments
                    };
                    
                    const response = await API.UpdateLead(lead._id, payload);
                    
                    if (response?.status || response?.success) {
                        messageApi.success('Comment deleted successfully');
                        if (onUpdateSuccess) {
                            await onUpdateSuccess();
                        }
                    } else {
                        messageApi.error(response?.message || 'Failed to delete comment');
                    }
                } catch (error) {
                    console.error('Error deleting comment:', error);
                    messageApi.error('Failed to delete comment');
                } finally {
                    setAddingComment(false);
                }
            }
        });
    };

    // Format date for display
    const formatCommentDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Get user role from localStorage
    const isAdmin = userData?.role === 'ADMIN';

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MessageOutlined style={{ color: '#1890ff' }} />
                    <span>Lead Comments History</span>
                    <Badge 
                        count={comments.length} 
                        showZero 
                        style={{ backgroundColor: '#52c41a' }}
                    />
                </div>
            }
            style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
        >
            {/* Add New Comment Section */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ marginBottom: '8px' }}>
                    <Text strong>Add New Comment:</Text>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Type your comment here... (e.g., Spoke with customer, he requested quote)"
                        rows={3}
                        style={{ flex: 1, borderRadius: '8px' }}
                        maxLength={500}
                        showCount
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddComment}
                        loading={addingComment}
                        style={{ height: 'auto', padding: '8px 16px', borderRadius: '8px' }}
                    >
                        Add Comment
                    </Button>
                </div>
            </div>

            {/* Comments List */}
            {comments.length === 0 ? (
                <Empty
                    description="No comments yet. Add the first comment above."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                />
            ) : (
                <Timeline
                    mode="left"
                    style={{ marginTop: '16px' }}
                    items={comments.map((comment, index) => ({
                        color: comment.editedAt ? 'blue' : 'green',
                        dot: comment.editedAt ? <EditOutlined /> : <MessageOutlined />,
                        label: (
                            <div style={{ minWidth: '160px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {formatCommentDate(comment.changedAt)}
                                </Text>
                                {comment.editedAt && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '11px', color: '#1890ff' }}>
                                            (Edited: {formatCommentDate(comment.editedAt)})
                                        </Text>
                                    </div>
                                )}
                            </div>
                        ),
                        children: (
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: '#fafafa',
                                    borderRadius: '8px',
                                    borderLeft: `3px solid ${comment.editedAt ? '#1890ff' : '#52c41a'}`
                                }}
                                bodyStyle={{ padding: '12px' }}
                            >
                                {editingCommentIndex === index ? (
                                    <div>
                                        <Input.TextArea
                                            value={editedComment}
                                            onChange={(e) => setEditedComment(e.target.value)}
                                            rows={3}
                                            autoFocus
                                            style={{ marginBottom: '8px', borderRadius: '6px' }}
                                        />
                                        <Space>
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<CheckOutlined />}
                                                onClick={() => handleSaveEdit(index)}
                                                loading={addingComment}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<CloseOutlined />}
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </Button>
                                        </Space>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: '8px' }}>
                                            {comment.comment}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        onClick={() => handleEditComment(index)}
                                                        style={{ padding: '0 4px' }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => handleDeleteComment(index)}
                                                        style={{ padding: '0 4px' }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )
                    }))}
                />
            )}

        
        </Card>
    );
};
// Main LeadDetails Component
const LeadDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isOrderProcessSource = location.state?.sourceTab === 'order-process';
    const requestedActiveTab = location.state?.activeTab;
    const leadDetailsPageRef = useRef(null);
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agentMap, setAgentMap] = useState({});
    const [agentFullMap, setAgentFullMap] = useState({});
const [serviceAgentMap, setServiceAgentMap] = useState({});
const [serviceAgentDetailsMap, setServiceAgentDetailsMap] = useState({});
    const [activeTab, setActiveTabState] = useState('details');
    // Never open a tab that is hidden for the current user; fall back to Lead Details
    const setActiveTab = (tabKey) => {
        setActiveTabState(
            isSalesAgentUser() && SALES_AGENT_HIDDEN_TABS.includes(tabKey) ? 'details' : tabKey
        );
    };
    const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
    const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
    const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
    const [addressModalVisible, setAddressModalVisible] = useState(false);
    const [yardModalVisible, setYardModalVisible] = useState(false);
    const [showZohoSignModal, setShowZohoSignModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const currentLeadYard = getLatestYardSelection(lead?.yard || []).currentYard;

    const userDataString = localStorage.getItem('userData');
    const userData = userDataString ? JSON.parse(userDataString) : null;
    const isAdmin = userData?.role === "ADMIN";

const [drawerVisible, setDrawerVisible] = useState(false);
const [selectedLeadData, setSelectedLeadData] = useState(null);

const [zohoStatus, setZohoStatus] = useState('');
const [invoiceStatus, setInvoiceStatus] = useState('');
const [purchaseOrderStatus, setPurchaseOrderStatus] = useState('');
const [showFTWEInvoiceModal, setShowFTWEInvoiceModal] = useState(false);
const documentStatusTooltips = {
    zoho: 'To update Zoho Sign Status, please go to Zoho Sign Pending page.',
    invoice: 'To update Invoice Status, please go to Invoice Pending page.',
    purchaseOrder: 'To update Purchase Order status, please go to Order Process page.'
};

const [isEditingOrderNumber, setIsEditingOrderNumber] = useState(false);
const [orderNumberInput, setOrderNumberInput] = useState('');
const [updatingOrderNumber, setUpdatingOrderNumber] = useState(false);
const [isEditingOrderDate, setIsEditingOrderDate] = useState(false);
const [orderDateInput, setOrderDateInput] = useState(null);
const [updatingOrderDate, setUpdatingOrderDate] = useState(false);

    // Scroll to the target div when component mounts or lead loads
    useEffect(() => {
        if (leadDetailsPageRef.current && !loading) {
            // Scroll to the div with smooth behavior
            leadDetailsPageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start' // Align to top of viewport
            });
        }
    }, [loading]); // Run when loading changes (when data is loaded)

    // Add another effect for initial load
    useEffect(() => {
        // Scroll to top immediately on initial render
        window.scrollTo(0, 0);

        // Then after a short delay, scroll to the specific div
        const timer = setTimeout(() => {
            if (leadDetailsPageRef.current) {
                leadDetailsPageRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 100);

        return () => clearTimeout(timer);
    }, []);

// Add this useEffect to sync status values when lead changes
useEffect(() => {
    if (lead) {
        setZohoStatus(lead.zohoStatus || '');
        setInvoiceStatus(lead.invoiceStatus || '');
        setPurchaseOrderStatus(lead.purchaseOrderStatus || '');
    }
}, [lead]);
const handleActiveTabChange = (tabKey) => {
    setActiveTab(tabKey);
};
// Handle edit order number click
const handleEditOrderNumber = () => {
    setOrderNumberInput(lead?.orderNumber || '');
    setIsEditingOrderNumber(true);
};

// Handle save order number
const handleSaveOrderNumber = async () => {
    if (!orderNumberInput || orderNumberInput.trim() === '') {
        message.warning('Please enter an order number');
        return;
    }

    try {
        setUpdatingOrderNumber(true);
        
        const payload = {
            orderNumber: orderNumberInput.trim(),
            orderDate: lead?.orderDate || new Date().toISOString()
        };
        
        const response = await API.UpdateLead(lead._id, payload);
        
        if (response?.status || response?.success) {
            message.success('Order number updated successfully!');
            setIsEditingOrderNumber(false);
            await loadLead(); // Refresh lead data
        } else {
            message.error(response?.message || 'Failed to update order number');
        }
    } catch (error) {
        console.error('Error updating order number:', error);
        message.error('Failed to update order number');
    } finally {
        setUpdatingOrderNumber(false);
    }
};
const handleEditOrderDate = () => {
    setIsEditingOrderDate(true);
    setOrderDateInput(lead?.orderDate || null);
};

const handleSaveOrderDate = async () => {
    if (!orderDateInput) {
        message.warning('Please select a date');
        return;
    }
      
    setUpdatingOrderDate(true);
    
    try {
        const newDateUTC = dayjs(orderDateInput)
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toISOString();
        
        const updateData = {
            orderDate: newDateUTC
        };
        
        const currentHistory = lead.leadStageHistory || [];
        const salesMadeIndex = currentHistory.findIndex(
            history => history.stage === 'Sales Made'
        );
        
        let updatedHistory;
        
        if (salesMadeIndex !== -1) {
            updatedHistory = [...currentHistory];
            updatedHistory[salesMadeIndex] = {
                ...updatedHistory[salesMadeIndex],
                changedAt: newDateUTC,
                comment: `Order date updated to ${dayjs(newDateUTC).format('MM-DD-YYYY')}`
            };
        } else {
            updatedHistory = [
                ...currentHistory,
                {
                    stage: 'Sales Made',
                    changedAt: newDateUTC,
                    comment: `Sales Made completed on ${dayjs(newDateUTC).format('MM-DD-YYYY')}`
                }
            ];
        }
        
        updateData.leadStageHistory = updatedHistory;
        
        if (lead.leadStage !== 'Sales Made') {
            updateData.leadStage = 'Sales Made';
        }
        
        const response = await API.UpdateLead(lead._id, updateData);
        
        if (response.status) {
            message.success('Order date and sales stage updated successfully');
            setIsEditingOrderDate(false);
            setOrderDateInput(null);
             await loadLead(); 
        } else {
            message.error(response?.message || 'Failed to update order date');
        }
    } catch (error) {
        console.error('Error updating order date:', error);
        message.error('Failed to update order date');
    } finally {
        setUpdatingOrderDate(false);
    }
};

const handleCancelEditOrderDate = () => {
    setIsEditingOrderDate(false);
    setOrderDateInput(null);
};
// Handle cancel edit
const handleCancelEditOrderNumber = () => {
    setIsEditingOrderNumber(false);
    setOrderNumberInput('');
};
// Status options arrays
const zohoStatusOptions = [
    'SENT',
    'NOT-SENT',
    'COMPLETED',
    'INCOMPLETE',
    'FAILED',
    'RECEIVED',
    'DL-COPY',
    'EMAIL-APPROVAL'
];

const invoiceStatusOptions = [
    'INVOICE-SENT',
    'INVOICE-NOT-SENT'
];

const purchaseOrderStatusOptions = [
    'PO-SENT',
    'PO-NOT-SENT',
    'PO-CONFIRMED',
    'PO-NOT-CONFIRMED'
];

// Status color mapping function
const getStatusColor = (status, type) => {
    const colorMap = {
        zoho: {
            'SENT': 'blue',
            'NOT-SENT': 'default',
            'COMPLETED': 'green',
            'INCOMPLETE': 'orange',
            'FAILED': 'red',
            'RECEIVED': 'purple',
            'DL-COPY': 'cyan',
            'EMAIL-APPROVAL': 'magenta'
        },
        invoice: {
            'INVOICE-SENT': 'green',
            'INVOICE-NOT-SENT': 'default'
        },
        po: {
            'PO-SENT': 'blue',
            'PO-NOT-SENT': 'default',
            'PO-CONFIRMED': 'green',
            'PO-NOT-CONFIRMED': 'orange'
        }
    };
    
    return colorMap[type]?.[status] || 'default';
};
const DocumentStatusCard = () => {
    const shouldShowStatusCard = () => {
        const stages = ['Pending Payment', 'Payment Completed', 'Sales Made'];
        return stages.includes(lead?.leadStage);
    };

    if (!shouldShowStatusCard()) return null;

    return (
        <Card 
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    <span>Document Status Management</span>
                </div>
            }
            style={{ 
                marginBottom: '16px',
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
        >
            <Row gutter={[16, 16]}>
                {/* Zoho Status */}
                <Col span={24}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '16px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px'
                    }}>
                        <div style={{ minWidth: '140px' }}>
                            <Space>
                                <SendOutlined style={{ color: '#1890ff' }} />
                                <Text strong>Zoho Sign Status</Text>
                            </Space>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Tooltip title={documentStatusTooltips.zoho}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Select
                                        value={zohoStatus}
                                        style={{ width: '100%' }}
                                        placeholder="Select Status"
                                        disabled
                                        size="middle"
                                    >
                                        {zohoStatusOptions.map(option => (
                                            <Option key={option} value={option}>
                                                <Space>
                                                    <Tag color={getStatusColor(option, 'zoho')} style={{ margin: 0 }}>
                                                        {option.replace(/-/g, ' ')}
                                                    </Tag>
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </Col>

                {/* Invoice Status */}
                <Col span={24}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '16px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px'
                    }}>
                        <div style={{ minWidth: '140px' }}>
                            <Space>
                                <FileTextOutlined style={{ color: '#52c41a' }} />
                                <Text strong>Invoice Status</Text>
                            </Space>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Tooltip title={documentStatusTooltips.invoice}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Select
                                        value={invoiceStatus}
                                        style={{ width: '100%' }}
                                        placeholder="Select Status"
                                        disabled
                                        size="middle"
                                    >
                                        {invoiceStatusOptions.map(option => (
                                            <Option key={option} value={option}>
                                                <Space>
                                                    <Tag color={getStatusColor(option, 'invoice')} style={{ margin: 0 }}>
                                                        {option.replace(/-/g, ' ')}
                                                    </Tag>
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </Col>

                {/* Purchase Order Status */}
                <Col span={24}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '16px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px'
                    }}>
                        <div style={{ minWidth: '140px' }}>
                            <Space>
                                <ShoppingCartOutlined style={{ color: '#faad14' }} />
                                <Text strong>Purchase Order</Text>
                            </Space>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Tooltip title={documentStatusTooltips.purchaseOrder}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Select
                                        value={purchaseOrderStatus}
                                        style={{ width: '100%' }}
                                        placeholder="Select Status"
                                        disabled
                                        size="middle"
                                    >
                                        {purchaseOrderStatusOptions.map(option => (
                                            <Option key={option} value={option}>
                                                <Space>
                                                    <Tag color={getStatusColor(option, 'po')} style={{ margin: 0 }}>
                                                        {option.replace(/_/g, ' ')}
                                                    </Tag>
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </Col>
            </Row>
        </Card>
    );
};
    useEffect(() => {
        loadLead({ showSpinner: true, resetActiveTab: true });
        loadAgents();
    }, [id]);

const loadLead = async ({ showSpinner = !lead, resetActiveTab = false } = {}) => {
    try {
        if (showSpinner) {
            setLoading(true);
        }

        const response = await API.GetLeadById(id);
        console.log('Lead data loaded:', response); // Debug log
        console.log('Shipment tracking in loaded lead:', response?.data?.ShipmentTracking); // Debug log

        const nextLead = response?.data || null;

        if (response?.status) {
            setLead(nextLead);
            if (resetActiveTab) {
                setActiveTab(getDefaultLeadTab(nextLead, requestedActiveTab));
            }
        } else if (nextLead) {
            setLead(nextLead);
            if (resetActiveTab) {
                setActiveTab(getDefaultLeadTab(nextLead, requestedActiveTab));
            }
        }
    } catch (error) {
        console.error('Error loading lead:', error);
        message.error('Failed to load lead details');
    } finally {
        if (showSpinner) {
            setLoading(false);
        }
    }
};

    const loadAgents = async () => {
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                const map = {};
                const agentDetails = {};
   const serviceAgentMap = {};     // For service agents
            const serviceAgentDetails = {};
                response.data.forEach((user) => {
                    if (user.role === "SALES-AGENT") {
                        map[user._id] = `${user.firstName} ${user.lastName}`;
                        agentDetails[user._id] = {
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                            phone: user.directNumber,
                            role: user.role,
                            status: user.status,
                                extension: user.extension,
    userName: user.userName,
    directNumber: user.directNumber,
    subRole: user.subRole,
    isPerformance: user.isPerformance,
    domainGroups: user.domainGroups,
    subRole: user.subRole,
    subset1: user.subset1,
    subset2: user.subset2,
    isDefaultTierEligible: user.isDefaultTierEligible,
    premium: user.premium
                        };
                    }
                         if (user.role === "SERVICE-AGENT") {
                    serviceAgentMap[user._id] = `${user.firstName} ${user.lastName}`;
                    serviceAgentDetails[user._id] = {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        phone: user.directNumber,
                        role: user.role,
                        status: user.status,
                            extension: user.extension,
    userName: user.userName,
    directNumber: user.directNumber,
    subRole: user.subRole,
    isPerformance: user.isPerformance,
    domainGroups: user.domainGroups,
    subRole: user.subRole,
    subset1: user.subset1,
    subset2: user.subset2,
    isDefaultTierEligible: user.isDefaultTierEligible,
    premium: user.premium
                    };
                }
                });

                setAgentMap(map);
                setAgentFullMap(agentDetails);
                setServiceAgentMap(serviceAgentMap);
                setServiceAgentDetailsMap(serviceAgentDetails);
            }
        } catch (error) {
            console.error('Error loading agents:', error);
        }
    };

    const handleSendEmail = () => {
        if (!lead?.personalInfo?.email) {
            message.warning('Customer email not available');
            return;
        }
        setIsEmailModalVisible(true);
    };

    const handleTransferSuccess = async () => {
        setIsTransferModalVisible(false);
        await Promise.all([
            loadLead({ showSpinner: false }),
            loadAgents()
        ]);
    };

    const handleTransferButtonClick = () => {
        setIsTransferModalVisible(true);
    };

    const handleEditButtonClick = () => {
        setIsEditDrawerVisible(true);
    };
const handleSendZohoSign = () => {
  setSelectedLeadData(lead);
  setDrawerVisible(true);
};
const handleFTWEPreviewInvoice = () => {
    if (!lead?._id) {
        message.warning('Lead information not available');
        return;
    }
    setShowFTWEInvoiceModal(true);
};

    const handleUpdateLeadSuccess = async () => {
        setIsEditDrawerVisible(false);
        await loadLead({ showSpinner: false });
    };

    const getTierTypeDisplay = (tierType) => {
        if (!tierType) return null;

        const colorMap = {
            'TIER-1': 'green',
            'TIER-2': 'blue',
            'TIER-3': 'purple'
        };

        return (
            <Tag color={colorMap[tierType] || 'default'}>
                {tierType}
            </Tag>
        );
    };

    const getStagesRequiringComments = () => {
        return leadStages.filter(stage => stage !== "Test Lead");
    };

    // Check if address data exists
    const hasAddressData = () => {
        if (!lead) return false;
        if (lead.bussinessName && lead.bussinessName.trim().length > 0) return true;

        const billingHasData = lead.billingAddress &&
            lead.billingAddress.street &&
            lead.billingAddress.street.trim().length > 0;

        const shippingHasData = lead.shippingAddress &&
            lead.shippingAddress.street &&
            lead.shippingAddress.street.trim().length > 0;

        return billingHasData || shippingHasData;
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return formatPSTDate(dateString);
    };

    // Show payment details
    const showPaymentDetails = (payment, index) => {
        Modal.info({
            title: `Payment Details - #${index + 1}`,
            width: 1000,
            content: (
                <div style={{ marginTop: '20px' }}>
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Date" span={2}>
                            {formatDate(payment.date)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Amount">
                            <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                ${payment.amount?.toFixed(2)}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Payment Method">
                            <Tag
                                color={getPaymentMethodColor(payment.paymentMethod)}
                                icon={getPaymentMethodIcon(payment.paymentMethod)}
                            >
                                {payment.paymentMethod}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Badge
                                status={payment.status === 'Completed' ? 'success' : 'processing'}
                                text={payment.status}
                            />
                        </Descriptions.Item>
                        {payment.cardType && (
                            <Descriptions.Item label="Card Type">
                                {payment.cardType}
                            </Descriptions.Item>
                        )}
                        {payment.cardNumber && (
                            <Descriptions.Item label="Card Number">
                                **** **** **** {payment.cardNumber.slice(-4)}
                            </Descriptions.Item>
                        )}
                        {payment.notes && (
                            <Descriptions.Item label="Notes" span={2}>
                                {payment.notes}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Created By" span={2}>
                            {payment.createdBy ? agentMap[payment.createdBy] || 'Unknown' : 'System'}
                        </Descriptions.Item>
                    </Descriptions>
                </div>
            ),
            okText: 'Close',
        });
    };

    // Combined Address/Yard/Zoho Tab Component
    const CombinedAddressYardZohoTab = () => {
        const [yardDetails, setYardDetails] = useState(null);
        const [yardLoading, setYardLoading] = useState(false);
        const [yardError, setYardError] = useState(null);
        const {
            currentYard: combinedCurrentYard,
            previousYards: combinedYardHistory
        } = getLatestYardSelection(lead?.yard || []);
        const combinedCurrentYardId = normalizeYardId(combinedCurrentYard?.yardId);
        const combinedCurrentYardAddress = combinedCurrentYard?.yardAddress;

        // Fetch yard details when component mounts or when lead yard changes
        useEffect(() => {
            const fetchYardDetails = async () => {
                if (combinedCurrentYardId) {
                    setYardLoading(true);
                    setYardError(null);
                    try {
                        const response = await API.GetYardById(combinedCurrentYardId);
                        if (response?.status) {
                            setYardDetails(response.data);
                        } else if (response?.data) {
                            setYardDetails(response.data);
                        } else if (response?.success) {
                            setYardDetails(response.data || response);
                        } else {
                            setYardError('Failed to load yard details');
                        }
                    } catch (error) {
                        console.error('Error fetching yard details:', error);
                        setYardError('Error loading yard details');
                    } finally {
                        setYardLoading(false);
                    }
                } else {
                    setYardDetails(null);
                }
            };

            fetchYardDetails();
        }, [combinedCurrentYardId]);

        const combinedContactPerson = getAssignmentOrPrimaryYardValue(combinedCurrentYard?.contactPerson, yardDetails?.contactPerson);
        const combinedContactEmail = getAssignmentOrPrimaryYardValue(combinedCurrentYard?.contactEmail, yardDetails?.email);
        const combinedContactNumber = getAssignmentOrPrimaryYardValue(combinedCurrentYard?.contactNumber, yardDetails?.contactNumbers);
        const combinedContactExtension = getAssignmentOrPrimaryYardValue(
            combinedCurrentYard?.contactPersonExtension,
            yardDetails?.contactPersonExtension
        );

        return (
            <Card>
                {/* Header with Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Title level={4} style={{ margin: 0 }}>Address, Yard & Zoho Information</Title>
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => setAddressModalVisible(true)}
                            icon={<EnvironmentOutlined />}
                        >
                            {hasAddressData() ? 'Edit Address' : 'Add Address'}
                        </Button>
                        <Button
                            type="default"
                            onClick={() => setYardModalVisible(true)}
                            icon={<CarOutlined />}
                        >
                            {combinedCurrentYard ? 'Change Yard' : 'Assign Yard'}
                        </Button>
                        {(lead.leadStage === 'Payment Completed' || lead.leadStage === 'Sales Made') && (
                            <Button
                                type="primary"
  onClick={handleSendZohoSign}
                                  icon={<SendOutlined />}
                            >
                                View Zoho Sign
                            </Button>
                        )}
                    </Space>
                </div>

                {/* Address Section */}
                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <EnvironmentOutlined />
                            <span>Address Information</span>
                        </div>
                    }
                    style={{ marginBottom: '24px' }}
                >
                    {hasAddressData() ? (
                        <Row gutter={16}>
                            {lead.bussinessName && lead.bussinessName.trim().length > 0 && (
                                <Col span={24} style={{ marginBottom: '16px' }}>
                                    <Card
                                        title="Business Information"
                                        size="small"
                                        style={{ borderTop: '3px solid #722ed1' }}
                                    >
                                        <div style={{ marginBottom: 8 }}>
                                            <Text strong style={{ fontSize: '12px', color: '#666' }}>Business Name:</Text>
                                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                                                {lead.bussinessName}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            )}

                            {lead.billingAddress &&
                                lead.billingAddress.street &&
                                lead.billingAddress.street.trim().length > 0 && (
                                    <Col span={24}>
                                        <Card
                                            title="Billing Address"
                                            size="small"
                                            style={{ borderTop: '3px solid #1890ff' }}
                                        >
                                            <div>
                                                <div style={{ marginBottom: 6 }}>
                                                    <Text strong style={{ fontSize: '12px', color: '#666' }}>Customer Name: </Text>
                                                    <Text>{lead.personalInfo?.fullName || 'N/A'}</Text>
                                                </div>
                                                <div>{lead.billingAddress.street} <br /></div>
                                                <div>
                                                    {lead.billingAddress.city || ''}, {lead.billingAddress.state || ''}, <br />
                                                    {lead.billingAddress.zip ? ` ${lead.billingAddress.zip}` : ''}
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '16px', fontSize: '12px', color: '#1890ff' }}>
                                                <CreditCardOutlined /> Billing Address
                                            </div>
                                        </Card>
                                    </Col>
                                )}

                            {lead.shippingAddress &&
                                lead.shippingAddress.street &&
                                lead.shippingAddress.street.trim().length > 0 && (
                                    <Col span={24}>
                                        <Card
                                            title="Shipping Address"
                                            size="small"
                                            style={{ borderTop: '3px solid #52c41a' }}
                                        >
                                            {lead.billingAddress &&
                                                lead.billingAddress.street === lead.shippingAddress.street &&
                                                lead.billingAddress.city === lead.shippingAddress.city &&
                                                lead.billingAddress.state === lead.shippingAddress.state &&
                                                lead.billingAddress.zip === lead.shippingAddress.zip ? (
                                                <div style={{ textAlign: 'center', padding: '16px' }}>
                                                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                                                    <div style={{ color: '#52c41a', fontWeight: 'bold' }}>Same as Billing Address</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div style={{ marginBottom: 6 }}>
                                                        <Text strong style={{ fontSize: '12px', color: '#666' }}>ATTN Name: </Text>
                                                        <Text>{lead.attnName || lead.personalInfo?.fullName || 'N/A'}</Text>
                                                    </div>
                                                    <div>{lead.shippingAddress.street}<br /></div>
                                                    <div>
                                                        {lead.shippingAddress.city || ''}, {lead.shippingAddress.state || ''}, <br />
                                                        {lead.shippingAddress.zip ? ` ${lead.shippingAddress.zip}` : ''}
                                                    </div>
                                                </div>
                                            )}
                                            <div style={{ marginTop: '16px', fontSize: '12px', color: '#52c41a' }}>
                                                <HomeOutlined /> Shipping Address
                                            </div>
                                        </Card>
                                    </Col>
                                )}
                        </Row>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '64px 16px' }}>
                            <EnvironmentOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '24px' }} />
                            <Title level={4} style={{ color: '#666', marginBottom: '16px' }}>
                                No Address Information
                            </Title>
                            <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
                                Add billing and shipping addresses to complete customer information
                            </Text>
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => setAddressModalVisible(true)}
                                icon={<PlusOutlined />}
                                style={{ minWidth: '200px' }}
                            >
                                Add Address Information
                            </Button>
                        </div>
                    )}
                </Card>

                {/* Yard Section */}
                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CarOutlined />
                            <span>Yard Information</span>
                            <Tag color={combinedCurrentYard ? 'green' : 'orange'}>
                                {combinedCurrentYard ? 'Assigned' : 'Not Assigned'}
                            </Tag>
                        </div>
                    }
                    style={{ marginBottom: '24px' }}
                >
                    {yardLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '16px', color: '#666' }}>Loading yard details...</div>
                        </div>
                    ) : yardError ? (
                        <Alert
                            message="Error Loading Yard Details"
                            description={yardError}
                            type="error"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />
                    ) : combinedCurrentYard && yardDetails ? (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card
                                    style={{ marginBottom: '16px', borderLeft: '4px solid #52c41a' }}
                                >
                                    {/* Yard Header */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <CarOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                                                <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                                                    {yardDetails.yardName || 'Unnamed Yard'}
                                                </Title>
                                            </div>
                                            <Text type="secondary">
                                                <EnvironmentOutlined style={{ marginRight: '4px' }} />
                                                {formatYardAddress(combinedCurrentYardAddress) || 'N/A'}
                                            </Text>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Assigned Price</div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                                ${(combinedCurrentYard.price || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Yard Details Grid */}
                                    <Row gutter={[16, 16]}>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Contact Person</div>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                        {combinedContactPerson || 'N/A'}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email</div>
                                                    <div>
                                                        {combinedContactEmail ? (
                                                            <a href={`mailto:${combinedContactEmail}`} style={{ fontSize: '14px' }}>
                                                                {combinedContactEmail}
                                                            </a>
                                                        ) : 'N/A'}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Phone</div>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                        {combinedContactNumber || 'N/A'}
                                                    </div>
                                                    {combinedContactExtension && (
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            Ext: {combinedContactExtension}
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Stock Number</div>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                        {formatYardAssignmentValue(combinedCurrentYard.stockNumber)}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Quote Number</div>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                        {formatYardAssignmentValue(combinedCurrentYard.quoteNumber)}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col span={8}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Part Grade</div>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                        {formatYardAssignmentValue(combinedCurrentYard.partGrade)}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Additional Information */}
                                    {(yardDetails.link || combinedCurrentYard.description) && (
                                        <div style={{ marginTop: '16px' }}>
                                            {yardDetails.link && (
                                                <div style={{ marginBottom: '8px' }}>
                                                    <Text strong style={{ fontSize: '12px', color: '#666' }}>Website: </Text>
                                                    <a href={yardDetails.link} target="_blank" rel="noopener noreferrer">
                                                        {yardDetails.link}
                                                    </a>
                                                </div>
                                            )}
                                            {combinedCurrentYard.description && (
                                                <div>
                                                    <Text strong style={{ fontSize: '12px', color: '#666' }}>Description: </Text>
                                                    <Text>{combinedCurrentYard.description}</Text>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Additional Details Button */}
                                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                        <Button
                                            type="link"
                                            onClick={() => {
                                                Modal.info({
                                                    title: 'Full Yard Details',
                                                    width: 800,
                                                    content: (
                                                        <Descriptions column={2} bordered size="small">
                                                            <Descriptions.Item label="Yard Name" span={2}>
                                                                {yardDetails.yardName || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Assigned Price">
                                                                <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                                                                    ${(combinedCurrentYard.price || 0).toFixed(2)}
                                                                </Text>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Contact Person">
                                                                {combinedContactPerson || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Email">
                                                                {combinedContactEmail ? (
                                                                    <a href={`mailto:${combinedContactEmail}`}>{combinedContactEmail}</a>
                                                                ) : 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Phone">
                                                                {combinedContactNumber || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Extension">
                                                                {combinedContactExtension || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Address" span={2}>
                                                                {formatYardAddress(combinedCurrentYardAddress) || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Yard Warranty">
                                                                {combinedCurrentYard.yardWarranty || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Yard Miles">
                                                                {combinedCurrentYard.yardMiles || 'N/A'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Stock Number">
                                                                {formatYardAssignmentValue(combinedCurrentYard.stockNumber)}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Quote Number">
                                                                {formatYardAssignmentValue(combinedCurrentYard.quoteNumber)}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Part Grade">
                                                                {formatYardAssignmentValue(combinedCurrentYard.partGrade)}
                                                            </Descriptions.Item>
                                                            {yardDetails.link && (
                                                                <Descriptions.Item label="Website" span={2}>
                                                                    <a href={yardDetails.link} target="_blank" rel="noopener noreferrer">
                                                                        {yardDetails.link}
                                                                    </a>
                                                                </Descriptions.Item>
                                                            )}
                                                            {combinedCurrentYard.description && (
                                                                <Descriptions.Item label="Description" span={2}>
                                                                    {combinedCurrentYard.description}
                                                                </Descriptions.Item>
                                                            )}
                                                        
                                                        </Descriptions>
                                                    ),
                                                    okText: 'Close'
                                                });
                                            }}
                                        >
                                            View Full Details
                                        </Button>
                                    </div>
                                </Card>
                            </Col>

                            {/* Yard History (if multiple yards assigned) */}
                            {combinedYardHistory.length > 0 && (
                                <Col span={24}>
                                    <Collapse>
                                        <Collapse.Panel header={`Previous Yards (${combinedYardHistory.length})`} key="history">
                                            <Timeline>
                                                {combinedYardHistory.map((yard, index) => (
                                                    <Timeline.Item key={index} color="gray">
                                                        <Card size="small">
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <div>
                                                                    <Text strong>{getYardDisplayName(null, yard)}</Text>
                                                                    <div style={{ marginTop: '4px' }}>
                                                                        <div><strong>Price:</strong> ${(yard.price || 0).toFixed(2)}</div>
                                                                        <div><strong>Address:</strong> {formatYardAddress(yard.yardAddress) || 'N/A'}</div>
                                                                        {yard.yardWarranty && <div><strong>Warranty:</strong> {yard.yardWarranty}</div>}
                                                                        {yard.yardMiles && <div><strong>Miles:</strong> {yard.yardMiles}</div>}
                                                                        {yard.stockNumber && <div><strong>Stock #:</strong> {yard.stockNumber}</div>}
                                                                        {yard.quoteNumber && <div><strong>Quote #:</strong> {yard.quoteNumber}</div>}
                                                                        {yard.partGrade && <div><strong>Part Grade:</strong> {yard.partGrade}</div>}
                                                                        {yard.description && (
                                                                            <div><strong>Description:</strong> {yard.description}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                               
                                                            </div>
                                                        </Card>
                                                    </Timeline.Item>
                                                ))}
                                            </Timeline>
                                        </Collapse.Panel>
                                    </Collapse>
                                </Col>
                            )}
                        </Row>
                    ) : combinedCurrentYard ? (
                        <Alert
                            message="Yard Details Unavailable"
                            description="Yard is assigned but details could not be loaded."
                            type="warning"
                            showIcon
                            action={
                                <Button
                                    size="small"
                                    onClick={async () => {
                                        setYardLoading(true);
                                        setYardError(null);
                                        try {
                                            const response = await API.GetYardById(combinedCurrentYardId);
                                            if (response?.status) {
                                                setYardDetails(response.data);
                                            } else if (response?.data) {
                                                setYardDetails(response.data);
                                            } else {
                                                setYardError('Failed to load yard details');
                                            }
                                        } catch (error) {
                                            setYardError('Error loading yard details');
                                        } finally {
                                            setYardLoading(false);
                                        }
                                    }}
                                >
                                    Retry
                                </Button>
                            }
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                            <CarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                            <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
                                No yard assigned to this lead
                            </Text>
                            <Button
                                type="primary"
                                onClick={() => setYardModalVisible(true)}
                                icon={<PlusOutlined />}
                            >
                                Assign Yard
                            </Button>
                        </div>
                    )}
                </Card>

                {/* Zoho Sign Section */}
                {(lead.leadStage === 'Pending Payment' ||
                    lead.leadStage === 'Payment Completed' ||
                    lead.leadStage === 'Sales Made') && (
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <SendOutlined />
                                    <span>Zoho Sign Documents</span>
                                    <Tag color={lead.zohoStatus ? getPaymentMethodColor(lead.zohoStatus) : 'default'}>
                                        {lead.zohoStatus || 'Not Sent'}
                                    </Tag>
                                </div>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Alert
                                        message="Document Actions"
                                        description="Send sales agreement and payment documents to customer via email for digital signature."
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: '16px' }}
                                    />
                                </Col>

                                <Col span={24}>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={() => setShowZohoSignModal(true)}
                                            icon={<FileTextOutlined />}
                                            style={{ minWidth: '200px', height: '48px' }}
                                        >
                                            Preview Zoho
                                        </Button>

                                        <Button
                                            type="default"
                                            size="large"
                                            onClick={() => setShowInvoiceModal(true)}
                                            icon={<FileTextOutlined />}
                                            style={{ minWidth: '200px', height: '48px' }}
                                        >
                                            Preview Invoice
                                        </Button>
                                    </div>
                                </Col>

                                {lead.zohoStatus && (
                                    <Col span={24}>
                                        <Card size="small" style={{ marginTop: '16px' }}>
                                            <Descriptions column={2} size="small">
                                                <Descriptions.Item label="Zoho Status">
                                                    <Tag color={getPaymentMethodColor(lead.zohoStatus)}>
                                                        {lead.zohoStatus}
                                                    </Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Last Updated">
                                                    {formatDate(lead.updatedAt)}
                                                </Descriptions.Item>
                                                {lead.zohoSign?.documentId && (
                                                    <Descriptions.Item label="Document ID" span={2}>
                                                        <Text copyable>{lead.zohoSign.documentId}</Text>
                                                    </Descriptions.Item>
                                                )}
                                            </Descriptions>
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )}
            </Card>
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!lead) {
        return <Empty description="Lead not found" />;
    }

    const handledById = getAgentReferenceId(lead?.handledBy);
    const currentAgentName =
        (handledById && agentMap[handledById]) ||
        getAgentDisplayName(lead?.handledBy) ||
        lead?.currentAgentName ||
        null;
    const isAgentAssigned = Boolean(handledById || currentAgentName);
    const currentStage = lead?.leadStage || "New";
    // Live balance for the header's Balance Due tag, worked out like the Payment
    // Details tab: Completed payments, plus older ones saved without a status.
    const BALANCE_TAG_STAGES = ['Pending Payment', 'Payment Completed', 'Sales Made'];
    const headerBalanceDue = BALANCE_TAG_STAGES.includes(currentStage)
        ? getBalance(lead?.totalAmount, roundMoney((lead?.advancePayments || []).reduce((sum, payment) => (
            payment?.status === 'Completed' || payment?.status === undefined
                ? sum + (Number(payment.amount) || 0)
                : sum
        ), 0)))
        : 0;
    const hasPaymentData = () => {
        if (!lead) return false;
        return lead.totalAmount > 0 ||
            (lead.advancePayments && lead.advancePayments.length > 0) ||
            lead.leadStage === 'Payment Details' ||
            lead.leadStage === 'Payment Completed' ||
            lead.leadStage === 'Sales Made' ||
            lead.leadStage === 'Pending Payment';
    };

    // Add this function in your LeadDetails component
    const handleUpdateLead = async (leadId, payload) => {
        try {
            const response = await API.UpdateLead(leadId, payload);

            if (response?.status) {
                // Refresh the lead data
                await loadLead();
                return response.data;
            } else {
                message.error(response?.message || 'Failed to update lead');
                return null;
            }
        } catch (error) {
            console.error('Error updating lead:', error);
            message.error('Error updating lead');
            return null;
        }
    };
   // Create tab items
const tabItems = [];


    tabItems.push({
        key: 'activity',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <HistoryOutlined />
                <span>Activity History</span>
            </div>
        ),
        children: <ActivityHistoryTab 
           lead={lead}
            agentMap={agentMap}
            agentDetails={agentFullMap}
            isAgentAssigned={isAgentAssigned}
            onTransferAgent={handleTransferButtonClick}
               userData={userData} 
           />

    });


tabItems.push({
    key: 'tracking-history',
    label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <HistoryOutlined />
            <span>Tracking History</span>
            <Badge status="processing" size="small" />
        </div>
    ),
    children: <TrackingHistoryTab lead={lead} userData={userData} />
});


// Add this with your other tabItems.push() calls

// ========== LEAD COMMENTS TAB ==========
tabItems.push({
    key: 'comments',
    label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MessageOutlined />
            <span>Comments</span>
            {lead?.leadCommentsHistory && lead.leadCommentsHistory.length > 0 && (
                <Badge 
                    count={lead.leadCommentsHistory.length} 
                    size="small" 
                    style={{ backgroundColor: '#52c41a' }}
                />
            )}
        </div>
    ),
    children: (
        <LeadCommentsTab
            lead={lead}
            onUpdateSuccess={loadLead}
            userData={userData}
        />
    )
});
// ADDRESS TAB - Separate tab
if (isOrderProcessSource || hasAddressData() || lead?.bussinessName ||
 lead?.leadStage === "Quoted" || lead?.leadStage === 'Payment Completed' 
 || lead?.leadStage === 'Sales Made' || lead?.leadStage === 'Pending Payment' ) {
    tabItems.push({
        key: 'address',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <EnvironmentOutlined />
                <span>Address</span>
                {hasAddressData() && (
                    <Badge status="success" size="small" />
                )}
            </div>
        ),
        children: <AddressTab 
            lead={lead} 
            onEditAddress={() => setAddressModalVisible(true)} 
            isAdmin={isAdmin}
        />
    });
}

// YARD TAB - Separate tab
if ((isOrderProcessSource 
    || currentLeadYard || lead?.leadStage === 'Pending Payment' 
    || lead?.leadStage === "Quoted" || lead?.leadStage === 'Payment Completed' 
    || lead?.leadStage === 'Sales Made') 
    // && lead?.payementAllow === 'Yes'
) {
    tabItems.push({
        key: 'yard',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CarOutlined />
                <span>Yard</span>
                {currentLeadYard && (
                    <Badge status="success" size="small" />
                )}
            </div>
        ),
        children: <YardTab 
            lead={lead} 
            onAssignYard={() => setYardModalVisible(true)}
            onUpdateSuccess={loadLead}
             isAdmin={isAdmin}  
        />
    });
}

// ZOHO SIGN TAB - Separate tab
if ((lead.leadStage === 'Pending Payment' || 
    lead.leadStage === 'Payment Completed' || 
    lead.leadStage === 'Sales Made') && lead.payementAllow === 'Yes' ) {
    tabItems.push({
        key: 'zoho',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <SendOutlined />
                <span>Zoho Sign</span>
                {lead.zohoStatus && (
                    <Badge 
                        status={lead.zohoStatus === 'Completed' ? 'success' : 'processing'} 
                        size="small" 
                    />
                )}
            </div>
        ),
        children: <ZohoTab
            lead={lead}
    onSendZoho={handleSendZohoSign}  // 👈 Add this prop
            onPreviewInvoice={() => setShowInvoiceModal(true)}
                onUpdateSuccess={loadLead}
                    onFTWEPreviewInvoice={handleFTWEPreviewInvoice}  // New prop
                    isAdmin={isAdmin}

        />
    });
}
// Shipment Tracking Tab
if (
  lead.leadStage === 'Pending Payment' ||
  lead.leadStage === 'Payment Completed' ||
  lead.leadStage === 'Sales Made'
) {
  tabItems.push(
    // In your LeadDetails component, update the Shipment Tracking tab item:
{
  key: 'shipment',
  label: (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <CarOutlined />
      <span>Shipment Tracking</span>
      {lead?.ShipmentTracking && (
        <Badge
          status={
            lead.ShipmentTracking.deliveryStatus === 'Delivered'
              ? 'success'
              : lead.ShipmentTracking.partStatus === 'Available'
              ? 'processing'
              : 'warning'
          }
          size="small"
        />
      )}
    </div>
  ),
  children: (
    <ShipmentTrackingTab
        shipmentData={lead?.ShipmentTracking || {}}
        leadId={lead._id}
        lead={lead} // Pass the entire lead object
        onUpdateSuccess={loadLead}
      />
  )
}
  );
}
if (
  isOrderProcessSource ||
  lead.leadStage === "Pending Payment" ||
  lead.leadStage === "Payment Completed" ||
  lead.leadStage === "Sales Made"
) {
  tabItems.push({
    key: "orderComplete",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CarOutlined />
        <span>Order Process Complete</span>
      </div>
    ),
    children: (
      <OrderProcessCompleteTab 
        lead={lead} 
        onUpdateSuccess={loadLead} // Pass refresh function
      />
    )
  });
}

if (
  ["Customer Canceled Order"].includes(
    lead?.ShipmentTracking?.partStatus
  )
) {
  tabItems.push({
    key: "yardRefund",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CarOutlined />
        <span>Yard Refund</span>
      </div>
    ),
    children: <YardRefundTab lead={lead} />
  });
}

if (
 ["Customer Canceled Order"].includes(lead?.ShipmentTracking?.partStatus) ||
    lead?.orderProcessComplete?.partStatus === "Customer Canceled"
) {
  tabItems.push({
    key: "customerRefund",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CarOutlined />
        <span>Customer Refund</span>
      </div>
    ),
    children: <CustomerRefundTab lead={lead} />
  });
}
if (
 ["PO-SENT", "PO-CONFIRMED"].includes(lead?.purchaseOrderStatus) ||
  lead?.orderProcessComplete?.partStatus === "Customer Canceled"
) {
  tabItems.push({
    key: "saleMade",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CarOutlined />
        <span>Sale Made</span>
      </div>
    ),
    children: <SaleMadeTab lead={lead} onUpdateSuccess={loadLead} />
  });
}
// Payment Detail Tab
if (hasPaymentData()) {
    tabItems.push({
        key: 'payment-detail',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CalculatorOutlined />
                <span>Payment Detail</span>
                {(lead.advancePayments && lead.advancePayments.length > 0) && (
                    <Badge
                        count={lead.advancePayments.length}
                        size="small"
                        offset={[5, -5]}
                    />
                )}
            </div>
        ),
        children: (
            <PaymentDetailTab
                lead={lead}
                agentMap={agentMap}
                onUpdateSuccess={loadLead}
                onOpenEditDrawer={handleEditButtonClick}
            />
        )
    });
}
// Quoted Tab
if (
  
  ((lead?.quotedValue && lead.quotedValue.length > 0) || lead?.leadStage === "Quoted")
) {
  tabItems.push({
    key: "quoted",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <DollarOutlined />
        <span>
{lead?.quotedValue?.length ? "Re-Quoted" : "Quoted"}
          </span>

        {lead?.quotedValue && lead.quotedValue.length > 0 && (
          <Badge
            count={lead.quotedValue.length}
            size="small"
            offset={[5, -5]}
          />
        )}
      </div>
    ),
    children: (
      <QuotedTab
        lead={lead}
        agentMap={agentMap}
        onUpdateSuccess={loadLead}
      />
    )
  });
}

// Follow Up Tab
if (lead?.followUpDate || lead?.leadStage === 'Follow-Up') {
    tabItems.push({
        key: 'followup',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CalendarOutlined />
                <span>Follow Up</span>
            </div>
        ),
        children: (
            <FollowUpTab
                lead={lead}
                onUpdateSuccess={loadLead}
                agentMap={agentMap}
            />
        )
    });
}

// Always add Details tab
tabItems.push({
    key: 'details',
    label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ProfileOutlined />
            <span>Lead Details</span>
        </div>
    ),
    children: 
    <DetailsTab
    lead={lead}
    isAdmin={isAdmin}
    onOpenEmailModal={handleSendEmail}
    onUpdateLead={handleUpdateLead} // Either pass this
    onRefresh={loadLead} // Or pass this
/>
});

// Always add Agent Info tab
tabItems.push({
    key: 'agentInfo',
    label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TeamOutlined />
            <span>Agent Info</span>
        </div>
    ),
    children: <AgentInfoTab
        lead={lead}
        agentMap={agentMap}
        agentDetails={agentFullMap}
        userData={userData}
        isAgentAssigned={isAgentAssigned}
        onTransferAgent={handleTransferButtonClick}
    />
});

    // All roles: show Lead Details first; sales agents also hide tabs they don't need
    const salesAgentView = isSalesAgentUser();
    const visibleTabItems = [
        ...tabItems.filter((tab) => tab.key === 'details'),
        ...tabItems.filter((tab) => tab.key !== 'details' && !(salesAgentView && SALES_AGENT_HIDDEN_TABS.includes(tab.key))),
    ];

    return (
        <>
            <div
                className="lead-details-page"
                ref={leadDetailsPageRef}
            ></div>
            <div style={{ padding: '24px', backgroundColor: '#f0f2f5' }}>
                {/* Header */}
                <div className="lead-details-header ">
                    <Card
    style={{
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        marginBottom: '10px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        border: '1px solid #e8e8e8',
    }}
    bodyStyle={{ padding: '10px' }}
>
    <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col flex="auto">
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <Button
                    type="default"
                    onClick={() => {
                        if (location.state?.from) {
                            navigate(location.state.from, {
                                state: { restoreMyLeads: true },
                                replace: true
                            });
                            return;
                        }

                        navigate(-1);
                    }}
                    icon={<ArrowLeftOutlined />}
                    style={{
                        padding: '6px 16px',
                        borderRadius: '6px',
                        borderColor: '#1890ff',
                        color: '#1890ff',
                        fontWeight: 500,
                        backgroundColor: '#e6f7ff'
                    }}
                >
                    Back
                </Button>

                <div style={{ position: 'relative' }}>
                    <Avatar
                        size={52}
                        style={{
                            backgroundColor: '#1890ff',
                            border: '4px solid #fff',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        <span style={{ fontSize: '28px', fontWeight: 'bold' }}>
                            {lead.personalInfo?.fullName?.charAt(0)?.toUpperCase() || 'L'}
                        </span>
                    </Avatar>
                </div>

                <div style={{ flex: 1, marginTop: 10 }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <Title
                            level={2}
                            style={{
                                margin: 0,
                                color: '#1f1f1f',
                                fontWeight: 600
                            }}
                        >
                            {lead.personalInfo?.fullName}
                        </Title>
                        
{/* Check if lead stage allows editing order number, order date, and PO date */}
{(() => {
    // Allowed stages for editing
    const allowedStages = ['Sales Made', 'Pending Payment', 'Payment Completed'];
    const canEditOrderNumber = isAdmin && allowedStages.includes(lead?.leadStage);
    const canEditOrderDate = isAdmin && allowedStages.includes(lead?.leadStage);

    return (
        <>
            {/* Order Number Field */}
            {lead.orderNumber ? (
                isEditingOrderNumber ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Input
                            value={orderNumberInput}
                            onChange={(e) => setOrderNumberInput(e.target.value)}
                            placeholder="Enter order number"
                            style={{ width: '180px', borderRadius: '16px' }}
                            size="small"
                            autoFocus
                        />
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={handleSaveOrderNumber}
                            loading={updatingOrderNumber}
                            style={{ borderRadius: '16px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelEditOrderNumber}
                            style={{ borderRadius: '16px' }}
                        />
                    </div>
                ) : (
                    <Tag
                        color="purple"
                        style={{
                            fontSize: '13px',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontWeight: 600,
                            border: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            cursor: canEditOrderNumber ? 'pointer' : 'default'
                        }}
                        icon={<FileTextOutlined />}
                        onClick={canEditOrderNumber ? handleEditOrderNumber : undefined}
                    >
                        Order: {lead.orderNumber}
                        {canEditOrderNumber && (
                            <EditOutlined 
                                style={{ 
                                    marginLeft: '8px', 
                                    fontSize: '12px',
                                    opacity: 0.7
                                }} 
                            />
                        )}
                    </Tag>
                )
            ) : (
                isEditingOrderNumber ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Input
                            value={orderNumberInput}
                            onChange={(e) => setOrderNumberInput(e.target.value)}
                            placeholder="Enter order number"
                            style={{ width: '180px', borderRadius: '16px' }}
                            size="small"
                            autoFocus
                        />
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={handleSaveOrderNumber}
                            loading={updatingOrderNumber}
                            style={{ borderRadius: '16px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelEditOrderNumber}
                            style={{ borderRadius: '16px' }}
                        />
                    </div>
                ) : (
                    canEditOrderNumber ? (
                        <Tag
                            color="orange"
                            style={{
                                fontSize: '13px',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontWeight: 500,
                                border: '1px dashed #fa8c16',
                                backgroundColor: '#fff7e6',
                                cursor: 'pointer'
                            }}
                            icon={<PlusOutlined />}
                            onClick={handleEditOrderNumber}
                        >
                            Add Order Number
                            <EditOutlined 
                                style={{ 
                                    marginLeft: '8px', 
                                    fontSize: '12px',
                                    opacity: 0.7
                                }} 
                            />
                        </Tag>
                    ) : (
                        <Tag
                            color="default"
                            style={{
                                fontSize: '13px',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontWeight: 500,
                                border: '1px solid #d9d9d9',
                                backgroundColor: '#f5f5f5'
                            }}
                            icon={<ClockCircleOutlined />}
                        >
                            Order Number Pending
                        </Tag>
                    )
                )
            )}

            {/* Order Date Field */}
           {lead.orderDate ? (
    isEditingOrderDate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatePicker
                value={orderDateInput ? dayjs(orderDateInput) : dayjs(lead.orderDate)}
                onChange={(date) => setOrderDateInput(date ? date.toISOString() : null)}
                placeholder="Select order date"
                style={{ width: '180px', borderRadius: '16px' }}
                size="small"
                format="MM-DD-YYYY"
            />
            <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSaveOrderDate}
                loading={updatingOrderDate}
                style={{ borderRadius: '16px' }}
            />
            <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEditOrderDate}
                style={{ borderRadius: '16px' }}
            />
        </div>
    ) : (
        <Tag
            color="blue"
            style={{
                fontSize: '13px',
                padding: '4px 12px',
                borderRadius: '16px',
                fontWeight: 600,
                border: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer'  // Always pointer to allow editing
            }}
            icon={<CalendarOutlined />}
            onClick={handleEditOrderDate}  // Always allow edit
        >
            Sale Date: {dayjs(lead.orderDate).format('MM-DD-YYYY')}
            <EditOutlined 
                style={{ 
                    marginLeft: '8px', 
                    fontSize: '12px',
                    opacity: 0.7
                }} 
            />
        </Tag>
    )
) : (
    isEditingOrderDate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatePicker
                value={orderDateInput ? dayjs(orderDateInput) : null}
                onChange={(date) => setOrderDateInput(date ? date.toISOString() : null)}
                placeholder="Select order date"
                style={{ width: '180px', borderRadius: '16px' }}
                size="small"
                format="MM-DD-YYYY"
            />
            <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSaveOrderDate}
                loading={updatingOrderDate}
                style={{ borderRadius: '16px' }}
            />
            <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEditOrderDate}
                style={{ borderRadius: '16px' }}
            />
        </div>
    ) : (
        <Tag
            color="orange"
            style={{
                fontSize: '13px',
                padding: '4px 12px',
                borderRadius: '16px',
                fontWeight: 500,
                border: '1px dashed #fa8c16',
                backgroundColor: '#fff7e6',
                cursor: 'pointer'
            }}
            icon={<PlusOutlined />}
            onClick={handleEditOrderDate}
        >
            Add Order Date
            <EditOutlined 
                style={{ 
                    marginLeft: '8px', 
                    fontSize: '12px',
                    opacity: 0.7
                }} 
            />
        </Tag>
    )
)}

       
        </>
    );
})()}

                        <Badge
                            status={isAgentAssigned ? "success" : "default"}
                            text={
                                <span style={{
                                    fontSize: '14px',
                                    color: isAgentAssigned ? '#52c41a' : '#8c8c8c'
                                }}>
                                    {isAgentAssigned ? currentAgentName : lead.status}
                                </span>
                            }
                        />
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Tag
                                color={stageColorMap[lead.leadStage]}
                                style={{
                                    fontSize: '14px',
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    fontWeight: 600,
                                    border: 'none',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {lead.leadStage}
                            </Tag>

                            {headerBalanceDue > 0 && (
                                <Tag
                                    color="red"
                                    style={{
                                        fontSize: '14px',
                                        padding: '6px 16px',
                                        borderRadius: '20px',
                                        fontWeight: 600,
                                        border: 'none'
                                    }}
                                >
                                    Balance Due {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(headerBalanceDue)}
                                </Tag>
                            )}

                            {isAdmin && lead.tierType && (
                                <Tag
                                    color={
                                        lead.tierType === 'TIER-1' ? 'green' :
                                            lead.tierType === 'TIER-2' ? 'blue' :
                                                lead.tierType === 'TIER-3' ? 'purple' : 'default'
                                    }
                                    style={{
                                        borderRadius: '12px',
                                        fontWeight: 500,
                                        fontSize: '12px'
                                    }}
                                >
                                    {lead.tierType}
                                </Tag>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Text type="secondary" style={{ fontSize: '14px' }}>
                                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                    {calculateLeadAge(lead)}
                                </Text>

                                <Divider type="vertical" style={{ height: '16px' }} />

                                <Text type="secondary" style={{ fontSize: '14px' }}>
                                    <CalendarOutlined style={{ marginRight: '4px' }} />
                                    {formatPSTDate(lead.createdAt)}
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Col>

        <Col>
            <Space size="middle">
                <Button
                    icon={<SyncOutlined />}
                    onClick={() => loadLead()}
                    style={{
                        padding: '8px 16px',
                        height: 'auto',
                        borderRadius: '8px',
                        borderColor: '#d9d9d9'
                    }}
                >
                    Refresh
                </Button>
                <Button
                    icon={<EditOutlined />}
                    onClick={handleEditButtonClick}
                    type="primary"
                    style={{
                        padding: '8px 24px',
                        height: 'auto',
                        borderRadius: '8px',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                    }}
                >
                    Edit Lead
                </Button>
            </Space>
        </Col>
    </Row>
</Card>
                </div >
                {/* Main Content */}
                < Row gutter={[24, 24]} >
                    {/* Left Column - Lead Details & Tabs */}
                    <Col xs={24} lg={16}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={visibleTabItems}
                        />
                    </Col>

                    {/* Right Column - Lead Properties & Quick Actions */}
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {/* Current Stage Card */}
                            <LeadStageCard
                                currentStage={currentStage}
                                leadDetails={lead}
                            />

        <ServiceAgentCard 
            lead={lead}
            serviceAgentMap={serviceAgentMap}
            serviceAgentDetailsMap={serviceAgentDetailsMap}
            onUpdateSuccess={loadLead}
        />
        <DocumentStatusCard />

                            {/* Quick Actions Card */}
                            <Card title="Quick Actions">
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                    {/* Call Customer */}
                                    {lead.personalInfo?.phoneNumber && (
                                        <Button
                                            type="primary"
                                            block
                                            href={`tel:${lead.personalInfo.phoneNumber}`}
                                            icon={<PhoneOutlined />}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            Call Customer
                                        </Button>
                                    )}

                                    {/* Email Customer */}
                                    {lead.personalInfo?.email && (
                                        <Button
                                            block
                                            onClick={handleSendEmail}
                                            icon={<MailOutlined />}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            Email Customer
                                        </Button>
                                    )}

                                    {/* Transfer Lead */}
                                    {/* <Button
                                    block
                                    onClick={() => setIsTransferModalVisible(true)}
                                    icon={<UserOutlined />}
                                    style={{
                                        backgroundColor: lead?.tierType ? '#f6ffed' : 'inherit',
                                        borderColor: lead?.tierType ? '#b7eb8f' : '#d9d9d9',
                                        marginBottom: '4px'
                                    }}
                                >
                                    Transfer Lead
                                </Button> */}




                                    {/* Schedule Follow Up Button - Show when in Follow-Up stage OR has followUpDate */}
                                    {(lead?.leadStage === "Follow-Up" || lead?.followUpDate) && (
                                        <Button
                                            block
                                            onClick={() => setActiveTab('followup')}
                                            icon={<CalendarOutlined />}
                                            style={{
                                                marginBottom: '4px',
                                                backgroundColor: '#fff7e6',
                                                borderColor: '#ffd591',
                                                color: '#fa8c16'
                                            }}
                                        >
                                            {lead?.followUpDate ? 'View Follow Up' : 'Schedule Follow Up'}
                                        </Button>
                                    )}

                                    {/* Add Quote Button - Show when in Quoted, Negotiation, or Payment stages */}
                                    {(lead?.leadStage === "Quoted" ||
                                        lead?.leadStage === "Pending Payment" ||
                                        lead?.leadStage === "Payment Details" ||
                                        lead?.leadStage === "Payment Completed" ||
                                        lead?.leadStage === "Sales Made" ||
                                        (lead?.quotedValue && lead.quotedValue.length > 0)) && (
                                            <Button
                                                block
                                                onClick={() => setActiveTab('quoted')}
                                                icon={<FileAddOutlined />}
                                                style={{
                                                    marginBottom: '4px',
                                                    backgroundColor: '#f6ffed',
                                                    borderColor: '#b7eb8f',
                                                    color: '#52c41a'
                                                }}
                                            >
                                                {lead?.quotedValue && lead.quotedValue.length > 0 ? 'View Quotes' : 'Add Quote'}
                                            </Button>
                                        )}

                                    {/* Address, Yard, Zoho, and Invoice buttons - Only show for Payment stages */}
                                    {(lead?.leadStage === "Quoted" || lead?.leadStage === 'Pending Payment' ||
                                        lead?.leadStage === 'Payment Completed' ||
                                        lead?.leadStage === 'Sales Made') && (
                                            <>
                                                {/* Add Address Button - Show only for payment stages */}
                                                <Button
                                                    block
                                                     onClick={() => setActiveTab('address')}
                                                    icon={<EnvironmentOutlined />}
                                                    style={{
                                                        marginBottom: '4px',
                                                        backgroundColor: hasAddressData() ? '#f0f9ff' : '#fff7e6',
                                                        borderColor: hasAddressData() ? '#69c0ff' : '#ffd591',
                                                        color: hasAddressData() ? '#096dd9' : '#fa8c16',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {hasAddressData() ? 'Edit Address' : 'Add Address'} *
                                                </Button>

                                                {/* Assign Yard Button - Show only for payment stages */}
                                                <Button
                                                    block
                                                    onClick={() => setActiveTab('yard')}
                                                    icon={<CarOutlined />}
                                                    style={{
                                                        marginBottom: '4px',
                                                        backgroundColor: currentLeadYard ? '#f0f9ff' : '#fff0f6',
                                                        borderColor: currentLeadYard ? '#69c0ff' : '#ffadd2',
                                                        color: currentLeadYard ? '#096dd9' : '#eb2f96',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {currentLeadYard ? 'Edit Yard' : 'Assign Yard'} *
                                                </Button>

                                                {/* Zoho Sign Button - Show only for payment stages */}
                                                <Button
                                                    block
                                                    onClick={() => setActiveTab('zoho')}
                                                    icon={<SendOutlined />}
                                                    style={{
                                                        marginBottom: '4px',
                                                        backgroundColor: lead?.zohoStatus ? '#f9f0ff' : '#fff0f6',
                                                        borderColor: lead?.zohoStatus ? '#d3adf7' : '#ffadd2',
                                                        color: lead?.zohoStatus ? '#722ed1' : '#eb2f96',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {lead?.zohoStatus ? 'View Zoho Sign' : 'Send Zoho Sign'} *
                                                </Button>

                                                {/* View Invoice Button - Show only for payment stages */}
                                                <Button
                                                    block
                                                    onClick={() => setShowInvoiceModal(true)}
                                                    icon={<FileTextOutlined />}
                                                    style={{
                                                        marginBottom: '4px',
                                                        backgroundColor: '#f0f9ff',
                                                        borderColor: '#91d5ff',
                                                        color: '#1890ff',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    View Invoice *
                                                </Button>

                                            </>
                                        )}



                                    {/* View Payment Details Button - Show when has payment data */}
                                    {hasPaymentData() && (
                                        <Button
                                            block
                                            onClick={() => setActiveTab('payment-detail')}
                                            icon={<EyeOutlined />}
                                            style={{
                                                marginBottom: '4px',
                                                backgroundColor: '#f0f9ff',
                                                borderColor: '#91d5ff'
                                            }}
                                        >
                                            View Payment Details
                                        </Button>
                                    )}
                                </Space>
                            </Card>
                            {/* Lead Properties Card */}
                            <Card
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <SettingOutlined />
                                        <span>Lead Properties</span>
                                    </div>
                                }
                            >
                                <Descriptions column={1}>
                                    <Descriptions.Item label="Agent">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <UserOutlined />
                                            {isAgentAssigned ? currentAgentName : lead.status}
                                        </div>
                                    </Descriptions.Item>
  <Descriptions.Item label="Service Handler">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TeamOutlined />
                {lead.serviceHandledBy ? (
                    (() => {
                        const handlerId = lead.serviceHandledBy?._id || lead.serviceHandledBy;
                        const handlerName = agentMap[handlerId] || 
                            (lead.serviceHandledBy?.firstName ? 
                                `${lead.serviceHandledBy.firstName} ${lead.serviceHandledBy.lastName}` : 
                                'Unknown');
                        return handlerName;
                    })()
                ) : (
                    <span style={{ color: '#999' }}>Not assigned</span>
                )}
                {lead.handledBy && lead.serviceHandledBy && 
                 lead.handledBy._id !== lead.serviceHandledBy._id && 
                 lead.handledBy !== lead.serviceHandledBy && (
                    <Tooltip title="Service handler is different from assigned agent">
                        <Tag color="orange" size="small">Different</Tag>
                    </Tooltip>
                )}
            </div>
        </Descriptions.Item>
        
                                    <Descriptions.Item label="Lead Source">
                                        <Tag color={lead.leadType === 'inbound' ? 'green' : 'blue'}>
                                            {lead.leadType}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Lead Age">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FieldTimeOutlined />
                                            {calculateLeadAge(lead)}
                                        </div>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Created">
                                        {formatPSTDate(lead.createdAt)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Existing Customer">
                                        <Tag color={lead.isExistingCustomer ? 'green' : 'default'}>
                                            {lead.isExistingCustomer ? 'Yes' : 'No'}
                                        </Tag>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Space>
                    </Col>
                </Row >

                {/* Modals */}
                < EmailTemplateModal
                    visible={isEmailModalVisible}
                    onCancel={() => setIsEmailModalVisible(false)}
                    onSend={() => {
                        setIsEmailModalVisible(false);
                        loadLead();
                    }}
                    lead={lead}
                    userData={userData}
                />

                <TransferLeadModal
                    visible={isTransferModalVisible}
                    onCancel={() => setIsTransferModalVisible(false)}
                    onTransfer={handleTransferSuccess}
                    lead={lead}
                    currentAgentId={handledById}
                    agentMap={agentMap}
                />

                {/* Edit Lead Drawer */}
                <EditLeadDrawer
                    visible={isEditDrawerVisible}
                    onClose={() => setIsEditDrawerVisible(false)}
                    onUpdate={handleUpdateLeadSuccess}
                    lead={lead}
                    currentStage={currentStage}
                    stageTransitionRules={stageTransitionRules}
                    leadStages={leadStages}
                    stagesRequiringComments={getStagesRequiringComments()}
                    isAdmin={isAdmin}
                    onTabChange={handleActiveTabChange} 
                />

                {/* Address Modal */}
                <AddressModal
                    visible={addressModalVisible}
                    onCancel={() => {
                        setAddressModalVisible(false);
                    }}
                    onOk={() => {
                        setAddressModalVisible(false);
                        loadLead();
                    }}
                    lead={lead}
                />

                {/* Yard Modal */}
                <YardModal
                    visible={yardModalVisible}
                    onCancel={() => {
                        setYardModalVisible(false);
                    }}
                    onOk={() => {
                        setYardModalVisible(false);
                        loadLead();
                    }}
                    lead={lead}
                />

                {/* Zoho Sign Modal */}
                <ZohoSignModal
                    visible={showZohoSignModal}
                    onCancel={() => setShowZohoSignModal(false)}
                    onSend={() => {
                        setShowZohoSignModal(false);
                        message.success('Document sent successfully!');
                        loadLead();
                    }}
                    lead={lead}
                    userData={userData}
                />

                {/* Invoice Preview Modal */}
                <InvoicePreviewModal
                    visible={showInvoiceModal}
                    onCancel={() => setShowInvoiceModal(false)}
                    lead={lead}
                />
            </div >

            <LeadDetailsDrawer
  visible={drawerVisible}
  onClose={() => setDrawerVisible(false)}
  leadData={selectedLeadData}
  onLeadUpdate={(updatedLead) => {
    // Update the lead data when changes are saved in drawer
    setLead(updatedLead);
    setDrawerVisible(false);
  }}
  loading={false}
/>
<Modal
    title={
        <Space>
            <ShoppingCartOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Purchase Order</span>
        </Space>
    }
    open={showFTWEInvoiceModal}
    onCancel={() => setShowFTWEInvoiceModal(false)}
    footer={null}
    width={1200}
    centered
    style={{ top: 20 }}
>
<PurchaseOrderTemplate 
  lead={lead} 
  GetYardById={API.GetYardById}  // Pass the API function
/>
</Modal>

        </>

    );
};

export default LeadDetails;


